import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

// REACT_APP_KRO_API_BASE includes the /api/v1 suffix in prod
// (e.g. https://ca-api-prod-wus2…/api/v1). Strip it here so the /api/v1/…
// paths below resolve to a single prefix instead of /api/v1/api/v1/… (404).
const API_BASE  = (process.env.REACT_APP_KRO_API_BASE || 'https://api.kromarketplace.com')
  .replace(/\/api\/v1\/?$/, '');

// SECURITY: the studio sends NO privileged credential. It only reads PUBLIC
// on-ledger status endpoints (canton/cer status, contracts, network-status).
// Settlement WRITES are server-to-server (CLI) only and are never invoked from
// the browser. Previously this embedded REACT_APP_ADMIN_KEY (= BLAST_ADMIN_KEY,
// a SYSTEM_ADMIN bypass) into the public bundle — that key has been removed.
const safeFetch = (url, opts = {}) =>
  fetch(url, { ...opts, headers: { ...(opts.headers || {}) } });

// ── Environment definitions ───────────────────────────────────────────────────
// This UI surfaces MainNet ONLY. DevNet and TestNet are strictly isolated and are
// deliberately NOT listed here, so the switcher, "all environments" cards, the
// Lighthouse grid, and every activeEnv-driven API call resolve to mainnet only.
const ENVS = [
  {
    id:          'mainnet',
    label:       'MainNet',
    status:      'LIVE',
    statusColor: '#10b981',
    promoted:    '2026-06-10',
    partyId:     'KRO-Mainnet-1::1220d605db014187922fb0b11d745aad32cf0700f7945b6a4423e1e4d49c5601ba9b',
    validatorHost:'wallet.validator.canton-mainnet.kromarketplace.com',
    validatorIP: '34.63.234.70',
    lighthouseUrl:'https://lighthouse.cantonloop.com/party/KRO-Mainnet-1%3A%3A1220d605db014187922fb0b11d745aad32cf0700f7945b6a4423e1e4d49c5601ba9b/transactions',
    partyUrl:    'https://lighthouse.cantonloop.com/participant/KRO-Mainnet-1%3A%3A1220d605db014187922fb0b11d745aad32cf0700f7945b6a4423e1e4d49c5601ba9b',
    darVersion:  'CIP-0056 offer/take instrument · v0.1.0',
    darUploadedAt:'2026-06-18',
    firstTxAt:   '2026-06-17T17:10:12Z',
    env:         'mainnet',
  },
];

// RFQ scope: on-ledger greenlit settlement operators (mirrors backend counterparty-pool.js).
const GREENLIT_SV_IDS = new Set([
  'broadridge-1', 'broadridge-2', 'tradeweb', 'hashnote', 'bank-frick',
  'ltp', 't-rize', 'usig',
  'cumberland-1', 'cumberland-2', 'zodia-1', 'zodia-2', 'virtu', 'marex',
  'five-north', 'global-settlement', 'edx-markets', 'bullish', 'lloyds-dap',
]);
const RFQ_SCOPE = { mode: 'on-ledger-cip56-dvp', cashLeg: 'CIP-56 token on Global Synchronizer' };

// Collateral mobility use-case landscape (Foundation pilot interfacing).
const COLLATERAL_MOBILITY_USE_CASES = [
  { id: 't-rize', name: 'T-RIZE Group', interest: 'Private credit as collateral', cashLeg: 'Tokenized private credit + CIP-56 DvP' },
  { id: 'usig', name: 'USIG', interest: 'Primary issuance tokenization for collateral optimization through programmability', cashLeg: 'Bank / issuance cash leg' },
];

// Section A — off-Global-Synchronizer primes (not in RFQ; bridge path pending Foundation).
const OFF_CHAIN_PRIME_TARGETS = [
  { id: 'bny-mellon', name: 'BNY Mellon', reachVia: 'Not on RFQ — future access pending Foundation', status: 'NOT_ON_MAINNET' },
  { id: 'broadridge', name: 'Broadridge (DLR)', reachVia: 'Not on RFQ — bridge pending Foundation', status: 'PRIVATE_DEPLOYMENT' },
  { id: 'dtcc-ficc', name: 'DTCC FICC GCF Repo', reachVia: 'Not on RFQ — roadmap', status: 'ROADMAP' },
  { id: 'jpmorgan', name: 'JPMorgan (Kinexys)', reachVia: 'Not on RFQ — future access pending Foundation', status: 'PRIVATE_DEPLOYMENT' },
  { id: 'goldman-sachs', name: 'Goldman Sachs (GS DAP)', reachVia: 'Not on RFQ — bridge pending Foundation', status: 'PRIVATE_DEPLOYMENT' },
  { id: 'morgan-stanley', name: 'Morgan Stanley', reachVia: 'Not on RFQ — future access pending Foundation', status: 'NOT_ON_MAINNET' },
  { id: 'euroclear', name: 'Euroclear', reachVia: 'Not on Global Synchronizer pilot', status: 'NOT_ON_MAINNET' },
  { id: 'clearstream', name: 'Clearstream', reachVia: 'Not on Global Synchronizer pilot', status: 'NOT_ON_MAINNET' },
];

const isGreenlitSv = (sv) => GREENLIT_SV_IDS.has(sv.id);

// ── Confirmed RFP transactions ────────────────────────────────────────────────
// Live Canton MainNet validator NODE OPERATORS (network watchlist — not all in RFQ scope)
// Greenlit on-chain RFQ respondents are SV-tier — see CANTON_SV_REGISTRY + GREENLIT_SV_IDS.
// eligTier: greenlit = in approvedLenders · bridge-watch = off-chain prime via bridge SV · watch = future · infra = not a counterparty
const CANTON_VALIDATOR_NODES = [
  {
    id: 'broadridge-1', name: 'Broadridge Financial Solutions (Node 1)', sponsor: 'GSF',
    repoEligible: false, eligTier: 'bridge-watch',
    domicile: 'US (NJ)', entityType: 'Broker-Dealer',
    role: 'DLR tri-party repo — private Canton sync, not Global Synchronizer',
    note: 'Prime candidate off-chain. Reach via Digital Asset bridge SV — NOT in greenlit RFQ approvedLenders.',
    partyId: 'validator-broadridge-1::1220fb5d230808f7caabd740d797daabfc31c7418180e2b1fd2365658f3220edf818',
  },
  {
    id: 'broadridge-2', name: 'Broadridge Financial Solutions (Node 2)', sponsor: 'GSF',
    repoEligible: false, eligTier: 'bridge-watch',
    domicile: 'US (NJ)', entityType: 'Broker-Dealer',
    role: 'DLR secondary node — same bridge path as Node 1',
    note: 'Not in greenlit RFQ scope. Network-wide cash participant interest only.',
    partyId: 'validator-broadridge-1::12205e668e22a25facdb6a3449890683f0f0c493bc1941549db9d1e16f2e324158cf',
  },
  {
    id: 'hashnote', name: 'Hashnote', sponsor: 'Cumberland',
    repoEligible: true, eligTier: 'greenlit',
    domicile: 'US (DE/NY)', entityType: 'Fund Manager / RIA',
    role: 'Tokenized US T-Bills (USYC) — BNY Mellon custody and settlement',
    note: 'Greenlit RFQ respondent — tokenized cash / short-duration instrument cash leg.',
    partyId: 'validator_Hashnote::12202eacbd060e2e22ab198cb9f337ccfe049e16a90968576e15b8bab4317cf17259',
  },
  {
    id: 'bank-frick', name: 'Bank Frick', sponsor: 'DA',
    repoEligible: true, eligTier: 'greenlit',
    domicile: 'Liechtenstein (EEA)', entityType: 'Bank',
    role: 'Licensed bank — institutional custody and token-based repo',
    note: 'EEA bank on Canton. Greenlit RFQ respondent — tokenized cash / bank cash-leg operator.',
    partyId: 'BankFrick-validator-1::1220ba277025c010dee5dc0b5abf9dd3bf4fa114029a987f5acd9e7b3098c410bf96',
  },
  {
    id: 'ltp', name: 'LTP (Liquidity Tech)', sponsor: 'GSF',
    repoEligible: true, eligTier: 'greenlit',
    domicile: 'Global', entityType: 'Prime Broker',
    role: 'Institutional prime broker — real-time lending at 1-minute increments',
    note: 'Collateral mobility pilot. Use-case: sub-minute / intraday lending cash leg for KRO.CER repo.',
    partyId: 'LTP-validator-1::122057dac226b611677e8dcee655758d400cc49c60f36fd1d7769f89220634b5a66a',
  },
  {
    id: 't-rize', name: 'T-RIZE Group', sponsor: 'GSF',
    repoEligible: true, eligTier: 'greenlit',
    domicile: 'CA / Global', entityType: 'Tokenization / Validator',
    role: 'Private credit tokenization — collateral acceptance on Canton',
    note: 'Collateral mobility pilot. Use-case: private credit as on-ledger collateral; Canton Foundation member.',
    partyId: 'TRIZEGroup-cantonMainnetValidator-1::12206ab3bf15b14410220357d6a6375eb1015f2e7fade1deb449463c2f2a25304889',
  },
  {
    id: 'usig', name: 'USIG', sponsor: 'GSF',
    repoEligible: true, eligTier: 'greenlit',
    domicile: 'US', entityType: 'Bank / Collateral Optimization',
    role: 'Primary issuance tokenization — programmable collateral optimization',
    note: 'Collateral mobility pilot. Use-case: tokenized primary-issuance subset as collateral; bank cash-leg operator.',
    partyId: 'usig-validator-1::12204a63011b461a00edf9049dd51509fe6d35b17120f205f122c5e5a1bd00b7e2db',
  },
  {
    id: 'zodia-1', name: 'Zodia Custody (Standard Chartered, Node 1)', sponsor: 'GSF',
    repoEligible: false, eligTier: 'watch',
    domicile: 'UK / US', entityType: 'Custodian',
    role: 'Institutional custody — Standard Chartered Group, FCA-regulated',
    note: 'FCA-regulated custodian (Standard Chartered subsidiary). Eligible subject to collateral schedule amendment confirming non-standard asset class. Standard GMRA amendment sufficient.',
    partyId: 'ZodiaCustody-validator-1::122048dee6ac5d575890024dbf6847d1ca4026c96e8d4c6df07bb5b96a10ae180030',
  },
  {
    id: 'zodia-2', name: 'Zodia Custody (Interchange Node)', sponsor: 'GSF',
    repoEligible: false, eligTier: 'watch',
    domicile: 'UK / US', entityType: 'Custodian',
    role: 'Cross-border settlement node — Standard Chartered custody',
    note: 'Same regulatory basis as Zodia Node 1. Interchange node for cross-border settlement flows.',
    partyId: 'zodiacustody-interchangevalidator-1::12204c95de051a6a1f1c66ae0db1e21a19ec747ed9c3761881525fc316c4e12be740',
  },
  {
    id: 'lloyds-dap', name: 'Lloyds Bank (DAP node)', sponsor: 'DA',
    repoEligible: false, eligTier: 'watch',
    domicile: 'UK', entityType: 'Bank',
    role: 'Digital Asset Platform — Lloyds Bank UK clearing and settlement',
    note: 'UK clearing bank, FCA-regulated, PRA-supervised. Eligible subject to GMRA collateral schedule amendment. DAP platform supports onboarding of non-standard asset classes.',
    partyId: 'LloydsBank-DAP-1::12200507d9a8971ff72e745f32c7bcebf1d383ff53d4a0fcb461a307f602ff83bb93',
  },
  {
    id: 'virtu-cumberland', name: 'Virtu Financial (Cumberland node)', sponsor: 'Cumberland',
    repoEligible: false, eligTier: 'watch',
    domicile: 'US (NY)', entityType: 'Market Maker / Broker-Dealer',
    role: 'Electronic market maker — US Treasury and fixed income repo',
    note: 'FINRA/SEC-registered broker-dealer (NY). Active in US Treasury repo. Eligible subject to MSLA/GMRA amendment for non-standard collateral. Operationally straightforward.',
    partyId: 'virtu-validator-1::122096e724a154c26747d16a6c047245cf16003b84d7f87e8b291839bb161eabff94',
  },
  {
    id: 'virtu-tradeweb', name: 'Virtu Financial (Tradeweb node)', sponsor: 'Tradeweb',
    repoEligible: false, eligTier: 'watch',
    domicile: 'US (NY)', entityType: 'Market Maker / Broker-Dealer',
    role: 'Electronic market maker — Tradeweb execution node',
    note: 'Same regulatory standing as Virtu Cumberland node. Execution via Tradeweb ATS.',
    partyId: 'twmain-virtu-1::122022ee33b95b2a72381f3e1052dd90327029e202db7dc2c44e3f66a98848fd1ee3',
  },
  {
    id: 'marex', name: 'Marex', sponsor: 'Cumberland',
    repoEligible: false, eligTier: 'watch',
    domicile: 'US / UK', entityType: 'Broker-Dealer',
    role: 'Institutional broker-dealer — commodities, fixed income, and repo',
    note: 'CFTC/FCA-regulated broker-dealer. US and UK operations. Eligible subject to collateral schedule amendment. Commodities background makes non-standard collateral onboarding familiar.',
    partyId: 'marex-validator-1::1220ee87f63c38ae6c9438f257c393faf757fc5604fd09aa4dcbb7b559a274e5ecc9',
  },
  {
    id: 'tw-treasury', name: 'Tradeweb Treasury Node', sponsor: 'Tradeweb',
    repoEligible: false, eligTier: 'watch',
    domicile: 'US (NJ / NY)', entityType: 'ATS / Repo Platform',
    role: 'Tradeweb Markets — treasury and agency repo settlement node',
    note: 'FINRA-registered ATS operating under US law (NJ/NY). Eligible subject to platform-level asset-class onboarding and standard GMRA amendment.',
    partyId: 'twmain-treasury-1::12200954e2b4596dfad67f92cd333602efa4ebcdc3860437884136817fa5c9f54a0e',
  },
  {
    id: 'global-settlement', name: 'GlobalSettlement', sponsor: 'GSF',
    repoEligible: false, eligTier: 'infra',
    domicile: 'US', entityType: 'Infrastructure',
    role: 'GSF-sponsored settlement infrastructure — not a direct repo counterparty',
    note: 'Network infrastructure node. No cash provision or repo function.',
    partyId: 'GlobalSettlement-mainnet-1::1220aae9df566656d20f75190349dbc096ce28871f914d81d0a03dfe5750af817573',
  },
  {
    id: 'edx', name: 'EDX Markets', sponsor: 'Cumberland',
    repoEligible: false, eligTier: 'infra',
    domicile: 'US (DE)', entityType: 'Exchange',
    role: 'Institutional digital asset exchange (Citadel / Fidelity / Schwab backed)',
    note: 'Spot trading venue — not a repo cash provider. No collateral or lending function.',
    partyId: 'edx-validator-1::1220a195571be41cc06440b9ea0ca30a137636d824fc1bd37ea62376ccc5a80e6a83',
  },
  {
    id: 'ceffu', name: 'Ceffu (institutional custody)', sponsor: 'Cumberland',
    repoEligible: false, eligTier: 'infra',
    domicile: 'Cayman / Singapore', entityType: 'Custodian',
    role: 'Institutional custody (Binance-affiliated)',
    note: 'Offshore custodian — MAS-regulated. Requires bespoke cross-border collateral agreement. Not a repo cash provider.',
    partyId: 'ceffu-validator-1::1220b3c5ad94ee8977dceaacd73123188ccb23cd95d65af2e8ae454d1c00cc7a2181',
  },
  {
    id: 'bullish', name: 'Bullish Exchange', sponsor: 'Cumberland',
    repoEligible: false, eligTier: 'infra',
    domicile: 'Gibraltar / US', entityType: 'Exchange',
    role: 'Regulated digital asset exchange',
    note: 'Spot exchange only. No repo or collateral lending function.',
    partyId: 'bullish-validator-1::1220801f071072caf0409df66061b2764379030af4b063cff5940bc028ddb82cdd30',
  },
];

// Live Canton MainNet super-validators — mirrors backend/config/counterparty-pool.js
const CANTON_SV_REGISTRY = [
  { id: 'tradeweb', name: 'Tradeweb Markets', ansName: 'tradeweb-markets-1.sv.cns', repoEligible: true, role: 'Electronic Repo Marketplace — on-chain Global Synchronizer SV', notes: 'Greenlit RFQ respondent. On-ledger settle only for pilot.', partyId: 'Tradeweb-Markets-1::12203cd253ff9f6526c871dd6a9b3af8d1f0484ff0910e6cae75e9bcf76d1be5ff99' },
  { id: 'cumberland-1', name: 'Cumberland DRW (Node 1)', ansName: 'cumberland-1.sv.cns', repoEligible: true, role: 'Institutional Market Maker — repo / securities lending', notes: 'Greenlit RFQ respondent.', partyId: 'Cumberland-1::12201aa8a23046d5740c9edd58f7e820c83e7f5c58f25551f955f3252d3a04240860' },
  { id: 'cumberland-2', name: 'Cumberland DRW (Node 2)', ansName: 'cumberland-2.sv.cns', repoEligible: true, role: 'Institutional Market Maker — secondary node', notes: 'Greenlit RFQ respondent.', partyId: 'Cumberland-2::1220120801da0994334c869a6db19d9321f3d5baf9e40377830a90c2af78658aede6' },
  { id: 'five-north', name: 'Five North Capital', ansName: 'five-north-1.sv.cns', repoEligible: true, role: 'Institutional Capital Markets Participant', notes: 'Greenlit RFQ respondent.', partyId: 'Five-North-1::1220520b6d396b91c4b13a1786bd5f317e4240001632d4a628c1c4f53a0b5165ba98' },
  { id: 'digital-asset-1', name: 'Digital Asset Holdings (Node 1)', ansName: 'digital-asset-1.sv.cns', repoEligible: false, isBridge: true, bridgeTo: ['broadridge', 'goldman-sachs', 'dtcc-ficc', 'bny-mellon'], role: 'Canton Protocol Author — institutional relationships', notes: 'Not in RFQ scope. Bridge outreach pending Foundation confirmation.', partyId: 'Digital-Asset-1::12203a616cd41a519d1950ffd4df0ced808cce3bea558d684296c007bb8e12d7f649' },
  { id: 'digital-asset-2', name: 'Digital Asset Holdings (Node 2)', ansName: 'digital-asset-2.sv.cns', repoEligible: false, isBridge: true, bridgeTo: ['broadridge', 'goldman-sachs', 'dtcc-ficc'], role: 'Digital Asset — secondary node', notes: 'Not in RFQ scope — bridge pending Foundation confirmation.', partyId: 'Digital-Asset-2::12209b21d512c6a7e2f5d215266fe6568cb732caaef7ff04e308f990a652340d3529' },
  { id: 'c7', name: 'C7 Technology Services', ansName: 'c7-technology-services-limited.sv.cns', repoEligible: false, role: 'Technology Services / Infrastructure', partyId: 'C7-Technology-Services-Limited::122027859595a9f295312977656425f2360813ce5a2681aadc7605d2e198dc82a877' },
  { id: 'mpch', name: 'MPC Holding Inc (MPCH)', ansName: 'mpc-holding-inc.sv.cns', repoEligible: false, role: 'Digital Asset Custody', partyId: 'MPC-Holding-Inc::12204783725aa3adcd787311531134baed8b9b28ccf76b100c62b8d4995842aabd6b' },
  { id: 'proof', name: 'Proof Group', ansName: 'proof-group-1.sv.cns', repoEligible: false, role: 'Blockchain Infrastructure', partyId: 'Proof-Group-1::1220a0027410294266f712c1a4787d2dcb9be20b712486dca010d4d9cdbe3e809385' },
  { id: 'lcv', name: 'Liberty City Ventures', ansName: 'liberty-city-ventures-1.sv.cns', repoEligible: false, role: 'Venture Capital', partyId: 'Liberty-City-Ventures-1::1220c8a81a9d60198fd67aa596f8af90cfe57f5966964427e32f2dab1570ad0d9a25' },
  { id: 'orb1', name: 'Orb-1 LP', ansName: 'orb-1-lp-1.sv.cns', repoEligible: false, role: 'Investment Vehicle', partyId: 'Orb-1-LP-1::122059e5d9c6036656bba13299bf959ed190fc51e2564024b9872ecd84001f87ea92' },
  { id: 'nodeops', name: 'SV NodeOps Limited', ansName: 'sv-nodeops-limited.sv.cns', repoEligible: false, role: 'Canton Node Operations', partyId: 'SV-Nodeops-Limited::1220734414a4b0658bb2be4caf33205e5a45bf8dc5207e3bfc769d361582dc72feef' },
  { id: 'gsf', name: 'Global Synchronizer Foundation', ansName: 'global-synchronizer-foundation.sv.cns', repoEligible: false, role: 'Canton Network Governance', partyId: 'Global-Synchronizer-Foundation::1220b0867964b602f2cc7ea61324a95f000f0060e735cfaf4f23f424fdab02c170ac' },
];

// Settlement architecture (pilot):
//   RFQ scope → IntradayRepoRFP to greenlit on-ledger operators · CIP-56 atomic DvP cash leg
const RFP_TRANSACTIONS = [
  {
    env:            'mainnet',
    rfpContractId:  '00ff179affd0e32e7b9f7eb500c93d90b7e3071499d008555e01de322e55b6e0cbca121220c84f347812337dfe15e006f6be95cda5f980ab840924f4e6158043ee59c86e63',
    rfqCorrelationRef: 'RFQ-KRO-MAINNET-OFFCHAIN-001',
    transmissionMethod: 'CIP-56_DVP',
    rail:           'Canton IntradayRepoRFP → AcceptRFP → on-chain DvP (CIP-56)',
    settlementNote: 'Greenlit on-ledger operator calls AcceptRFP → IntradayRepoFacility on Global Synchronizer. Cash leg: CIP-56 token in same atomic submit.',
    submittedAt:    '2026-06-18T21:48:59Z',
    expiresAt:      '2026-06-20T21:48:59Z',
    dtifId:         'ZX18049M0',
    drawNotional:   10_000_000,
    collateral:     '182 units kro.as.accountability.v2 (Provenance HASH wallet)',
    collateralValue:10_204_082, // $10M draw ÷ (1 − 2% margin) — adjusted (post-haircut) = exactly $10.0M
    haircutPct:     '2%',
    intradayRateBps:10,
    kroRole:        'BORROWER / COLLATERAL PROVIDER',
    lei:            '254900QOF5WI2UKAPE82',
    isoFields:      ['InstrPrty: HIGH', 'SvcLvl: URGP', 'CtgyPurp: COLL', 'CtgyPurp: INTC (RmtInf)', 'CtgyPurp: TRAD (RmtInf)'],
    dvpFlow:        ['IntradayRepoRFP created', 'Lender calls AcceptRFP', 'IntradayRepoFacility (atomic)', 'KRO calls OpenDraw → IntradayRepoDraw', 'CloseOut → IntradayCloseOutReceipt (final)'],
    approvedLenders: [
      { name: 'Tradeweb Markets', party: 'Tradeweb-Markets-1::12203cd253ff9f6526c871dd6a9b3af8d1f0484ff0910e6cae75e9bcf76d1be5ff99', entityType: 'Greenlit SV · Global Synchronizer', note: 'On-chain RFQ respondent — US Treasury & Agency repo platform' },
      { name: 'Cumberland DRW (Node 1)', party: 'Cumberland-1::12201aa8a23046d5740c9edd58f7e820c83e7f5c58f25551f955f3252d3a04240860', entityType: 'Greenlit SV · Global Synchronizer', note: 'Institutional market maker — on-chain settle' },
      { name: 'Cumberland DRW (Node 2)', party: 'Cumberland-2::1220120801da0994334c869a6db19d9321f3d5baf9e40377830a90c2af78658aede6', entityType: 'Greenlit SV · Global Synchronizer', note: 'Secondary Cumberland node — on-chain settle' },
      { name: 'Five North Capital', party: 'Five-North-1::1220520b6d396b91c4b13a1786bd5f317e4240001632d4a628c1c4f53a0b5165ba98', entityType: 'Greenlit SV · Global Synchronizer', note: 'Institutional capital markets — on-chain settle' },
    ],
    lenderStatus:   `BROADCAST — ${GREENLIT_SV_IDS.size} on-ledger Global Synchronizer operators`,
    status:         'ACTIVE',
    lighthouseUrl:  'https://lighthouse.cantonloop.com/party/KRO-Mainnet-1%3A%3A1220d605db014187922fb0b11d745aad32cf0700f7945b6a4423e1e4d49c5601ba9b/transactions',
    package:        'CIP-0056 offer/take instrument · v0.1.0',
    packageHash:    'resolved by package-name at submit',
  },
];

// ════════════════════════════════════════════════════════════════════════════
//  PREVIEW-MODE SUPPORT — Tier A/B determinism + foundation lock standards
// ════════════════════════════════════════════════════════════════════════════

// CIP-0116 — Featured App per-PartyId Canton Coin (CC) locking requirement.
// Source: github.com/canton-foundation/cips/blob/main/cip-0116/cip-0116.md
// IMPORTANT: CIP-0116 is NOT a collateral / UCC Article-12 standard. It defines the
// Canton Coin that must be locked, per PartyId, to QUALIFY FOR and KEEP Featured App
// status (and the reward eligibility that comes with it). Asset issuers (KRO.CER is
// an issuer) must lock the higher tier; falling below it = immediate loss of FA
// status. Collateral / Article-12 control is a different thing entirely — it is
// governed by the CIP-0056 Holding.lock primitive + the Canton Collateral Control
// Plane, surfaced separately in the preview below.
const CIP0116 = {
  issuerLockCC:    25_000_000,
  nonIssuerLockCC:  5_000_000,
  vestDays:        60,
  reference:       'https://github.com/canton-foundation/cips/blob/main/cip-0116/cip-0116.md',
};

// Canonical PartyId index — every PartyId we treat as authoritative (env parties +
// validator node operators + super-validator registry). Used to detect drift in the
// hardcoded RFP lender list so a stale/typo'd PartyId can never be used to settle.
function buildCanonicalPartyIndex() {
  const full = new Set();
  const prefixes = new Set();
  const add = (pid) => {
    if (!pid || typeof pid !== 'string') return;
    full.add(pid);
    const ix = pid.indexOf('::');
    if (ix > 0) prefixes.add(pid.slice(0, ix));
  };
  ENVS.forEach(e => add(e.partyId));
  (typeof CANTON_VALIDATOR_NODES !== 'undefined' ? CANTON_VALIDATOR_NODES : []).forEach(v => add(v.partyId));
  (typeof CANTON_SV_REGISTRY !== 'undefined' ? CANTON_SV_REGISTRY : []).forEach(s => add(s.partyId));
  return { full, prefixes };
}

// Defensively extract whatever party identifiers the live backend RFP preview
// exposes (shape can vary across backend versions). Returns a Set or null.
function liveLenderSet(rfpPreview) {
  if (!rfpPreview) return null;
  const out = new Set();
  const sources = [
    rfpPreview?.approvedLenders,
    rfpPreview?.preview?.approvedLenders,
    rfpPreview?.command?.createArguments?.approvedLenders,
    rfpPreview?.preview?.createArguments?.approvedLenders,
    rfpPreview?.createArguments?.approvedLenders,
  ];
  sources.forEach(src => {
    if (Array.isArray(src)) src.forEach(x => {
      const p = typeof x === 'string' ? x : (x?.party || x?.partyId);
      if (p) out.add(p);
    });
  });
  return out.size ? out : null;
}

// Reconcile a hardcoded lender list against the canonical index + (optionally) the
// live preview. Deterministic, computed from in-file data so it always renders.
function reconcileLenders(lenders, canonical, livePartySet) {
  return (lenders || []).map(l => {
    const party = l.party || '';
    const ix = party.indexOf('::');
    const prefix = ix > 0 ? party.slice(0, ix) : party;
    const inLive = livePartySet ? livePartySet.has(party) : null;
    let status, detail;
    if (inLive === true) {
      status = 'live'; detail = 'Matches live on-ledger RFP preview.';
    } else if (canonical.full.has(party)) {
      status = 'canonical'; detail = 'Matches the on-file canonical registry.';
    } else if (canonical.prefixes.has(prefix)) {
      status = 'drift'; detail = 'Participant prefix is known but this full PartyId is NOT in the canonical registry — likely stale/typo. Reconcile to live before settling.';
    } else {
      status = 'unverified'; detail = 'PartyId not found in canonical registry or live preview.';
    }
    return { ...l, status, detail, prefix };
  });
}

// ── Validator contact card ────────────────────────────────────────────────────
const VALIDATOR_CONTACT = {
  name:       'K-R-O BROKERS SPC',
  email:      'lexy@krotrust.org',
  lei:        '254900QOF5WI2UKAPE82',
  url:        'https://kromarketplace.com',
  location:   'Seattle, WA, USA',
  partyHint:  'KRO-Mainnet-1',
  adminApi:   `POST /v1/validator/admin/contact`,
};

// ── Ledger API tracking endpoints ─────────────────────────────────────────────
const TRACKING_APIS = [
  {
    method: 'GET', endpoint: '/v2/updates?parties={party_id}',
    desc: 'All ledger updates for a party — includes every contract create, exercise, and archive event.',
    useCase: 'See who created or exercised choices on your contracts in real time.',
  },
  {
    method: 'GET', endpoint: '/v2/events/by-contract-id/{contract_id}',
    desc: 'Full event history for a specific contract (by Canton CID).',
    useCase: 'Track acceptance of a specific RFQ or DrawWorkflow by counterparty.',
  },
  {
    method: 'GET', endpoint: '/v2/state/active-contracts?parties={party_id}',
    desc: 'All currently active contracts for a party.',
    useCase: 'See your live MarkerCollateral, IntradayRepoFacility, and CloseOutReceipt contracts.',
  },
  {
    method: 'POST', endpoint: '/v2/query',
    desc: 'Query contracts by Daml template ID with optional filter.',
    useCase: 'Filter for IntradayRepoRFP contracts where signatories include counterparty party ID.',
  },
  {
    method: 'GET', endpoint: '/v2/transactions?parties={party_id}&begin_offset={offset}',
    desc: 'Paginated transaction stream from a ledger offset.',
    useCase: 'Replay events from a checkpoint — useful for integration tests and audit.',
  },
];

// Repo.Intraday event types persisted by the Canton settlement bridge / feed.
const REPO_EVENT_TYPES = [
  'INTRADAY_RFP', 'INTRADAY_FACILITY', 'INTRADAY_DRAW',
  'REPO_ROLLOVER', 'INTRADAY_CLOSEOUT', 'HERITAGE_RECORD',
];

// Pre-deploy review copy of the current contract language (faithful to Main.daml).
// Surfaced read-only in the Contract Review tab so reviewers see exactly what deploys.
const CONTRACT_LANGUAGE_V014 = `-- CIP-0056 offer/take instrument · v0.1.0 · deterministic settlement templates
-- Data standard carried Offer -> Position -> CloseOutReceipt.

data SettlementSpec = SettlementSpec with
    settlementMethod : Text   -- "CANTON-DVP" (Canton Network DvP · T+0 · on-chain)
    cashAccountRef   : Text   -- CIP-0056 cash InstrumentId admin/id, e.g. "circle-validator-1::…/USDCx"
    currency         : Text   -- ISO 4217, e.g. "USD"
    instructionRef   : Text   -- settlementRef / UETR, e.g. "CIP56-SETTLEMENT-MAINNET-20260704"
-- ^ Populated at offer time from the counterparty onboarding packet (see the
--   "Settlement Instructions & Routing" card below for the concrete values).

-- 2b. COLLATERAL OFFER -- deterministic settlement primitive.
-- The offeror presents clean, unencumbered collateral; an eligible taker
-- accepts or the offer lapses. First valid acceptance wins (FCFS, ledger-enforced).
template CollateralOffer with
    offeror              : Party              -- holder of record (KRO Brokers, SPC)
    eligibleTakers       : [Party]            -- enumerated set of permitted takers
    collateralId         : ContractId MarkerCollateral
    offeredAmountUsd     : Decimal
    commercialValueUsd   : Decimal            -- real-world commercial value
    unencumbered         : Bool               -- attestation: clean title, unpledged
    eligibilityProfileId : Text               -- compliance ruleset reference
    settlementSpec       : SettlementSpec     -- cash settlement instructions
    offerRef             : Text               -- UETR / offer identifier
    initialMargin        : Decimal            -- e.g., 0.02 for 2% initial margin (haircut)
    pricingBps           : Decimal            -- e.g., 10.0 for 10 bps intraday
    positionExpiry       : Time               -- maturity timestamp
  where
    ensure offeredAmountUsd > 0.0
        && commercialValueUsd > 0.0
        && unencumbered
        && eligibilityProfileId /= ""
        && offerRef /= ""
        && not (null eligibleTakers)
        && initialMargin >= 0.0 && initialMargin < 1.0
        && pricingBps >= 0.0
    signatory offeror
    observer  eligibleTakers

    choice AcceptCollateral : ContractId CollateralPosition
      with taker : Party
      controller taker
      do
        assertMsg "Taker is not eligible" (taker \`elem\` eligibleTakers)
        collateral <- fetch collateralId
        assertMsg "Collateral not held by offeror"
          (collateral.kro == offeror)
        -- Margin enforcement: offered amount <= (1 - margin) * collateral value
        assertMsg "Offered amount exceeds collateral value with margin"
          (offeredAmountUsd <= (1.0 - initialMargin) * intToDecimal collateral.units * collateral.priceUsd)
        -- Acceptance locks collateral; subsequent attempts fail (FCFS).
        lockedCid <- exercise collateralId LockForPledge with pledgeUetr = offerRef
        create CollateralPosition with
          offeror; taker; lockedCollateralId = lockedCid
          positionAmountUsd = offeredAmountUsd; commercialValueUsd
          eligibilityProfileId; settlementSpec; offerRef
          pricingBps; positionExpiry

    -- The offeror may withdraw the offer prior to acceptance.
    choice WithdrawOffer : ()
      controller offeror
      do pure ()

-- Acceptance creates position; cash leg authorized.
template CollateralPosition with
    offeror              : Party
    taker                : Party
    lockedCollateralId   : ContractId LockedMarkerCollateral
    positionAmountUsd    : Decimal
    commercialValueUsd   : Decimal
    eligibilityProfileId : Text
    settlementSpec       : SettlementSpec
    offerRef             : Text
    pricingBps           : Decimal
    positionExpiry       : Time
  where
    ensure positionAmountUsd > 0.0
    signatory offeror, taker

    -- Settlement service (offeror) verifies the cash leg, then writes the receipt.
    choice SettleCollateral : (ContractId CloseOutReceipt, ContractId MarkerCollateral)
      with instructionRef : Text; settlementTxHash : Text
      controller offeror
      do
        now         <- getTime
        cashSettled <- verifyCashLeg positionAmountUsd settlementTxHash
        assertMsg "Cash leg not settled" cashSettled
        collCid    <- exercise lockedCollateralId UnlockCollateral
        receiptCid <- create CloseOutReceipt with
          kro = offeror; counterparty = taker
          settledAmountUsd = positionAmountUsd; uetr = offerRef
          instructionRef; settlementTxHash; settledAt = now
          eligibilityProfileId = Some eligibilityProfileId
          settlementSpec       = Some settlementSpec
          pricingBps           = Some pricingBps
        return (receiptCid, collCid)

    -- Unwind the position and reclaim clean collateral after maturity.
    choice ReturnCollateral : ContractId MarkerCollateral
      controller offeror
      do
        now <- getTime
        assertMsg "Position not yet expired" (now > positionExpiry)
        exercise lockedCollateralId UnlockCollateral

-- CloseOutReceipt (existing template) carries three Optional, appended fields
-- (upgrade-safe): eligibilityProfileId : Optional Text, settlementSpec : Optional
-- SettlementSpec, pricingBps : Optional Decimal.
`;

// Pre-deploy review copy of the UCC Article 12 control-manifest module
// (daml/Kro/Cer/ControlManifest.daml) — faithful to the deployed source.
const CONTROL_MANIFEST_V1 = `-- KRO CER — UCC Article 12 (§ 12-105) Controllable Electronic Record "Control" proof.
--
-- This module adds a legal-control layer ON TOP of the CIP-56 Holding asset
-- (Cer.Token) without modifying the vetted token templates. It implements the
-- "Bilateral Observer Pattern" the settlement blueprint requires:
--
--   * CER_Control_Manifest    -- signed by the CONTROLLER (K-R-O Brokers). Carries a
--                               DYNAMIC observer block (cashCounterparties) so a DvP
--                               cash counterparty can be granted visibility of the
--                               control metrics WITHOUT any transfer of ownership.
--   * Verify_Control          -- non-consuming choice that programmatically proves the
--                               three pillars of UCC § 12-105(a) against the LIVE,
--                               unencumbered CER holding, and emits a durable,
--                               counterparty-visible CER_ControlAttestation.
--   * CER_ControlAttestation  -- the on-ledger artifact a counterparty node (Broadridge
--                               DLR / GS DAFN / Euroclear-DTCC) reads as proof of
--                               "perfection by control".
--
-- Why a separate record (not the holding's observer set): Holding fixes its
-- observer to \`owner\` at creation. Legal control disclosure must be added/removed
-- per counterparty over the asset's life, so it lives in this dedicated manifest.
--
-- NB: the field is \`controllerParty\`, not \`controller\` -- \`controller\` is a reserved
-- Daml keyword (choice controller clause) and cannot be used as a field name.
module Cer.ControlManifest where

import DA.Optional (isNone)

import Cer.Token (Holding, instrumentId)
import Splice.Api.Token.HoldingV1 (InstrumentId)


-- | Programmatic proof of the three pillars of UCC § 12-105(a) plus the § 12-105(b)
-- identifiability test. Anchored by Verify_Control.
data ControlProof = ControlProof
  { controllerParty : Party        -- the person "in control"
  , instrumentId    : InstrumentId -- the CER instrument the control attaches to
  , assetAmount     : Decimal      -- live holding amount at verification time
  , powerToBenefit  : Bool         -- § 12-105(a)(1): power to avail of substantially all benefit
  , powerToPrevent  : Bool         -- § 12-105(a)(2): exclusive power to prevent others' benefit
  , powerToTransfer : Bool         -- § 12-105(a)(3): exclusive power to transfer control
  , identifiable    : Bool         -- § 12-105(b):    controller readily identifiable from the record
  , verifiedAt      : Time
  , disclosedTo     : [Party]      -- counterparties the proof is exposed to
  }
  deriving (Eq, Show)


-- | Durable, counterparty-visible attestation produced by Verify_Control.
-- Signed by the controller; observable by exactly the disclosed counterparties.
template CER_ControlAttestation
  with
    controllerParty : Party
    proof           : ControlProof
    legalRef        : Text
  where
    signatory controllerParty
    observer proof.disclosedTo


-- | The control manifest binding a controller to a specific CER holding, with a
-- dynamic observer block for bilateral disclosure.
template CER_Control_Manifest
  with
    controllerParty    : Party                    -- K-R-O Brokers -- signatory; the person in "control"
    issuer             : Party                    -- CER registry / instrument admin
    assetHoldingCid    : ContractId Holding -- the CER this manifest attests control over
    assetAmount        : Decimal                  -- declared notional units (sanity bound)
    cashCounterparties : [Party]                  -- DYNAMIC observer block (DvP cash legs)
    legalRef           : Text                     -- e.g. "UCC Art. 12 § 12-105"
  where
    signatory controllerParty
    observer cashCounterparties
    ensure assetAmount > 0.0

    -- Grant a cash counterparty visibility of the control metrics WITHOUT
    -- transferring ownership of the underlying CER. Controller-authorized only.
    choice Manifest_AddObserver : ContractId CER_Control_Manifest
      with newObserver : Party
      controller controllerParty
      do
        assertMsg "Observer already present" (newObserver \`notElem\` cashCounterparties)
        create this with cashCounterparties = newObserver :: cashCounterparties

    -- Revoke a counterparty's visibility.
    choice Manifest_RemoveObserver : ContractId CER_Control_Manifest
      with dropObserver : Party
      controller controllerParty
      do create this with cashCounterparties = filter (/= dropObserver) cashCounterparties

    -- Non-consuming: prove the three § 12-105 pillars against the LIVE holding and
    -- anchor a counterparty-visible attestation. Re-runnable; the manifest persists.
    nonconsuming choice Verify_Control : ContractId CER_ControlAttestation
      controller controllerParty
      do
        h   <- fetch assetHoldingCid
        now <- getTime
        -- Exclusivity test: the asset must be unencumbered (no competing Lock) and
        -- the controller must be a stakeholder of the underlying CER. A holding that
        -- is escrowed into an Allocation (DvP in flight) is locked -> control is not
        -- exclusive -> this correctly fails rather than emitting a false proof.
        let unlocked = isNone h.lock
        assertMsg "Asset holding is locked/encumbered -- control is not exclusive" unlocked
        assertMsg "Manifest controller is not a stakeholder of the asset"
          (controllerParty == h.issuer || controllerParty == h.owner)
        let proof = ControlProof with
              controllerParty
              instrumentId    = instrumentId issuer
              assetAmount     = h.amount
              powerToBenefit  = controllerParty == h.owner || controllerParty == h.issuer
              powerToPrevent  = unlocked
              powerToTransfer = controllerParty == h.issuer
              identifiable    = True
              verifiedAt      = now
              disclosedTo     = cashCounterparties
        create CER_ControlAttestation with controllerParty, proof, legalRef
`;

// ── Actual on-chain deployment data (production, from MainNetRepoScript.daml) ──
const CONTRACT_FACTS = [
  ['Issuer', 'K-R-O Brokers SPC (Seattle, WA)'],
  ['MainNet PartyID', 'KRO-Mainnet-1::1220d605db014187922fb0b11d745aad32cf0700f7945b6a4423e1e4d49c5601ba9b'],
  ['LEI (GLEIF)', '254900QOF5WI2UKAPE82'],
  ['DTIF (ISO 24165)', 'ZX18049M0 — kro.as.accountability.v2'],
  ['CER token', 'CER-TOKEN-USD-A12-MAINNET-001 (UCC Article 12)'],
  ['Prime collateral', 'kro.as.accountability.v2 (UCC Article 12 CER RWA) · DTIF ZX18049M0'],
  ['Package (deployed)', 'CIP-0056 offer/take instrument · v0.1.0'],
  ['Legal finality', 'On-ledger atomic settlement finality (Canton) · UCC Article 12 (CER)'],
];

// ── Settlement Instructions & Routing — mirrors the counterparty ONBOARDING PACKET ──
// Source of truth: backend/data/onboarding-packets/cer-onboarding-mainnet-joint-rfq-1.md
// (ref CIP56-SETTLEMENT-MAINNET-20260704). These are the exact parameters counterparties receive,
// so the Contract Review page shows the same routing/wallet details that get sent out.
const SETTLEMENT_ROUTING = {
  reference: 'CIP56-SETTLEMENT-MAINNET-20260704',
  // Mirrors the backend splice offer settlement.meta (REPO_TRADE_TERMS_DEFAULT in
  // backend/services/cerSettlement.js) — structured standard repo terms embedded
  // on-ledger so acting parties sign a fully-populated offer. Keep in sync.
  tradeTerms: [
    ['Transaction type', 'Intraday repo financing'],
    ['Settlement method', 'Canton Network DvP · T+0 · on-chain · simultaneous cash/collateral exchange'],
    ['Cash amount', 'USD 5,000,000'],
    ['Cash purpose', 'Intraday liquidity funding'],
    ['Collateral', 'Eligible collateral (cash-equivalent) · DTI ZX18049M0'],
    ['Haircut', '2.00%'],
    ['Repo rate', '10 bps per annum (ACT/360)'],
    ['Trade / value date', 'T+0'],
    ['Repurchase', 'Same-day intraday · principal plus accrued (ACT/360)'],
  ],
  participants: [
    ['USDC — cash leg (confirms)', 'Circle', 'circle-validator-1::12209d457bab21f1ce3d52f979cdf021c2990cb74cf01ef2dcf41bf87a79ddaf3ba8'],
    ['Liquidity — liquidity leg (confirms)', 'Cumberland DRW', 'Cumberland-DRWS-1::1220f792fc36ceb9f88536e862f0923f1b655cccb9a711ee4d5ede1397ad722bb155'],
    ['Informed party (observer)', 'T-RIZE Group', 'TRIZEGroup-cantonMainnetValidator-1::12206ab3bf15b14410220357d6a6375eb1015f2e7fade1deb449463c2f2a25304889'],
    ['Arranging hub / issuer / coordinator (delivers KRO.CER)', 'K-R-O Brokers, SPC', 'KRO-Mainnet-1::1220d605db014187922fb0b11d745aad32cf0700f7945b6a4423e1e4d49c5601ba9b'],
  ],
  settlement: [
    ['Payee party ID (KRO wallet)', 'KRO-Mainnet-1::1220d605db014187922fb0b11d745aad32cf0700f7945b6a4423e1e4d49c5601ba9b'],
    ['Payer party ID', 'Cumberland-DRWS-1::1220f792fc36ceb9f88536e862f0923f1b655cccb9a711ee4d5ede1397ad722bb155'],
    ['Canton payment gateway', 'Canton Network — Global Synchronizer (on-ledger; the payee party ID is the settlement address, no off-ledger rails)'],
    ['Settlement rail', 'CIP-0056 Allocation (splice-api-token-allocation-v1) → Allocation_ExecuteTransfer — atomic DvP, keyed by settlementRef'],
    ['Cash instrument', 'USDCx — InstrumentId { admin: circle-validator-1::12209d457bab…, id: USDCx }'],
    ['Collateral instrument', 'KRO.CER — InstrumentId { admin (issuer): KRO-Mainnet-1::1220d605db…, id: KRO.CER }'],
  ],
  appendix: [
    ['KRO Party ID (wallet)', 'KRO-Mainnet-1::1220d605db014187922fb0b11d745aad32cf0700f7945b6a4423e1e4d49c5601ba9b'],
    ['Validator / wallet host', 'wallet.validator.canton-mainnet.kromarketplace.com'],
    ['Digital Instrument Record (DTIF)', 'ZX18049M0'],
    ['Collateral reference (SHA-256)', '382787abfa710d5ae7032b5ce5ef20e5387f6d4f733e4484022d927324de2958'],
  ],
};

// ── On-chain settlement evidence — SHA-256 document key + KRO's qualified custodian ──
// Source-of-wealth evidence is the SHA-256 collateral-reference root (MarkerCollateral.sha256Ref),
// anchored on-ledger. Custody sits with BitGo as an INFORMEE party holding KRO's qualified
// custody; the assets never leave KRO's technical control.
const SETTLEMENT_EVIDENCE = {
  sha256Key: '382787abfa710d5ae7032b5ce5ef20e5387f6d4f733e4484022d927324de2958',
  custody: [
    ['Custodian', 'BitGo Enterprise — MPC Custody'],
    ['Attestation', 'Enterprise onboarded custody wallet'],
    ['Role', 'Informee party — assets never leave KRO technical control'],
  ],
};

// ── Who sees what — per-section audience map for the Contract Review page ──
// Answers "which sections are relevant to each party?" and flags the sections that are
// KRO-internal reference only (not part of counterparty engagement). `kind`:
//   'action'     — live settlement path; the listed parties act on / are bound by it.
//   'shared-ref' — shared reference / context; visible to all, informational (not an action).
//   'kro-ref'    — K-R-O internal reference ONLY (operations / go-to-market — not counterparty-facing).
const PARTY_TAGS = {
  KRO: { label: 'K-R-O',            c: '#34d399' },
  CIR: { label: 'Circle',           c: '#6ee7b7' },
  CUM: { label: 'Cumberland',       c: '#93c5fd' },
  TRZ: { label: 'T-RIZE',           c: '#fcd34d' },
  GSF: { label: 'GSF',              c: '#fbbf24' },
  CUS: { label: 'Custodian (BitGo)',c: '#f59e0b' },
  CLR: { label: 'Clearing agent',   c: '#a78bfa' },
};
const SECTION_KIND = {
  action:       { label: 'Live settlement', c: '#10b981' },
  'shared-ref': { label: 'Shared reference', c: '#60a5fa' },
  'kro-ref':    { label: 'K-R-O reference only', c: '#94a3b8' },
};
const SECTION_AUDIENCE = [
  { s: 'Recent ledger events (outbound / internal)',                 who: ['KRO'],                       kind: 'kro-ref' },
  { s: 'Build settlement command',                                   who: ['KRO'],                       kind: 'kro-ref' },
  { s: 'Atomic DvP — 5-step deterministic sequence',                 who: 'ALL',                         kind: 'action' },
  { s: 'Counterparty PartyIds — determinism check',                  who: 'ALL',                         kind: 'action' },
  { s: 'CIP-0056 Holding.lock — collateral control',                 who: ['KRO','CIR','CUM','CUS'],     kind: 'action' },
  { s: 'Validator Activity Rewards — Traffic Rebate',                who: ['KRO'],                       kind: 'kro-ref' },
  { s: 'Actual on-chain deployment data (production)',               who: 'ALL',                         kind: 'shared-ref' },
  { s: 'CIP-56 Token — Instrument Reference (DTIF)',                 who: 'ALL',                         kind: 'shared-ref' },
  { s: 'Multi-Party Intraday Settlement (Acting Parties & Routing)', who: 'ALL',                         kind: 'action' },
  { s: 'PCP Collateral Settlement Flow — Main.daml',                 who: ['KRO','CUS'],                 kind: 'kro-ref' },
  { s: 'Daml source — offer/take instrument',                        who: 'ALL',                         kind: 'shared-ref' },
  { s: 'Settlement Instructions & Routing',                          who: ['KRO','CIR','CUM','TRZ','GSF'], kind: 'action' },
  { s: 'Settlement Evidence — Source of Wealth & Custody',  who: 'ALL',                         kind: 'shared-ref' },
  { s: 'Daml source — UCC Art. 12 control manifest',                 who: 'ALL',                         kind: 'shared-ref' },
  { s: 'Intraday Repo Flow — Repo/Intraday.daml',                    who: ['KRO','CUS','CLR','TRZ','GSF'], kind: 'action' },
  { s: 'View on Canton Lighthouse',                                  who: 'ALL',                         kind: 'shared-ref' },
  { s: 'Target institutions not yet on Canton',                      who: ['KRO'],                       kind: 'kro-ref' },
  { s: 'What You Can Track in Lighthouse',                           who: 'ALL',                         kind: 'shared-ref' },
  { s: 'Where the Contact Card Appears',                             who: ['KRO'],                       kind: 'kro-ref' },
  { s: 'Ledger API — Contract Engagement Endpoints',                 who: ['KRO','CIR','CUM','TRZ','GSF'], kind: 'shared-ref' },
];

// ── PCP collateral settlement flow (Main.daml) — templates → choices ──
const PCP_FLOW = [
  { t: 'MarkerCollateral',          sig: 'kro · custodian',     choices: ['InspectCollateral', 'LockForPledge', 'UpdateCustodian'], data: '182 units × $5M = $910M (kro.as.accountability.v2)' },
  { t: 'CollateralOffer',           sig: 'offeror',             choices: ['AcceptCollateral', 'WithdrawOffer'],                     data: 'clean/unencumbered offer → eligible takers, FCFS · 2% initialMargin · pricingBps · positionExpiry' },
  { t: 'CollateralPosition',        sig: 'offeror · taker',     choices: ['SettleCollateral', 'ReturnCollateral'],                  data: 'cash leg verified (verifyCashLeg) → unlock + receipt · ReturnCollateral after expiry' },
  { t: 'PledgeWorkflow',            sig: 'kro',                 choices: ['AcceptPledge', 'RevokePledge'],                          data: '$10M pledge ≤ $910M · eligibilityProfileId · settlementSpec' },
  { t: 'DrawWorkflow',              sig: 'kro · counterparty',  choices: ['ConfirmDraw'],                                           data: 'carries eligibility + settlement spec' },
  { t: 'SettlementWorkflow',        sig: 'kro · counterparty',  choices: ['Settle', 'RejectSettlement'],                            data: 'ethicsFundEnabled = False (pilot)' },
  { t: 'CloseOutReceipt',           sig: 'kro · counterparty',  choices: ['—'],                                                     data: 'immutable · +eligibilityProfileId +settlementSpec' },
];

// ── Intraday repo flow (Repo/Intraday.daml) — templates → choices ──
const REPO_TEMPLATES = [
  { t: 'IntradayRepoRFQ',         choices: ['AcceptRFQ', 'CancelRFQ', 'ExpireRFQ'],                                                                                            data: '' },
  { t: 'IntradayRepoFacility',    choices: ['OpenDraw*', 'AdjustUsedCapacity'],                                                                                                data: 'standing revolving facility (*nonconsuming)' },
  { t: 'IntradayRepoDraw',        choices: ['CustodianApproveCollateral', 'ClearingAgentApprove', 'ReleaseFunds', 'RenewDraw', 'CancelDraw', 'SyncFacilityCid', 'ComputeIntradayCost*'], data: 'draw lifecycle: AWAITING_PAYMENT→CREATED→CLEARED→RELEASED→ROLLED (*nonconsuming; DeclareDefault retained as disabled upgrade stub)' },
  { t: 'IntradayCloseOutReceipt', choices: ['—'],                                                                                                                              data: 'per-cycle rollover close-out receipt (immutable; not a terminal repayment)' },
  { t: 'RolloverRecord',          choices: ['—'],                                                                                                                              data: 'immutable per-cycle rollover audit trail' },
  { t: 'HeritageNetworkRecord',   choices: ['—'],                                                                                                                              data: 'immutable heritage anchor created atomically by AcceptRFQ' },
];

// ── Actual MainNet pilot lifecycle (MainNetRepoScript:mainnetRepoSetup) ──
// These are the SIX choices the pilot actually exercised on Repo/Intraday.daml, in order, with
// the module's real status vocabulary (AWAITING_PAYMENT → CREATED → CLEARED → RELEASED → ROLLED).
// The MainNet pilot ran as a single-party self-deal (all roles = KRO-Mainnet-1); in the general
// model the draw's stakeholders are lender + borrower (signatories) and custodian, clearingAgent,
// kroOS (observers). IMPORTANT: GSF & T-RIZE are NOT stakeholders on the repo draw — they are
// informees on the CIP-0056 allocation that settles the value legs (see the Multi-Party Intraday
// Settlement card); that is the surface where they witness the DvP lifecycle.
// `kind`:
//   'step' — deterministic transition; the consuming choice CREATES the next draw contract (a
//            witnessed ledger event) or the submission is rejected (no contract is created).
//   'gate' — an approval controlled by one party (custody / clearing). PASS creates the next draw
//            (witnessed); a failed precondition (assertMsg) rejects the submission with NO ledger
//            event; CancelDraw (pre-release) is the explicit, witnessed exit.
//   'auto' — fires under the standing facility authorization (revolving rollover; no return leg).
// `emits` = the observable on-ledger status the draw's stakeholders witness at this stage.
const REPO_MAINNET_LIFECYCLE = [
  { n: '1', cmd: 'create IntradayRepoFacility', kind: 'step', emits: 'FACILITY_OPEN — witnessed by lender, borrower (sig) + custodian, clearingAgent, kroOS (obs)', data: 'FAC-KRO-MAINNET-V2-001 · $755M capacity · 2% haircut · 10bps intraday · LEI 254900QOF5WI2UKAPE82' },
  { n: '2', cmd: 'OpenDraw', kind: 'step', emits: 'AWAITING_PAYMENT — new IntradayRepoDraw created (witnessed)', data: 'DRAW-KRO-MAINNET-V2-001 · $10M · DTIF ZX18049M0 · collateral kro.as.accountability.v2 $10.5M · capacity reserved (ensure adjusted ≥ notional)' },
  { n: '3', cmd: 'CustodianApproveCollateral', kind: 'gate', emits: 'CREATED — draw re-created with custodianApproved=True (witnessed)', data: 'BitGo confirms KRO.CER is held in the qualified-custody enterprise wallet (pb1mktt…5mp6) — custody wallet confirmation only · no value moved · no other role or agreement' },
  { n: '4', cmd: 'ClearingAgentApprove', kind: 'gate', emits: 'CLEARED — draw re-created with clearingApproved=True (witnessed)', data: 'Cumberland DRW clearing approval · requires custodianApproved first' },
  { n: '5', cmd: 'ReleaseFunds', kind: 'step', emits: 'RELEASED — draw re-created with fundsReleased=True (authorization flag; witnessed)', data: 'Requires both gates + not-already-released. Authorization marker only — the atomic value settlement is the separate CIP-0056 Allocation_ExecuteTransfer (one tx, all legs).' },
  { n: '6', cmd: 'RenewDraw (cycle 1)', kind: 'auto', emits: 'ROLLED — IntradayCloseOutReceipt + new pre-approved draw + RolloverRecord (all witnessed)', data: 'Standing-facility auto-rollover · closes cycle N / opens cycle N+1 · net exposure unchanged · NO return-of-funds — the position revolves, never repaid' },
];

// Status marker vocabulary shared with the read-tracking parties (GSF, T-RIZE). Each stage
// resolves to exactly one of these — and, critically, a FAIL/STALL is an EXPLICIT on-ledger
// event (or an off-ledger deadline miss), never a silent aborted submission.
const STAGE_KIND = {
  step: { label: 'STEP', tip: 'Deterministic transition — the consuming choice creates the next draw contract (a witnessed ledger event) or the submission is rejected', c: '#10b981', icon: 'bi-check-circle-fill' },
  gate: { label: 'GATE', tip: 'Approval gate — PASS creates the next draw (witnessed); a failed precondition rejects the submission with no ledger event; CancelDraw (pre-release) is the explicit witnessed exit', c: '#f59e0b', icon: 'bi-shield-exclamation' },
  auto: { label: 'AUTO', tip: 'Fires under the standing facility authorization — revolving rollover (no return-of-funds)', c: '#60a5fa', icon: 'bi-arrow-repeat' },
};

// "How do we notify them where it breaks down?" — answered with STANDARD CIP-0056 lifecycle,
// not a bespoke status contract. The canonical Allocation lifecycle already emits WITNESSED
// ledger events for every outcome: informees (GSF, T-RIZE) are observers on the standard
// AllocationRequest / Allocation, so they receive the create of each leg, the settle, and any
// standard exit (Reject / Cancel / Withdraw). A stall = the standard settlement window
// (allocateBefore / settleBefore) elapsing, which is detected off-ledger by deadline.
const STAGE_STATUS_SRC = `-- Canton Network Token Standard (CIP-0056) · 0.4.0 · standard allocation lifecycle
--
-- Observers see WHERE it breaks down through STANDARD interface events — no custom types.
-- (A bare 'assertMsg' abort is invisible to observers because a rejected Canton submission
--  produces no ledger event; the token standard avoids that by using explicit choices.)

import Splice.Api.Token.AllocationV1        -- Allocation, Allocation_ExecuteTransfer, _Cancel, _Withdraw
import Splice.Api.Token.AllocationRequestV1 -- AllocationRequest, AllocationRequest_Reject

-- The observable state machine IS the standard allocation lifecycle. Each transition is a
-- witnessed create/exercise that informees (observers on the request + allocations) receive:
--
--   AllocationRequest created         →  REQUESTED   (informees witness the offer + legs)
--   Allocation created (per leg)      →  ALLOCATED   (a counterparty funded its leg)
--   Allocation_ExecuteTransfer        →  EXECUTED    (settled — delivered Holdings created)   [SUCCESS]
--   AllocationRequest_Reject          →  REJECTED    (offer declined — witnessed)             [BREAK]
--   Allocation_Cancel / _Withdraw     →  CANCELED    (a funded leg pulled back — witnessed)   [BREAK]
--   past allocateBefore / settleBefore →  EXPIRED    (standard window elapsed)                [STALL]
--
-- SUCCESS (executor delivers all legs atomically in one Canton transaction):
--   exercise allocationCid Allocation_ExecuteTransfer with extraArgs
--     -> Allocation_ExecuteTransferResult { senderHoldingCids, receiverHoldingCids, meta }
--
-- BREAK (any party's standard exit is a real event the informees see — not a silent abort):
--   exercise allocationRequestCid AllocationRequest_Reject   -- offer declined
--   exercise allocationCid        Allocation_Cancel          -- funded leg canceled
--   exercise allocationCid        Allocation_Withdraw        -- funded leg withdrawn
--
-- STALL: no exit is exercised before the standard settlement window closes. Inaction emits no
-- ledger event by construction, so it is caught OFF-ledger: a watcher on the JSON Ledger API
-- v2 stream (filtered to the informee parties) flags any settlementRef still un-EXECUTED past
-- its allocateBefore / settleBefore and alerts GSF & T-RIZE exactly where it is stuck.
`;

// ── Current token-standard offer topology — Canton Network Token Standard (CIP-0056) ──
// v0.4.0 is the current DAR. JointAllocationRequest is the multi-party confirming
// variant: requesters are SIGNATORIES alongside issuer (each named counterparty is an
// ACTING + CONFIRMING party), and `informing` is an OPTIONAL observer-only set (parties
// that must SEE the offer without CONFIRMING it). `informing` is an appended Optional →
// an upgrade-safe superset (older readers see None).
const JOINT_ALLOC_SRC = `-- Canton Network Token Standard (CIP-0056) · 0.4.0  (current)
-- Multi-party confirming variant. requesters are SIGNATORIES alongside issuer, so every
-- named counterparty is an ACTING + CONFIRMING party. \`informing\` is an OPTIONAL
-- observer-only set (parties that must SEE the offer without CONFIRMING it).
template JointAllocationRequest
  with
    issuer       : Party
    settlement   : SettlementInfo
    transferLegs : TextMap TransferLeg
    requesters   : [Party]                 -- CONFIRMING parties (form the threshold)
    informing    : Optional [Party]        -- OBSERVER-only informees
  where
    signatory issuer, requesters           -- issuer + requesters sign (threshold)
    observer (fromOptional [] informing)   -- informees witness; do NOT confirm
`;

// CURRENT multi-party intraday DvP — expressed in STANDARD CIP-0056 token-standard language.
// No bespoke settlement types: the intraday draw settles through the canonical Splice token
// interfaces (AllocationRequest → Allocation → Allocation_ExecuteTransfer). Our parameters
// (KRO.CER instrument, the three identified legs, KRO executor) fill the standard shapes.
const INTRADAY_MULTIPARTY_SRC = `-- Canton Network Token Standard (CIP-0056) · 0.4.0 · CIP-0056 intraday DvP
-- STANDARD interfaces only (Splice token standard) — nothing bespoke. Authorization &
-- atomicity come from the platform (parties Validate / Control / Observe; atomic commit via
-- the mediator's single threshold verdict), exactly as in the Canton architecture model.

import Splice.Api.Token.MetadataV1          -- InstrumentId, Metadata
import Splice.Api.Token.HoldingV1           -- Holding
import Splice.Api.Token.AllocationV1        -- Allocation, Allocation_ExecuteTransfer
import Splice.Api.Token.AllocationRequestV1 -- AllocationRequest, SettlementInfo, TransferLeg

-- Instrument: the KRO.CER Controllable-Electronic-Record token — WRAPPED from the Provenance
-- mainnet marker (denom kro.as.accountability.v2), anchored on Canton by a SHA-256 scope
-- record, and exposed through the standard Holding interface. (Wrap already done.)
--   InstrumentId { admin = kro, id = "KRO.CER" }   (DTIF ZX18049M0, ISO 24165)

-- One standard SettlementInfo binds every leg to a shared settlementRef (no custom fields):
--   settlement = SettlementInfo with
--     executor       = kro                         -- arranging hub / settlement executor
--     settlementRef  = Reference with id = uetr    -- correlates all legs of the draw
--     requestedAt; allocateBefore; settleBefore    -- the STANDARD settlement window
--
-- transferLegs : TextMap TransferLeg — one STANDARD leg each (2-leg atomic DvP):
--   "cash"  TransferLeg { sender = circle | cumberland, receiver = kro,   amount, instrumentId = usdc/cash } -- cash leg — EITHER Circle OR Cumberland funds it (1-of-2 CONFIRMS)
--   "asset" TransferLeg { sender = kro,                 receiver = trize, amount, instrumentId = cer  }      -- KRO.CER collateral, KRO-delivered → T-RIZE

-- The offer IS the canonical AllocationRequest interface. The cash leg is fundable by EITHER Circle
-- OR Cumberland (1-of-2, threshold 1); T-RIZE (collateral-leg receiver) + BitGo (custodian) are informees.
template JointAllocationRequest   -- implements AllocationRequestV1.AllocationRequest
  with
    issuer       : Party                -- KRO — instrument admin + settlement executor
    settlement   : SettlementInfo       -- standard
    transferLegs : TextMap TransferLeg  -- standard — the legs above
    requesters   : [Party]              -- Eligible cash-leg funders (observers): [circle, cumberland] · 1-of-2
    informing    : Optional [Party]     -- Informing Parties (observers): [trize, bitgo, gsf]
  where
    signatory issuer                    -- KRO sole signatory; disclosed to funders (observer model)
    observer  requesters, (fromOptional [] informing)  -- funders + informees witness; the funder that allocates the cash leg confirms → threshold 1

    interface instance AllocationRequest for JointAllocationRequest where
      view = AllocationRequestView with settlement, transferLegs, meta = emptyMetadata
      -- standard exit path — a WITNESSED ledger event, never a silent abort:
      allocationRequest_RejectImpl   _ _ = pure ChoiceExecutionMetadata with meta = emptyMetadata
      allocationRequest_WithdrawImpl _ _ = pure ChoiceExecutionMetadata with meta = emptyMetadata

-- Each counterparty funds its leg by creating the STANDARD Allocation (escrow) against the
-- shared settlementRef; KRO (executor) then settles ALL legs atomically with one choice:
--   exercise allocationCid Allocation_ExecuteTransfer   -- one Canton tx, all-or-nothing
-- Standard witnessed exits: Allocation_Cancel · Allocation_Withdraw · AllocationRequest_Reject.
`;

// Confirmed topology: K-R-O hub (acting) + cash leg funded by EITHER Circle OR Cumberland (1-of-2,
// threshold 1) + informees. `access`: 'hub' | 'confirm' | 'inform'.
const ACTING_PARTIES = [
  { party: 'K-R-O (hub)',    role: 'Arranging hub / issuer / settlement coordinator', access: 'hub',     sees: 'Signs + submits · delivers KRO.CER collateral leg' },
  { party: 'Circle',         role: 'Cash leg — USDCx (eligible funder · 1-of-2)',      access: 'confirm', sees: 'The cash leg — confirms if it funds the leg' },
  { party: 'Cumberland DRW', role: 'Cash leg — alternate funder (eligible · 1-of-2)',  access: 'confirm', sees: 'The cash leg — confirms if it funds the leg' },
  { party: 'T-RIZE Group',   role: 'Collateral leg — receiver (observer)',             access: 'inform',  sees: 'Witnesses the lifecycle; confirms nothing' },
  { party: 'BitGo',          role: 'Qualified custodian — custody informee',           access: 'inform',  sees: 'Custody reference only · no value moved' },
  { party: 'GSF',            role: 'Global Synchronizer Foundation — governance',      access: 'inform',  sees: 'Governance oversight of the lifecycle; confirms nothing' },
];

// STANDARD CIP-0056 allocation lifecycle states — the on-ledger events every acting party
// (and the informee trackers) witness. No bespoke statuses: these ARE the token-standard
// AllocationRequest / Allocation transitions.
const SETTLEMENT_STATUS_VIEW = [
  { s: 'REQUESTED', d: 'AllocationRequest created — offer + legs published to parties',   c: '#64748b' },
  { s: 'ALLOCATED', d: 'Allocation created — a counterparty funded (escrowed) its leg',   c: '#0ea5e9' },
  { s: 'EXECUTED',  d: 'Allocation_ExecuteTransfer — atomic DvP settled (Holdings created)', c: '#10b981' },
  { s: 'REJECTED',  d: 'AllocationRequest_Reject — offer declined (witnessed)',           c: '#ef4444' },
  { s: 'CANCELED', d: 'Allocation_Cancel / _Withdraw — funded leg pulled back',          c: '#f59e0b' },
  { s: 'EXPIRED',   d: 'Past allocateBefore / settleBefore — standard window elapsed',    c: '#a78bfa' },
];

// Settlement parameters for the multi-party atomic DvP flow.
const JOINT_SETTLEMENT_PARAMS = [
  ['Model',                'Atomic DvP — 2-leg intraday repo (collateral + cash) · K-R-O hub + cash funder (1-of-2)'],
  ['Interfaces',           'CIP-0056 standard — AllocationRequestV1.AllocationRequest → AllocationV1.Allocation → Allocation_ExecuteTransfer (Splice token standard)'],
  ['Template',             'CIP-0056 AllocationRequest (Splice token standard) · v0.4.0'],
  ['Settlement type',      'Atomic DvP · Allocation_ExecuteTransfer · single Canton round'],
  ['Wallet integration',   'Standard CIP-0056 TransferFactory (FOP payment) + AllocationFactory (DvP) — published by the KRO.CER registry; served by any compliant Canton wallet'],
  ['Counterparty action',  'The cash-leg funder allocates from its standard wallet — AllocationFactory.Allocate against the shared settlementRef; KRO executes Allocation_ExecuteTransfer (one atomic round)'],
  ['Confirming threshold', '1 — cash leg funded by either Circle (USDCx) or Cumberland DRW (1-of-2)'],
  ['Informing parties',    'BitGo — qualified-custody informee · GSF — Global Synchronizer Foundation (governance observer)'],
  ['Settlement window',    'allocateBefore +20h < settleBefore +24h — a finality buffer to run Allocation_ExecuteTransfer after the cash leg is allocated'],
  ['Arranging hub / issuer', 'K-R-O — initiates, co-signs & coordinates; issues + delivers the collateral leg'],
  ['Instrument',           'Eligible collateral (cash-equivalent) · DTI ZX18049M0 (ISO 24165 DTIF)'],
  ['Collateral leg',       'KRO → cash funder (Circle or Cumberland) · eligible collateral (cash-equivalent) · KRO-delivered · atomic DvP'],
  ['Cash leg',             'Circle (USDCx) or Cumberland DRW → KRO · single cash leg (1-of-2 funder)'],
  ['Privacy',              'Need-to-know — each counterparty sees / signs only its own leg (Canton sub-transaction privacy)'],
  ['Synchronizer',         'global-domain::1220b1431ef2… (Global Synchronizer)'],
  ['Package',              'Canton Network Token Standard (CIP-0056) · v0.4.0 · CIP-0056 offer/take instrument · v0.1.0'],
];

const parseMeta = (m) => {
  if (!m) return {};
  if (typeof m === 'object') return m;
  try { return JSON.parse(m); } catch { return {}; }
};

const fmtUsd = (v) => (v == null || v === '' ? '—' : `$${Number(v).toLocaleString()}`);

// Intraday repo lifecycle stages, in settlement order.
const REPO_LIFECYCLE = [
  { type: 'INTRADAY_RFP',      label: 'RFQ Broadcast',      icon: 'bi-broadcast',          color: '#0ea5e9' },
  { type: 'INTRADAY_FACILITY', label: 'Repo Facility',      icon: 'bi-bank2',              color: '#3b82f6' },
  { type: 'INTRADAY_DRAW',     label: 'Repo Draw',          icon: 'bi-cash-stack',         color: '#6366f1' },
  { type: 'REPO_ROLLOVER',     label: 'Automatic Rollover', icon: 'bi-arrow-repeat',       color: '#14b8a6' },
  { type: 'INTRADAY_CLOSEOUT', label: 'Close-Out Receipt',  icon: 'bi-file-earmark-check', color: '#8b5cf6' },
  { type: 'HERITAGE_RECORD',   label: 'Heritage Anchor',    icon: 'bi-link-45deg',         color: '#ec4899' },
];

// Per-type metadata projection → [label, value] rows for the lifecycle cards.
function repoFields(type, row, m) {
  switch (type) {
    case 'INTRADAY_RFP':
      return [['Notional', fmtUsd(row.amount_usd)], ['DTIF', row.uetr], ['RFQ correlation ref', m.rfqCorrelationRef]];
    case 'INTRADAY_FACILITY':
      return [['Facility', m.facilityId], ['Capacity', fmtUsd(m.capacityLimit)], ['Used', fmtUsd(m.usedCapacity)]];
    case 'INTRADAY_DRAW':
      return [['Draw', m.drawId], ['Notional', fmtUsd(row.amount_usd)],
              ['Cleared', m.clearingApproved ? 'yes' : 'no'], ['Released', m.fundsReleased ? 'yes' : 'no']];
    case 'REPO_ROLLOVER':
      return [['Cycle', m.cycleNumber], ['Closed', m.closedDrawId], ['Opened', m.newDrawId],
              ['Notional', fmtUsd(row.amount_usd)], ['Financing cost', fmtUsd(m.finalCostUsd)]];
    case 'INTRADAY_CLOSEOUT':
      return [['Draw', m.drawId], ['Settled', fmtUsd(row.amount_usd)], ['Financing cost', fmtUsd(m.finalCostUsd)],
              ['Settlement ref', m.settlementRef], ['Prov TX', m.settlementTxHash]];
    case 'HERITAGE_RECORD':
      return [['Draw ref', row.uetr], ['Lender LEI', m.lenderLEI], ['RFQ correlation ref', m.rfqCorrelationRef]];
    default:
      return [['Amount', fmtUsd(row.amount_usd)]];
  }
}

export default function CantonNetworkHub() {
  const [activeEnv,     setActiveEnv]     = useState('mainnet');
  const [activeTab,     setActiveTab]     = useState('overview');
  const [summaryData,   setSummaryData]   = useState({});
  const [recentEvents,  setRecentEvents]  = useState([]);
  const [eventLimit,    setEventLimit]    = useState(100);
  const [repoContracts, setRepoContracts] = useState([]);
  const [liveStatus,    setLiveStatus]    = useState(null);
  const [liveStatusErr, setLiveStatusErr] = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [lastRefresh,   setLastRefresh]   = useState(null);
  const [copied,        setCopied]        = useState(null);
  const [inbound,       setInbound]       = useState(null);
  const [inboundMeta,   setInboundMeta]   = useState(null);
  const [inboundErr,    setInboundErr]    = useState(null);
  const [inboundAt,     setInboundAt]     = useState(null);
  // Live counterparty / registry / broadcast state — reconciles the hardcoded
  // constants below against the backend source of truth (falls back to the
  // constants only when an endpoint is unreachable).
  const [scope,         setScope]         = useState(null);
  const [svLive,        setSvLive]        = useState(null);
  const [broadcasts,    setBroadcasts]    = useState(null);
  const [transferOffers, setTransferOffers] = useState(null);
  // Contract Review is trimmed to the bare-minimum counterparty view (settlement routing,
  // evidence, execution flow). The removed sections are retained as a K-R-O-only archived
  // record behind this toggle (default hidden — not part of the acting-party view).
  const [showKroArchive, setShowKroArchive] = useState(false);
  const [jointOffers,   setJointOffers]   = useState(null);
  const intervalRef = useRef(null);
  const inboundRef  = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, evtRes, repoRes, statusRes, scopeRes, svRes] = await Promise.all([
        safeFetch(`${API_BASE}/api/v1/canton/summary`),
        safeFetch(`${API_BASE}/api/v1/canton/contracts?env=${activeEnv}&limit=${eventLimit}`),
        safeFetch(`${API_BASE}/api/v1/canton/contracts?env=${activeEnv}&limit=100`),
        safeFetch(`${API_BASE}/api/v1/canton/network-status?network=${activeEnv}`),
        safeFetch(`${API_BASE}/api/v1/canton/counterparty-scope`),
        safeFetch(`${API_BASE}/api/v1/canton/sv-registry`),
      ]);
      if (sumRes.ok) setSummaryData((await sumRes.json()).summary || {});
      if (evtRes.ok) setRecentEvents((await evtRes.json()).records || []);
      if (repoRes.ok) {
        const all = (await repoRes.json()).records || [];
        setRepoContracts(all.filter(r => REPO_EVENT_TYPES.includes(r.event_type)));
      }
      if (scopeRes.ok) { const j = await scopeRes.json(); if (j.success) setScope(j); }
      if (svRes.ok) {
        const j = await svRes.json();
        // Normalize backend `cantonPartyId` → `partyId` so live entries render
        // through the same fields the hardcoded lists use.
        if (j.success) setSvLive({
          ...j,
          superValidators: (j.superValidators || []).map(s => ({ ...s, partyId: s.cantonPartyId })),
          settlementOperators: (j.settlementOperators || []).map(s => ({ ...s, partyId: s.cantonPartyId })),
        });
      }
      if (statusRes.ok) {
        const j = await statusRes.json();
        if (j.success) { setLiveStatus(j.status); setLiveStatusErr(null); }
        else { setLiveStatus(null); setLiveStatusErr(j.error || 'unavailable'); }
      } else {
        const j = await statusRes.json().catch(() => ({}));
        setLiveStatus(null); setLiveStatusErr(j.error || `status ${statusRes.status}`);
      }
    } catch (_) {}
    setLoading(false);
    setLastRefresh(new Date());
  }, [activeEnv, eventLimit]);

  useEffect(() => {
    fetchData();
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchData, 30_000);
    return () => clearInterval(intervalRef.current);
  }, [fetchData]);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  const env = ENVS.find(e => e.id === activeEnv);

  // ── Live-vs-static reconciliation helpers ────────────────────────────────────
  // The backend gate (CER_RFQ_PILOT_GREENLIT) is the source of truth for who is
  // greenlit; the constants above are only a fallback for offline rendering.
  const liveGreenlit    = scope?.greenlitSettlement || null;         // actual on-ledger greenlit operators
  const liveObserve     = scope?.settlementOperatorsObserve || null; // observe / non-broadcast operators
  const liveGreenlitIds = liveGreenlit ? new Set(liveGreenlit.map(o => o.cantonPartyId)) : null;
  const isGreenlit      = (node) => liveGreenlitIds ? liveGreenlitIds.has(node.partyId) : isGreenlitSv(node);
  const greenlitCount   = liveGreenlit ? liveGreenlit.length : CANTON_SV_REGISTRY.filter(isGreenlitSv).length;
  const svRegistryList  = svLive?.superValidators?.length ? svLive.superValidators : CANTON_SV_REGISTRY;
  const collateralUseCases = scope?.collateralMobilityUseCases?.length ? scope.collateralMobilityUseCases : COLLATERAL_MOBILITY_USE_CASES;
  const offChainPrime   = scope?.offChainPrime?.length
    ? scope.offChainPrime.map(t => ({ id: t.id, name: t.name, reachVia: t.reachVia || t.notes || 'Not on RFQ — future access pending Foundation', status: t.status || 'NOT_ON_MAINNET' }))
    : OFF_CHAIN_PRIME_TARGETS;
  const liveScope       = Boolean(scope);   // true once the backend scope has loaded
  const rfqBroadcastCount = broadcasts?.total ?? RFP_TRANSACTIONS.length;

  // ── CIP-56 CER token settlement ──────────────────────────────────────────────
  const [cerStatus,   setCerStatus]   = useState(null);
  const [cerHoldings, setCerHoldings] = useState([]);
  const [cerBusy,     setCerBusy]     = useState(false);
  const [cerResult,   setCerResult]   = useState(null);
  const [cerForm,     setCerForm]     = useState({ receiver: '', amount: '', cashLeg: 'CIP56' });
  const [cerReward,   setCerReward]   = useState(null);

  // ── Pre-deploy contract preview (IntradayRepoRFP, before submitting) ─────────
  const [rfpPreview,     setRfpPreview]     = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError,   setPreviewError]   = useState(null);
  const [expandedJson,   setExpandedJson]   = useState({});

  const loadRfpPreview = useCallback(async () => {
    setPreviewLoading(true); setPreviewError(null);
    try {
      const r = await safeFetch(`${API_BASE}/api/v1/canton/repo-rfp-preview?network=${activeEnv}`);
      const j = await r.json();
      if (j.success) setRfpPreview(j);
      else { setRfpPreview(null); setPreviewError(j.error || 'Preview unavailable'); }
    } catch (e) {
      setRfpPreview(null); setPreviewError(String(e));
    }
    setPreviewLoading(false);
  }, [activeEnv]);

  useEffect(() => { if (activeTab === 'rfp') loadRfpPreview(); }, [activeTab, loadRfpPreview]);

  // ── Live RFQ broadcast log + Canton Coin carrier offers + joint RFQ offers ────
  const loadBroadcasts = useCallback(async () => {
    try {
      const [b, t, j] = await Promise.all([
        safeFetch(`${API_BASE}/api/v1/canton/rfq-broadcasts?limit=200`),
        safeFetch(`${API_BASE}/api/v1/canton/transfer-offers`),
        safeFetch(`${API_BASE}/api/v1/canton/joint-offers`),
      ]);
      if (b.ok) { const jb = await b.json(); if (jb.success) setBroadcasts(jb); }
      if (t.ok) { const jt = await t.json(); if (jt.success) setTransferOffers(jt); }
      if (j.ok) { const jj = await j.json(); if (jj.success) setJointOffers(jj); }
    } catch (_) {}
  }, []);
  useEffect(() => { if (activeTab === 'rfp') loadBroadcasts(); }, [activeTab, loadBroadcasts]);

  // ── Live inbound counterparty activity (contracts + event transactions) ──────
  const loadInbound = useCallback(async () => {
    try {
      const r = await safeFetch(`${API_BASE}/api/v1/canton/inbound?network=${activeEnv}&limit=150`);
      const j = await r.json();
      if (r.ok && j.success) { setInbound(j.activity); setInboundMeta(j.reconciliation || (j.reconciled ? {} : null)); setInboundErr(null); }
      else { setInbound(null); setInboundMeta(null); setInboundErr(j.error || `status ${r.status}`); }
    } catch (e) { setInbound(null); setInboundMeta(null); setInboundErr(String(e)); }
    setInboundAt(new Date());
  }, [activeEnv]);

  // Real-time: poll every 15s while the Inbound tab is open, and immediately on
  // env change. The endpoint is cached 12s server-side so this is cheap.
  useEffect(() => {
    clearInterval(inboundRef.current);
    if (activeTab === 'inbound') {
      loadInbound();
      inboundRef.current = setInterval(loadInbound, 15_000);
    }
    return () => clearInterval(inboundRef.current);
  }, [activeTab, loadInbound]);

  const loadCerStatus = useCallback(async () => {
    try {
      const r = await safeFetch(`${API_BASE}/api/v1/cer/status?network=${activeEnv}`);
      if (r.ok) setCerStatus(await r.json());
    } catch (_) {}
    try {
      const rr = await safeFetch(`${API_BASE}/api/v1/cer/reward-status?network=${activeEnv}`);
      if (rr.ok) setCerReward(await rr.json());
    } catch (_) {}
  }, [activeEnv]);

  const discoverCer = useCallback(async () => {
    setCerBusy(true);
    try {
      const r = await safeFetch(`${API_BASE}/api/v1/cer/holdings?network=${activeEnv}`);
      if (r.ok) setCerHoldings((await r.json()).holdings || []);
    } catch (_) {}
    setCerBusy(false);
  }, [activeEnv]);

  // ── CIP-56 onboarding packets (served live by the registry, public read-only) ─
  // Links each greenlit provider to the standard onboarding/relationship packet
  // their registry operator needs. Standard RFP service handoff.
  const [onboarding,    setOnboarding]    = useState(null);
  const [onboardingErr, setOnboardingErr] = useState(null);
  const loadOnboarding = useCallback(async () => {
    setOnboardingErr(null);
    try {
      const r = await safeFetch(`${API_BASE}/registry/onboarding/v1/info?network=${activeEnv}`);
      const j = await r.json();
      if (r.ok) setOnboarding(j);
      else { setOnboarding(null); setOnboardingErr(j.error || `status ${r.status}`); }
    } catch (e) { setOnboarding(null); setOnboardingErr(String(e)); }
  }, [activeEnv]);
  useEffect(() => { if (activeTab === 'onboard') loadOnboarding(); }, [activeTab, loadOnboarding]);

  // Settlement is a privileged WRITE — it is NOT executed from the browser.
  // The studio holds no settlement credential; settlement runs server-to-server
  // (CLI: `node backend/scripts/settle-cer.js <network> settle <amount> <receiver>`
  // with the server-side credential). This builds the exact command for an
  // operator to run instead of POSTing from an anonymous client.
  const settleCer = useCallback(() => {
    if (!cerForm.receiver || !cerForm.amount) { setCerResult({ success: false, error: 'Receiver party and amount are required.' }); return; }
    const cmd = `node backend/scripts/settle-cer.js ${activeEnv} settle ${cerForm.amount} "${cerForm.receiver.trim()}" ${cerForm.cashLeg}`;
    setCerResult({
      success: true,
      deferred: true,
      command: cmd,
      note: 'Settlement is executed server-to-server, not from the browser. Copy this command and run it with the operator credential.',
    });
  }, [activeEnv, cerForm]);

  useEffect(() => { if (activeTab === 'settle') { loadCerStatus(); discoverCer(); } }, [activeTab, loadCerStatus, discoverCer]);

  // Preview flow tab pulls every live signal it annotates: CER status/holdings + RFP preview.
  useEffect(() => {
    if (activeTab === 'flow') { loadCerStatus(); discoverCer(); loadRfpPreview(); }
  }, [activeTab, loadCerStatus, discoverCer, loadRfpPreview]);

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 1200 }}>

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/kro-exchange/settlement-hub">Settlement Hub</Link></li>
          <li className="breadcrumb-item active">Canton Network</li>
        </ol>
      </nav>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-3">
        <div>
          <h1 className="display-6 fw-bold mb-1">
            <i className="bi bi-hdd-network-fill me-2 text-primary"></i>
            Canton Network
          </h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.87rem' }}>
            Lighthouse explorer, validator profile, contract engagement tracking — Canton MainNet.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <small style={{ color: '#64748b', fontSize: '0.68rem' }}>
            {lastRefresh ? `Refreshed ${lastRefresh.toLocaleTimeString()}` : ''}
          </small>
          <button className="btn btn-sm btn-outline-primary" onClick={fetchData} disabled={loading}>
            <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-arrow-clockwise'} me-1`}></i>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Environment selector pills */}
      <div className="d-flex gap-2 mb-4 flex-wrap">
        {ENVS.map(e => (
          <button
            key={e.id}
            onClick={() => setActiveEnv(e.id)}
            className="btn btn-sm"
            style={{
              background: activeEnv === e.id ? `${e.statusColor}18` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${activeEnv === e.id ? e.statusColor : 'rgba(255,255,255,0.1)'}`,
              color: activeEnv === e.id ? e.statusColor : '#64748b',
              borderRadius: 6,
            }}
          >
            <span className="rounded-circle me-2" style={{ width: 7, height: 7, display: 'inline-block', background: e.statusColor, boxShadow: activeEnv === e.id ? `0 0 5px ${e.statusColor}` : 'none' }}></span>
            {e.label}
            <span className="badge ms-2" style={{ background: `${e.statusColor}22`, color: e.statusColor, fontSize: '0.58rem' }}>{e.status}</span>
          </button>
        ))}
      </div>

      {/* Tab bar */}
      <ul className="nav nav-pills mb-4" style={{ gap: '0.25rem' }}>
        {[
          { id: 'overview',    icon: 'bi-grid-fill',          label: 'Environment Overview' },
          { id: 'flow',        icon: 'bi-diagram-3-fill',     label: 'Settlement Flow (Preview)' },
          { id: 'settle',      icon: 'bi-lightning-charge-fill', label: 'CIP-56 Settle' },
          { id: 'contract',    icon: 'bi-file-earmark-code',  label: 'Contract Review' },
          { id: 'repo',        icon: 'bi-arrow-repeat',       label: 'Repo Lifecycle', badge: repoContracts.length },
          { id: 'rfp',         icon: 'bi-send-fill',          label: 'RFQ Transactions', badge: rfqBroadcastCount },
          { id: 'svregistry',  icon: 'bi-bank2',              label: 'SV Registry', badge: svRegistryList.length },
          { id: 'onboard',     icon: 'bi-file-earmark-arrow-down', label: 'Onboarding Packets' },
          { id: 'validators',  icon: 'bi-building-fill',      label: 'Node Operators', badge: greenlitCount },
          { id: 'lighthouse',  icon: 'bi-binoculars-fill',    label: 'Lighthouse Guide' },
          { id: 'validator',   icon: 'bi-person-badge-fill',  label: 'Validator Contact Card' },
          { id: 'tracking',    icon: 'bi-activity',           label: 'Contract Engagement' },
          { id: 'inbound',     icon: 'bi-inbox-fill',         label: 'Inbound Activity', badge: inbound?.counts?.inboundEvents ?? undefined, live: true },
          { id: 'events',      icon: 'bi-list-check',         label: 'Live Event Feed' },
        ].map(t => (
          <li key={t.id} className="nav-item">
            <button
              className={`nav-link ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
              {t.badge ? <span className="badge ms-1" style={{ background: '#10b98130', color: '#10b981', fontSize: '0.6rem' }}>{t.badge}</span> : null}
            </button>
          </li>
        ))}
      </ul>

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: INBOUND ACTIVITY — live counterparty contracts + event transactions
          Polls /api/v1/canton/inbound every 15s. Read straight from KRO's node.
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'inbound' && (() => {
        const acts = inbound || {};
        const events = acts.events || [];
        const contracts = acts.contracts || [];
        const inboundEvents = events.filter(e => e.direction === 'INBOUND');
        const otherEvents = events.filter(e => e.direction !== 'INBOUND');
        const short = (p) => String(p || '').split('::')[0];
        const dirColor = (d) => d === 'INBOUND' ? '#10b981' : d === 'OUTBOUND' ? '#60a5fa' : '#64748b';
        // Package-details link → Lighthouse /packages/<packageId> (origin of the env explorer).
        const lhOrigin = (() => { try { return new URL(env?.lighthouseUrl || 'https://lighthouse.cantonloop.com').origin; } catch { return 'https://lighthouse.cantonloop.com'; } })();
        const pkgUrl = (pid) => pid ? `${lhOrigin}/packages/${pid}` : null;
        const pkgCell = (pid, disp, key) => (
          pid ? (
            <span style={{ whiteSpace: 'nowrap' }}>
              <a href={pkgUrl(pid)} target="_blank" rel="noopener noreferrer" title={`Package ${pid} — open details ↗`}
                 style={{ color: '#60a5fa', fontFamily: 'monospace' }}>{disp}</a>
              <i className="bi bi-clipboard ms-1" role="button" title="copy package id"
                 style={{ cursor: 'pointer', color: copied === key ? '#10b981' : '#475569' }}
                 onClick={() => copy(pid, key)}></i>
            </span>
          ) : <span style={{ color: '#64748b' }}>{disp || '—'}</span>
        );
        const evRow = (e, i) => (
          <tr key={`${e.updateId}-${e.offset}-${i}`}>
            <td style={{ whiteSpace: 'nowrap', fontSize: '0.72rem', color: '#94a3b8' }}>{e.at ? new Date(e.at).toLocaleString() : '—'}</td>
            <td><span className="badge" style={{ background: `${dirColor(e.direction)}22`, color: dirColor(e.direction), fontSize: '0.6rem' }}>{e.direction}</span></td>
            <td style={{ fontSize: '0.74rem' }}>{e.type}{e.choice ? <> · <span style={{ color: '#f1f5f9' }}>{e.choice}</span></> : ''}{e.consuming ? <span title="consuming" style={{ color: '#f59e0b' }}> ⊘</span> : ''}</td>
            <td style={{ fontSize: '0.74rem', color: '#cbd5e1' }}>{e.template}</td>
            <td style={{ fontSize: '0.72rem' }}>{(e.counterparties || []).map(short).join(', ') || (e.actor === 'kro' ? 'KRO' : '—')}</td>
            <td style={{ fontSize: '0.66rem' }}>{pkgCell(e.packageId, e.package, `iep-${i}`)}</td>
            <td style={{ fontSize: '0.66rem' }}>
              {e.updateId ? (
                <code style={{ color: '#818cf8', cursor: 'pointer' }} title="copy update id" onClick={() => copy(e.updateId, `iu-${i}`)}>
                  {copied === `iu-${i}` ? 'copied' : `${e.updateId.slice(0, 14)}…`}
                </code>
              ) : '—'}
            </td>
          </tr>
        );
        return (
          <>
            <div className="card mb-3" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 10 }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <h5 className="fw-bold mb-1">
                      <i className="bi bi-inbox-fill text-success me-2"></i>Inbound Counterparty Activity
                      <span className="badge ms-2" style={{ background: '#10b98122', color: '#10b981', fontSize: '0.6rem', verticalAlign: 'middle' }}>
                        <i className="bi bi-broadcast-pin me-1"></i>LIVE · 15s
                      </span>
                      {inboundMeta && (
                        <span className="badge ms-1" style={{ background: '#8b5cf622', color: '#a78bfa', fontSize: '0.6rem', verticalAlign: 'middle' }} title="Backend auto-reconciles the ledger on every transaction movement; the studio reads the reconciled snapshot.">
                          <i className="bi bi-arrow-repeat me-1"></i>AUTO-RECONCILED{typeof inboundMeta.reconciliations === 'number' ? ` · ${inboundMeta.reconciliations}` : ''}
                        </span>
                      )}
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: '0.82rem' }}>
                      Every counterparty contract and event transaction on KRO's ledger, read directly from your participant node ({(acts.network || activeEnv).toUpperCase()}). An <strong style={{ color: '#10b981' }}>INBOUND</strong> event means a counterparty's node acted (e.g. exercised a choice on our contract). This is the party-only view — richer than the public scan.
                    </p>
                  </div>
                  <div className="text-end" style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                    <button className="btn btn-sm btn-outline-success mb-1" onClick={loadInbound}><i className="bi bi-arrow-clockwise me-1"></i>Refresh</button>
                    <div>{inboundAt ? `updated ${inboundAt.toLocaleTimeString()}` : 'loading…'}</div>
                    {typeof acts.offset !== 'undefined' && <div>ledger offset {acts.offset}</div>}
                    {inboundMeta?.reconciledAt && <div style={{ color: '#a78bfa' }}>reconciled {new Date(inboundMeta.reconciledAt).toLocaleTimeString()}</div>}
                  </div>
                </div>
                {acts.counts && (
                  <div className="d-flex gap-3 mt-2 flex-wrap" style={{ fontSize: '0.72rem' }}>
                    <span style={{ color: '#10b981' }}><strong>{acts.counts.inboundEvents}</strong> inbound events</span>
                    <span style={{ color: '#cbd5e1' }}><strong>{acts.counts.events}</strong> events in window</span>
                    <span style={{ color: '#cbd5e1' }}><strong>{acts.counts.inboundContracts}</strong> counterparty contracts (active)</span>
                    <span style={{ color: '#94a3b8' }}><strong>{acts.counts.counterpartySignatoryContracts}</strong> counterparty-signed</span>
                  </div>
                )}
              </div>
            </div>

            {inboundErr && (
              <div className="alert alert-warning py-2" style={{ fontSize: '0.8rem' }}>
                <i className="bi bi-exclamation-triangle me-1"></i>Live read unavailable: {inboundErr}
              </div>
            )}

            {/* Inbound counterparty event transactions */}
            <div className="card mb-3" style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10 }}>
              <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'transparent', borderBottom: '1px solid #1e293b' }}>
                <span className="fw-semibold" style={{ color: '#f1f5f9', fontSize: '0.9rem' }}>
                  <i className="bi bi-arrow-down-left-circle-fill text-success me-2"></i>Counterparty event transactions
                </span>
                <span className="badge" style={{ background: '#10b98122', color: '#10b981' }}>{inboundEvents.length}</span>
              </div>
              <div className="card-body p-0" style={{ overflowX: 'auto' }}>
                <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.78rem' }}>
                  <thead><tr style={{ color: '#64748b' }}>
                    <th>Time</th><th>Dir</th><th>Type / Choice</th><th>Template</th><th>Counterparty</th><th>Package</th><th>Update ID</th>
                  </tr></thead>
                  <tbody>
                    {inboundEvents.length ? inboundEvents.map(evRow)
                      : <tr><td colSpan={7} className="text-center text-muted py-3" style={{ fontSize: '0.8rem' }}>No inbound counterparty events in the current window.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Active counterparty contracts (ACS) */}
            <div className="card mb-3" style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10 }}>
              <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'transparent', borderBottom: '1px solid #1e293b' }}>
                <span className="fw-semibold" style={{ color: '#f1f5f9', fontSize: '0.9rem' }}>
                  <i className="bi bi-file-earmark-text-fill text-info me-2"></i>Active counterparty contracts
                </span>
                <span className="badge" style={{ background: '#60a5fa22', color: '#60a5fa' }}>{contracts.length}</span>
              </div>
              <div className="card-body p-0" style={{ overflowX: 'auto' }}>
                <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.78rem' }}>
                  <thead><tr style={{ color: '#64748b' }}>
                    <th>Created</th><th>Role</th><th>Template</th><th>Package</th><th>Counterparty</th><th>Detail</th><th>Contract ID</th>
                  </tr></thead>
                  <tbody>
                    {contracts.length ? contracts.map((c, i) => (
                      <tr key={`${c.contractId}-${i}`}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.72rem', color: '#94a3b8' }}>{c.createdAt ? new Date(c.createdAt).toLocaleString() : '—'}</td>
                        <td><span className="badge" style={{ background: c.role === 'counterparty-signatory' ? '#10b98122' : '#60a5fa22', color: c.role === 'counterparty-signatory' ? '#10b981' : '#60a5fa', fontSize: '0.58rem' }}>{c.role}</span></td>
                        <td style={{ fontSize: '0.74rem', color: '#cbd5e1' }}>{c.template}</td>
                        <td style={{ fontSize: '0.66rem' }}>{pkgCell(c.packageId, c.package, `icp-${i}`)}</td>
                        <td style={{ fontSize: '0.72rem' }}>{(c.counterparties || []).map(short).join(', ')}</td>
                        <td style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{c.detail?.amount ? `amt ${c.detail.amount}` : ''}{c.detail?.ref ? ` · ${c.detail.ref}` : ''}{c.detail?.description ? ` · ${c.detail.description.slice(0, 40)}` : ''}</td>
                        <td style={{ fontSize: '0.66rem' }}>
                          <code style={{ color: '#818cf8', cursor: 'pointer' }} onClick={() => copy(c.contractId, `ic-${i}`)}>
                            {copied === `ic-${i}` ? 'copied' : `${String(c.contractId).slice(0, 16)}…`}
                          </code>
                        </td>
                      </tr>
                    )) : <tr><td colSpan={7} className="text-center text-muted py-3" style={{ fontSize: '0.8rem' }}>No active counterparty contracts right now (expired offers are shown as events above).</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Other (outbound/internal) events for context */}
            {otherEvents.length > 0 && (
              <div className="card mb-3" style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10 }}>
                <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'transparent', borderBottom: '1px solid #1e293b' }}>
                  <span className="fw-semibold" style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                    <i className="bi bi-arrow-up-right-circle me-2"></i>Other recent events (outbound / internal) <span className="badge" style={{ background: 'rgba(148,163,184,0.15)', color: '#94a3b8', fontSize: '0.5rem', fontWeight: 600 }}>K-R-O REF ONLY</span>
                  </span>
                  <span className="badge" style={{ background: '#64748b22', color: '#94a3b8' }}>{otherEvents.length}</span>
                </div>
                <div className="card-body p-0" style={{ overflowX: 'auto', maxHeight: 360 }}>
                  <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.76rem' }}>
                    <thead><tr style={{ color: '#64748b' }}>
                      <th>Time</th><th>Dir</th><th>Type / Choice</th><th>Template</th><th>Counterparty</th><th>Package</th><th>Update ID</th>
                    </tr></thead>
                    <tbody>{otherEvents.map(evRow)}</tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: CIP-56 SETTLE — issue / discover / atomic DvP on the KRO CER token
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'settle' && (
        <>
          <div className="card mb-3" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 10 }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                  <h5 className="fw-bold mb-1"><i className="bi bi-lightning-charge-fill text-success me-2"></i>CIP-56 CER Token — Live Settlement</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.82rem' }}>
                    Canton Network Token Standard. The KRO CER marker is a first-class token: discoverable by any wallet via the <code>Holding</code> interface and settleable atomically (DvP) via the <code>Allocation</code> interface. Asset (delivery) leg settles on-ledger on acceptance; cash leg follows on the selected rail.
                  </p>
                </div>
                <span className="badge" style={{ background: '#10b98122', color: '#10b981', fontSize: '0.62rem', alignSelf: 'center' }}>
                  {cerStatus?.configured ? `${(cerStatus.network || activeEnv).toUpperCase()} · CONFIGURED` : 'NOT CONFIGURED'}
                </span>
              </div>
              {cerStatus && (
                <div className="row g-2 mt-2" style={{ fontSize: '0.7rem' }}>
                  <div className="col-md-4"><span className="text-muted">Instrument</span><div className="font-monospace">{cerStatus.instrumentId} · admin = issuer</div></div>
                  <div className="col-md-4"><span className="text-muted">Issuer party</span><div className="font-monospace text-truncate">{cerStatus.issuerParty || '—'}</div></div>
                  <div className="col-md-4"><span className="text-muted">Token package</span><div className="font-monospace text-truncate">{cerStatus.packageId}</div></div>
                </div>
              )}
              {cerReward && (
                <div className="mt-2 p-2 d-flex align-items-start gap-2" style={{ background: cerReward.featured ? 'rgba(16,185,129,0.08)' : 'rgba(148,163,184,0.08)', border: `1px solid ${cerReward.featured ? 'rgba(16,185,129,0.3)' : 'rgba(148,163,184,0.25)'}`, borderRadius: 8, fontSize: '0.68rem' }}>
                  <i className={`bi ${cerReward.featured ? 'bi-award-fill text-success' : 'bi-info-circle'}`} style={cerReward.featured ? undefined : { color: '#94a3b8' }}></i>
                  <div>
                    <span className="fw-bold">{cerReward.featured ? 'Featured App: ACTIVE — traffic is reward-visible' : 'Settlement not gated on rewards — settle now'}</span>
                    <span className="text-muted ms-1">· {cerReward.featured ? cerReward.rewardModel : 'Validator activity rewards = ~20% rebate on traffic (CC burned)'}</span>
                    <div className="text-muted">
                      {cerReward.featured
                        ? cerReward.action
                        : 'Settlement (CIP-56 DvP) is not gated on rewards. KRO\u2019s validator node is live and healthy on MainNet. Validator liveness rewards were set to zero network-wide by Canton Foundation governance (2026-04-30). Validator activity rewards are a subsidy/rebate on traffic purchases: you burn CC to buy traffic and ~20% of the CC burned returns as rewards that round (e.g. 1,000 CC of traffic → ~200 CC). IS has enabled traffic top-ups now that the wallet is funded with CC and a TransferPreapproval is live (1-step receive), so the node burns CC to buy traffic and activity rewards begin accruing as that traffic is burned each round. KRO is NOT a featured app, so featured-app traffic rewards (AppRewardCoupon, CIP-0104) are a separate, future track.'}
                    </div>
                    {!cerReward.featured && (
                      <div className="mt-1" style={{ color: '#10b981' }}>
                        <i className="bi bi-check-circle-fill me-1"></i>Wallet funded with CC · TransferPreapproval live (1-step receive) · IS enabled traffic top-ups → ~20% activity rebate accrues per round as traffic is burned
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="row g-3">
            {/* Settle now form */}
            <div className="col-lg-5">
              <div className="card h-100" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
                <div className="card-body">
                  <h6 className="fw-bold mb-3"><i className="bi bi-send-check-fill me-2 text-primary"></i>Build settlement command <span className="badge" style={{ background: 'rgba(148,163,184,0.15)', color: '#94a3b8', fontSize: '0.5rem', fontWeight: 600, verticalAlign: 'middle' }}>K-R-O REF ONLY</span></h6>
                  <p className="text-muted" style={{ fontSize: '0.72rem' }}>
                    <i className="bi bi-shield-lock me-1 text-warning"></i>
                    Settlement is a privileged write — it runs <strong>server-to-server</strong>, never from the browser.
                    This builds the exact CLI command (issue → allocate → atomic delivery) for an operator to run with the server-side credential.
                  </p>

                  <label className="form-label mb-1" style={{ fontSize: '0.72rem' }}>Receiver PartyID</label>
                  <input
                    className="form-control form-control-sm mb-2 font-monospace"
                    style={{ fontSize: '0.7rem' }}
                    placeholder="Counterparty::1220…"
                    value={cerForm.receiver}
                    onChange={e => setCerForm(f => ({ ...f, receiver: e.target.value }))}
                  />

                  <label className="form-label mb-1" style={{ fontSize: '0.72rem' }}>Amount (CER units)</label>
                  <input
                    type="number"
                    className="form-control form-control-sm mb-2"
                    placeholder="182"
                    value={cerForm.amount}
                    onChange={e => setCerForm(f => ({ ...f, amount: e.target.value }))}
                  />

                  <label className="form-label mb-1" style={{ fontSize: '0.72rem' }}>Cash leg</label>
                  <select
                    className="form-select form-select-sm mb-3"
                    value={cerForm.cashLeg}
                    onChange={e => setCerForm(f => ({ ...f, cashLeg: e.target.value }))}
                  >
                    <option value="CIP56">CIP-56 cash token (atomic DvP on Global Synchronizer)</option>
                  </select>

                  <button className="btn btn-outline-primary btn-sm w-100" onClick={settleCer}>
                    <i className="bi bi-terminal me-1"></i>Build settlement command
                  </button>

                  {cerResult && (
                    <div className="mt-3 p-2" style={{ background: cerResult.success === false ? 'rgba(239,68,68,0.08)' : 'rgba(59,130,246,0.08)', border: `1px solid ${cerResult.success === false ? 'rgba(239,68,68,0.3)' : 'rgba(59,130,246,0.3)'}`, borderRadius: 8, fontSize: '0.68rem' }}>
                      {cerResult.success === false ? (
                        <span className="text-danger"><i className="bi bi-x-octagon me-1"></i>{cerResult.error}</span>
                      ) : cerResult.deferred ? (
                        <>
                          <div className="fw-bold text-info mb-1"><i className="bi bi-shield-lock me-1"></i>Run server-side (not from the browser)</div>
                          <code className="d-block p-2 mb-1" style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 6, color: '#93c5fd', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{cerResult.command}</code>
                          <button className="btn btn-link btn-sm p-0" style={{ fontSize: '0.66rem' }} onClick={() => navigator.clipboard?.writeText(cerResult.command)}>
                            <i className="bi bi-clipboard me-1"></i>Copy command
                          </button>
                          <div className="text-muted mt-1">{cerResult.note}</div>
                        </>
                      ) : (
                        <>
                          <div className="fw-bold text-success mb-1"><i className="bi bi-check-circle-fill me-1"></i>Settled — {cerResult.settlementRef}</div>
                          <div className="font-monospace text-truncate">alloc: {cerResult.allocation?.allocationCid}</div>
                          <div className="font-monospace text-truncate">delivered: {cerResult.execution?.deliveredHoldingCid}</div>
                          <div className="text-muted mt-1">{cerResult.note}</div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Discovered holdings */}
            <div className="col-lg-7">
              <div className="card h-100" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-bold mb-0"><i className="bi bi-wallet2 me-2 text-info"></i>CER Holdings <span className="text-muted" style={{ fontSize: '0.7rem' }}>(CIP-56 Holding)</span></h6>
                    <button className="btn btn-outline-info btn-sm" onClick={discoverCer} disabled={cerBusy}>
                      <i className={`bi ${cerBusy ? 'bi-hourglass-split' : 'bi-arrow-clockwise'} me-1`}></i>Discover
                    </button>
                  </div>
                  {cerHoldings.length === 0 ? (
                    <p className="text-muted small mb-0">No CER holdings to display for {activeEnv.toUpperCase()} yet. Settle to issue and deliver units — they appear here once on-ledger.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-borderless align-middle" style={{ fontSize: '0.7rem' }}>
                        <thead><tr className="text-muted"><th>Owner</th><th className="text-end">Amount</th><th>Instrument</th><th>Lock</th><th>Contract</th></tr></thead>
                        <tbody>
                          {cerHoldings.map((h, i) => (
                            <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                              <td className="font-monospace text-truncate" style={{ maxWidth: 140 }}>{h.owner}</td>
                              <td className="text-end fw-bold">{h.amount}</td>
                              <td className="font-monospace">{h.instrumentId}</td>
                              <td>{h.locked ? <span className="badge bg-warning text-dark">LOCKED</span> : <span className="badge bg-success">FREE</span>}</td>
                              <td className="font-monospace text-truncate" style={{ maxWidth: 120 }}>{h.contractId}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="mt-2 p-2" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, fontSize: '0.66rem' }}>
                    <i className="bi bi-shield-check me-1 text-primary"></i>
                    Compliance is enforced at the participant/registry layer. Title is UCC Art. 12 controllable electronic record, Provenance-attested. Settlement finality = the on-ledger atomic delivery.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: SETTLEMENT FLOW (PREVIEW) — deterministic RFP → DvP → finality,
          annotated with live on-ledger evidence. Read-only: no writes happen here.
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'flow' && (() => {
        const canonical   = buildCanonicalPartyIndex();
        const liveSet     = liveLenderSet(rfpPreview);
        const flowRfp     = RFP_TRANSACTIONS.find(r => r.env === activeEnv) || RFP_TRANSACTIONS[0];
        const lenders     = reconcileLenders(flowRfp?.approvedLenders, canonical, liveSet);
        const driftCount  = lenders.filter(l => l.status === 'drift' || l.status === 'unverified').length;
        const lockedHoldings = (cerHoldings || []).filter(h => h.locked);

        const steps = [
          { n: 1, key: 'initiate', title: 'Initiate — IntradayRepoRFP broadcast',
            done: !!(liveStatus?.openRfp || rfpPreview),
            live: liveStatus?.openRfp ? `Open RFQ · on-ledger IntradayRepoRFP` : (rfpPreview ? 'Preview built (unsubmitted)' : 'awaiting'),
            desc: 'KRO (cash buyer) broadcasts the repo RFQ to the named respondent PartyIds, soliciting indicative intraday quotes. The Daml contract IS the channel — no off-ledger whitelist or conditional approval.' },
          { n: 2, key: 'accept', title: 'Accept — counterparty exercises AcceptRFP',
            done: (repoContracts || []).some(r => /accept|facility/i.test(r.event_type || '')),
            live: (repoContracts || []).some(r => /accept|facility/i.test(r.event_type || '')) ? 'AcceptRFP / Facility on-ledger' : 'awaiting counterparty',
            desc: 'The named creditor PartyId signs and atomically creates the IntradayRepoFacility. First-come-first-served; no optional branches.' },
          { n: 3, key: 'transfer', title: 'Transfer CER — Allocation lock + deliver (CIP-0056)',
            done: !!cerStatus?.configured,
            live: cerStatus?.configured ? `KRO.CER configured · ${cerHoldings.length} holding(s)` : 'CER not configured',
            desc: 'Delivery leg: AllocationFactory_Allocate locks the CER Holding, Allocation_ExecuteTransfer delivers control to the identifiable transferee.' },
          { n: 4, key: 'cash', title: 'Execute cash leg — CIP-56 on-ledger (primary)',
            done: !!liveStatus?.lastSettlement,
            live: liveStatus?.lastSettlement ? `cash recorded · ${liveStatus.lastSettlement.display}` : 'awaiting',
            desc: 'CIP-56 cash token allocated in the same submit for atomic DvP on Global Synchronizer.' },
          { n: 5, key: 'finalize', title: 'Finalize — CloseOut receipt (immutable)',
            done: !!liveStatus?.lastSettlement?.contractId,
            live: liveStatus?.lastSettlement?.contractId ? `CloseOut ${String(liveStatus.lastSettlement.contractId).slice(0, 18)}…` : 'awaiting',
            desc: 'IntradayCloseOutReceipt / CloseOutReceipt is the deterministic settlement-finality record.' },
        ];

        return (
          <div className="flow-preview-readable">
            <style>{`.flow-preview-readable .text-muted { color: #cbd5e1 !important; }`}</style>
            {/* Preview banner */}
            <div className="card mb-3" style={{ background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 10 }}>
              <div className="card-body d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                  <h5 className="fw-bold mb-1"><i className="bi bi-diagram-3-fill text-primary me-2"></i>Settlement Flow — Preview</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.82rem' }}>
                    Deterministic RFQ → DvP → finality for <b>{activeEnv.toUpperCase()}</b>, annotated with live on-ledger evidence. <b>This tab performs no on-ledger writes</b> — it is a pre-deploy review of the exact flow counterparties accept.
                  </p>
                </div>
                <span className="badge" style={{ background: '#6366f122', color: '#a5b4fc', fontSize: '0.62rem', alignSelf: 'center' }}>PREVIEW · READ-ONLY</span>
              </div>
            </div>

            {/* 5-step atomic DvP */}
            <div className="card mb-3" style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
              <div className="card-header border-0" style={{ background: 'transparent' }}>
                <h6 className="fw-bold mb-0"><i className="bi bi-list-ol me-2 text-info"></i>Atomic DvP — 5-step deterministic sequence</h6>
              </div>
              <div className="card-body">
                {steps.map(s => (
                  <div key={s.key} className="d-flex gap-3 mb-3">
                    <div className="text-center" style={{ minWidth: 28 }}>
                      <span className="rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: 26, height: 26, background: s.done ? '#10b98122' : '#f59e0b22', color: s.done ? '#10b981' : '#f59e0b', fontSize: '0.72rem', fontWeight: 700 }}>{s.n}</span>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between flex-wrap gap-1">
                        <span className="fw-semibold" style={{ fontSize: '0.82rem' }}>{s.title}</span>
                        <span className="badge" style={{ background: s.done ? '#10b98122' : '#f59e0b22', color: s.done ? '#10b981' : '#f59e0b', fontSize: '0.58rem' }}>
                          <i className={`bi ${s.done ? 'bi-check-circle-fill' : 'bi-hourglass-split'} me-1`}></i>{s.live}
                        </span>
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.72rem' }}>{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Counterparty determinism */}
            <div className="card mb-3" style={{ background: '#0f172a', color: '#e2e8f0', border: `1px solid ${driftCount ? 'rgba(245,158,11,0.4)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 10 }}>
              <div className="card-header border-0 d-flex justify-content-between align-items-center" style={{ background: 'transparent' }}>
                <h6 className="fw-bold mb-0"><i className="bi bi-people-fill me-2 text-warning"></i>Counterparty PartyIds — determinism check</h6>
                <span className="badge" style={{ background: driftCount ? '#f59e0b22' : '#10b98122', color: driftCount ? '#f59e0b' : '#10b981', fontSize: '0.6rem' }}>
                  {driftCount ? `${driftCount} need reconciliation` : 'all reconciled'}
                </span>
              </div>
              <div className="card-body">
                <p className="text-muted" style={{ fontSize: '0.72rem' }}>
                  Canton binds settlement to exact PartyIds. The <b>live backend RFQ preview is the single source of truth</b>; the list below is the studio's list checked against the on-file canonical registry{liveSet ? ' and the live preview' : ''}. Flagged rows must be reconciled to the live PartyId before settling.
                </p>
                {!liveSet && (
                  <div className="p-2 mb-2" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, fontSize: '0.68rem' }}>
                    <i className="bi bi-info-circle me-1 text-warning"></i> Live RFQ preview not loaded for {activeEnv.toUpperCase()} — showing canonical-registry check only. Reconciliation against live resumes when <code>/canton/repo-rfp-preview</code> returns lenders.
                  </div>
                )}
                <div className="table-responsive">
                  <table className="table table-sm table-borderless align-middle" style={{ fontSize: '0.7rem', color: '#e2e8f0' }}>
                    <thead><tr className="text-muted"><th>Lender</th><th>PartyId</th><th>Status</th></tr></thead>
                    <tbody>
                      {lenders.map((l, i) => {
                        const c = (l.status === 'live' || l.status === 'canonical') ? '#10b981' : l.status === 'drift' ? '#f59e0b' : '#ef4444';
                        return (
                          <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                            <td>{l.name}<div className="text-muted" style={{ fontSize: '0.62rem' }}>{l.entityType}</div></td>
                            <td className="font-monospace text-truncate" style={{ maxWidth: 240 }} title={l.party}>{l.party}</td>
                            <td><span className="badge" style={{ background: `${c}22`, color: c, fontSize: '0.58rem' }} title={l.detail}>{l.status.toUpperCase()}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* CIP-0056 Holding.lock (collateral control) vs approved-validator rewards (reward layer) */}
            <div className="row g-3 mb-3">
              <div className="col-lg-6">
                <div className="card h-100" style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
                  <div className="card-body">
                    <h6 className="fw-bold mb-1"><i className="bi bi-lock-fill me-2 text-info"></i>CIP-0056 Holding.lock — collateral control</h6>
                    <p className="text-muted" style={{ fontSize: '0.72rem' }}>
                      Article-12 exclusive control is represented on-ledger by the standard <code>Optional Lock</code> on the CER Holding (holders · expiry · context). A locked holding is the encumbrance; an identifiable transferee + no competing claim are enforced by the consuming Allocation.
                    </p>
                    <div className="d-flex gap-3 my-2" style={{ fontSize: '0.72rem' }}>
                      <div><div className="text-muted" style={{ fontSize: '0.6rem' }}>Holdings</div><b>{cerHoldings.length}</b></div>
                      <div><div className="text-muted" style={{ fontSize: '0.6rem' }}>Locked</div><b style={{ color: '#f59e0b' }}>{lockedHoldings.length}</b></div>
                      <div><div className="text-muted" style={{ fontSize: '0.6rem' }}>Free</div><b style={{ color: '#10b981' }}>{cerHoldings.length - lockedHoldings.length}</b></div>
                    </div>
                    {lockedHoldings.length === 0 && (
                      <div className="text-muted" style={{ fontSize: '0.66rem' }}>
                        No locked holdings yet — the <code>lock</code> field is set when an Allocation locks a holding for a settlement leg (it clears on execute/expiry per the standard).
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="card h-100" style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <h6 className="fw-bold mb-1"><i className="bi bi-coin me-2 text-muted"></i>Validator Activity Rewards — Traffic Rebate (~20%) <span className="badge" style={{ background: 'rgba(148,163,184,0.15)', color: '#94a3b8', fontSize: '0.5rem', fontWeight: 600, verticalAlign: 'middle' }}>K-R-O REF ONLY</span></h6>
                      <span className="badge" style={{ background: 'rgba(16,185,129,0.18)', color: '#10b981', fontSize: '0.55rem' }}>WALLET FUNDED · TRAFFIC TOP-UPS ON · REWARDS ACCRUE AS TRAFFIC BURNS · DOES NOT GATE SETTLEMENT</span>
                    </div>
                    <p className="text-muted" style={{ fontSize: '0.72rem' }}>
                      KRO\u2019s <b>validator node is live and healthy</b> on MainNet (Party ID confirmed; verifiable on cantonscan.com). <b>Validator liveness rewards were set to zero network-wide</b> by Canton Foundation governance (2026-04-30). <b>Validator activity rewards</b> are a <b>subsidy/rebate on traffic purchases</b>: you burn CC to buy traffic, and <b>~20% of the CC burned</b> returns as rewards that round. The wallet is now <b>funded with Canton Coin</b> and a <b>TransferPreapproval is live</b> (1-step receive). IS has <b>enabled traffic top-ups</b>, so the node burns CC to buy traffic and activity rewards begin accruing as that traffic is burned each round. This is the <b>reward</b> layer only — it <b>does not gate settlement</b>. KRO is <b>not</b> a featured app (<code>AppRewardCoupon</code>, CIP-0104 is a separate, future track).
                    </p>
                    <div className="d-flex justify-content-between align-items-center p-2 mb-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, fontSize: '0.72rem' }}>
                      <span className="text-muted">Expected reward (per round)</span>
                      <b>~20% of CC burned on traffic · e.g. 1,000 CC → ~200 CC</b>
                    </div>
                    <div className="d-flex gap-3 mb-2" style={{ fontSize: '0.66rem' }}>
                      <span className="text-muted">Liveness rewards are zero network-wide (Foundation governance, 2026-04-30). Activity rewards are proportional (~20%) to the CC burned on traffic each round — there is no per-PartyId CC lock.</span>
                    </div>
                    <div className="p-2" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, fontSize: '0.64rem' }}>
                      <i className="bi bi-check-circle-fill me-1" style={{ color: '#10b981' }}></i>
                      Live: <b>wallet funded with CC</b> · <b>TransferPreapproval active</b> · IS enabled traffic top-ups → ~20% activity rebate accrues per round as traffic is burned. The node is live and connected; settlement is never blocked by this.
                    </div>
                    <a href={CIP0116.reference} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.64rem', color: '#60a5fa' }}>Featured-app track (separate · future) — CIP-0116 ↗</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: ENVIRONMENT OVERVIEW
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <>
          {/* Env detail card */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0', border: `1px solid ${env.statusColor}33` }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between" style={{ background: `linear-gradient(135deg, ${env.statusColor}10, #0f172a)` }}>
              <div className="d-flex align-items-center gap-2">
                <span className="rounded-circle" style={{ width: 10, height: 10, background: env.statusColor, boxShadow: `0 0 6px ${env.statusColor}` }}></span>
                <h5 className="mb-0 fw-bold">{env.label} — Active Configuration</h5>
              </div>
              <div className="d-flex align-items-center gap-2">
                {liveStatus?.verifiedAt ? (
                  <span style={{ fontSize: '0.6rem', color: '#64748b' }}>
                    <i className="bi bi-shield-check me-1" style={{ color: '#10b981' }}></i>
                    on-ledger verified {new Date(liveStatus.verifiedAt).toLocaleTimeString()}
                  </span>
                ) : liveStatusErr ? (
                  <span style={{ fontSize: '0.6rem', color: '#f59e0b' }} title={liveStatusErr}>
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    live status unavailable
                  </span>
                ) : null}
                <span className="badge" style={{ background: `${env.statusColor}22`, color: env.statusColor }}>{env.status}</span>
              </div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {[
                  { label: 'Party ID',           value: env.partyId,        key: 'party', mono: true },
                  { label: 'Validator Host',     value: env.validatorHost,  key: 'host',  mono: true },
                  { label: 'Validator IP',       value: env.validatorIP,    key: 'ip',    mono: true },
                  { label: 'DAR Version',        value: env.darVersion,     key: 'dar',   mono: false },
                  { label: 'DAR Uploaded',       value: env.darUploadedAt,  key: 'upd',   mono: false },
                  // ── Live on-ledger status (from /api/v1/canton/network-status) ──
                  ...(liveStatus?.activePackage ? [{ label: 'Active Package', value: liveStatus.activePackage, key: 'pkg', mono: false }] : []),
                  ...(liveStatus?.firstTxAt ? [{ label: 'First Transaction', value: new Date(liveStatus.firstTxAt).toLocaleString(), key: 'ftx', mono: false }] : []),
                  ...(liveStatus?.lastSettlement ? [{ label: 'Latest Settlement', value: `${liveStatus.lastSettlement.display} · ${new Date(liveStatus.lastSettlement.at).toLocaleString()}`, key: 'lsx', mono: false, highlight: true }] : []),
                  ...(liveStatus?.lastSettlement?.contractId ? [{ label: 'Latest Close-Out CID', value: liveStatus.lastSettlement.contractId, key: 'lcid', mono: true }] : []),
                  ...(typeof liveStatus?.activeContracts === 'number' ? [{ label: 'Active Contracts (on-ledger)', value: `${liveStatus.activeContracts} KRO contracts`, key: 'acc', mono: false, highlight: true }] : []),
                  ...(liveStatus?.cerToken?.live ? [{ label: 'CIP-56 Token', value: `KRO.CER live${liveStatus.cerToken.registry ? ' · registry' : ''}${liveStatus.cerToken.holdings ? ` · ${liveStatus.cerToken.holdings} holding(s)` : ''}`, key: 'cer', mono: false, highlight: true }] : []),
                  ...(liveStatus?.openRfp ? [{ label: 'Open Intraday Repo RFQ', value: `${liveStatus.openRfp.templateId ? 'IntradayRepoRFP' : 'RFQ'}${liveStatus.openRfp.expiryTime ? ` · open until ${new Date(liveStatus.openRfp.expiryTime).toLocaleString()}` : ''}`, key: 'rfp', mono: false, highlight: true }] : []),
                  { label: 'Lighthouse',         value: env.lighthouseUrl,  key: 'lh',    mono: false, link: env.lighthouseUrl },
                ].map((row, i) => (
                  <div key={i} className="col-12 col-md-6">
                    <div className="p-2 rounded" style={{ background: row.highlight ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${row.highlight ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.06)'}` }}>
                      <div style={{ fontSize: '0.65rem', color: row.highlight ? '#6ee7b7' : '#475569', marginBottom: '0.2rem' }}>{row.label}</div>
                      <div className="d-flex align-items-center gap-2">
                        {row.link
                          ? <a href={row.link} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontSize: '0.78rem' }}>{row.value}</a>
                          : <span style={{ fontFamily: row.mono ? '"Fira Code",monospace' : 'inherit', fontSize: row.mono ? '0.68rem' : '0.82rem', color: '#e2e8f0', wordBreak: 'break-all' }}>{row.value}</span>
                        }
                        {row.mono && (
                          <button
                            onClick={() => copy(row.value, row.key)}
                            style={{ background: 'none', border: 'none', color: copied === row.key ? '#10b981' : '#475569', cursor: 'pointer', fontSize: '0.75rem', padding: '0 4px', flexShrink: 0 }}
                          >
                            <i className={`bi ${copied === row.key ? 'bi-check2' : 'bi-clipboard'}`}></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Ledger API URL */}
              <div className="mt-3 p-3 rounded" style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)' }}>
                <div style={{ fontSize: '0.65rem', color: '#3b82f6', marginBottom: '0.3rem' }}>
                  <i className="bi bi-link-45deg me-1"></i>JSON Ledger API Base URL
                </div>
                <div className="d-flex align-items-center gap-2">
                  <code style={{ fontSize: '0.72rem', color: '#93c5fd', wordBreak: 'break-all' }}>
                    https://{env.validatorHost}/json-ledger-api
                  </code>
                  <button
                    onClick={() => copy(`https://${env.validatorHost}/json-ledger-api`, 'api')}
                    style={{ background: 'none', border: 'none', color: copied === 'api' ? '#10b981' : '#475569', cursor: 'pointer', fontSize: '0.75rem' }}
                  >
                    <i className={`bi ${copied === 'api' ? 'bi-check2' : 'bi-clipboard'}`}></i>
                  </button>
                </div>
                <div className="mt-2" style={{ fontSize: '0.67rem', color: '#64748b' }}>
                  Auth: Bearer token from Auth0 M2M (scope: <code style={{ color: '#94a3b8' }}>daml_ledger_api</code>). IS manages the M2M credentials per environment.
                </div>
              </div>
            </div>
          </div>

          {/* All environments at a glance */}
          <div className="row g-3 mb-4">
            {ENVS.map(e => (
              <div key={e.id} className="col-12 col-md-4">
                <div className="card border-0 shadow-sm h-100" style={{ background: '#0f172a', border: `1px solid ${e.statusColor}22` }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="rounded-circle" style={{ width: 8, height: 8, background: e.statusColor, boxShadow: `0 0 4px ${e.statusColor}` }}></span>
                      <strong style={{ color: '#e2e8f0', fontSize: '0.88rem' }}>{e.label}</strong>
                      <span className="badge ms-auto" style={{ background: `${e.statusColor}20`, color: e.statusColor, fontSize: '0.6rem' }}>{e.status}</span>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.7 }}>
                      <div><span style={{ color: '#475569' }}>Validator:</span> <code style={{ color: '#94a3b8', fontSize: '0.62rem' }}>{e.validatorHost.split('.')[0]}</code></div>
                      <div><span style={{ color: '#475569' }}>IP:</span> <code style={{ color: '#94a3b8', fontSize: '0.62rem' }}>{e.validatorIP}</code></div>
                      <div><span style={{ color: '#475569' }}>DAR:</span> <code style={{ color: '#94a3b8', fontSize: '0.62rem' }}>{e.darVersion}</code></div>
                      <div><span style={{ color: '#475569' }}>Promoted:</span> {e.promoted}</div>
                    </div>
                    <div className="mt-2 d-flex gap-2 flex-wrap">
                      <a href={e.lighthouseUrl} target="_blank" rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ background: `${e.statusColor}15`, border: `1px solid ${e.statusColor}33`, color: e.statusColor, fontSize: '0.62rem', padding: '0.18rem 0.6rem' }}>
                        <i className="bi bi-binoculars me-1"></i>Lighthouse
                      </a>
                      <a href={`https://${e.validatorHost}/wallet`} target="_blank" rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#64748b', fontSize: '0.62rem', padding: '0.18rem 0.6rem' }}>
                        <i className="bi bi-box-arrow-up-right me-1"></i>Wallet UI
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Live summary from API */}
          {Object.keys(summaryData).length > 0 && (
            <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0' }}>
              <div className="card-header border-0" style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)', fontSize: '0.82rem' }}>
                <i className="bi bi-database-fill-check text-success me-2"></i>
                <strong>Live Canton DB Summary</strong>
                <span className="badge bg-success ms-2" style={{ fontSize: '0.58rem' }}>From Azure SQL</span>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  {[
                    ['Total Events',    summaryData.totalEvents],
                    ['Active Contracts',summaryData.activeContracts],
                    ['MainNet Records', summaryData.mainnetRecords],
                    ['Total Notional',  `$${((summaryData.totalNotionalUsd||0)/1e6).toFixed(2)}M`],
                    ['Network',         summaryData.network],
                    ['Last Event',      summaryData.lastEventAt ? new Date(summaryData.lastEventAt).toLocaleString() : '—'],
                  ].map(([l, v], i) => (
                    <div key={i} className="col-6 col-md-3">
                      <div className="p-2 rounded text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ fontSize: '0.62rem', color: '#475569' }}>{l}</div>
                        <div className="fw-bold" style={{ fontSize: '0.88rem' }}>{v ?? '—'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: CONTRACT REVIEW (CIP-0056 offer/take instrument · v0.1.0 — review copy)
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'contract' && (
        <div className="flow-preview-readable">
          <style>{`.flow-preview-readable .text-muted { color: #cbd5e1 !important; }`}</style>
          {/* Header / review banner */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid rgba(16,185,129,0.3)' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.10), #0f172a)' }}>
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-file-earmark-code fs-5" style={{ color: '#34d399' }}></i>
                <div>
                  <h5 className="mb-0 fw-bold text-white">Collateral Settlement Contract — Source Review</h5>
                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                    Full lifecycle · <code style={{ color: '#fbbf24' }}>CIP-0056 offer/take instrument · v0.1.0</code> · PCP collateral settlement + intraday repo · live on Canton MainNet
                  </div>
                </div>
              </div>
              <span className="badge" style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', fontSize: '0.62rem' }}>
                LIVE ON MAINNET · EXACT CONTRACT SOURCE
              </span>
            </div>
          </div>

          {/* Instrument legal character — UCC Article 12 CER */}
          <div className="p-3 rounded mb-4" style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.28)', fontSize: '0.74rem', color: '#a7f3d0', lineHeight: 1.6 }}>
            <i className="bi bi-bank2 me-2" style={{ color: '#34d399' }}></i>
            <b style={{ color: '#e2e8f0' }}>KRO.CER is a UCC Article 12 CER</b> (cash-equivalent of RWAs), perfected and transferred by control — <b style={{ color: '#e2e8f0' }}>not a security</b>; no CSD/securities-intermediary involved. Recognized instrument identity = <code style={{ color: '#6ee7b7' }}>DTIF ZX18049M0</code>.
          </div>

          {/* K-R-O internal archive toggle — reveals the retained record (not counterparty-facing) */}
          <div className="d-flex justify-content-end mb-3">
            <button
              onClick={() => setShowKroArchive(v => !v)}
              className="btn btn-sm"
              style={{ background: showKroArchive ? 'rgba(148,163,184,0.18)' : 'transparent', border: '1px solid rgba(148,163,184,0.3)', color: '#94a3b8', fontSize: '0.62rem', padding: '0.2rem 0.7rem' }}
              title="K-R-O internal record — audience map, DTIF card, PCP flow, and Daml source blocks. Hidden from the counterparty view."
            >
              <i className={`bi ${showKroArchive ? 'bi-eye-slash' : 'bi-archive'} me-1`}></i>
              {showKroArchive ? 'Hide K-R-O internal archive' : 'K-R-O internal archive'}
            </button>
          </div>

          {/* Who sees what — per-section audience map · K-R-O ARCHIVE */}
          {showKroArchive && (
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(96,165,250,0.28)' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'linear-gradient(135deg, rgba(96,165,250,0.10), #0f172a)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="bi bi-diagram-2-fill me-2" style={{ color: '#60a5fa' }}></i>Who Sees What — Section Audience Map
              </span>
              <span className="badge" style={{ background: 'rgba(96,165,250,0.15)', color: '#93c5fd', fontSize: '0.6rem' }}>Relevance per party · K-R-O reference flagged</span>
            </div>
            <div className="card-body">
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                Each section below is tagged by <b style={{ color: '#cbd5e1' }}>relevance</b> and by <b style={{ color: '#cbd5e1' }}>who
                it is for</b>. Sections marked <b style={{ color: '#94a3b8' }}>K-R-O reference only</b> are internal (operations /
                go-to-market) and are <b>not</b> part of what counterparties engage with or are bound by.
              </div>
              {/* Kind legend */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                {Object.entries(SECTION_KIND).map(([k, v]) => (
                  <span key={k} className="badge" style={{ background: `${v.c}1c`, color: v.c, fontSize: '0.6rem', border: `1px solid ${v.c}44` }}>{v.label}</span>
                ))}
              </div>
              <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>
                  <thead>
                    <tr style={{ color: '#64748b', borderColor: 'rgba(255,255,255,0.08)' }}>
                      <th style={{ width: '44%' }}>Section</th><th style={{ width: '20%' }}>Relevance</th><th>Relevant to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SECTION_AUDIENCE.map((row, i) => {
                      const kind = SECTION_KIND[row.kind];
                      return (
                        <tr key={i} style={{ borderColor: 'rgba(255,255,255,0.05)', background: row.kind === 'kro-ref' ? 'rgba(148,163,184,0.05)' : 'transparent' }}>
                          <td style={{ color: '#e2e8f0' }}>{row.s}</td>
                          <td><span className="badge" style={{ background: `${kind.c}1c`, color: kind.c, fontSize: '0.56rem', border: `1px solid ${kind.c}44` }}>{kind.label}</span></td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {row.who === 'ALL'
                                ? <span className="badge" style={{ background: 'rgba(16,185,129,0.12)', color: '#6ee7b7', fontSize: '0.56rem' }}>All parties</span>
                                : row.who.map((p) => {
                                    const t = PARTY_TAGS[p];
                                    return <span key={p} className="badge" style={{ background: `${t.c}1c`, color: t.c, fontSize: '0.56rem', border: `1px solid ${t.c}44` }}>{t.label}</span>;
                                  })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: '0.64rem', color: '#64748b', marginTop: '0.6rem', lineHeight: 1.55 }}>
                <b style={{ color: '#94a3b8' }}>Party roles:</b> K-R-O = arranging hub / issuer / settlement coordinator ·
                Circle = USDC cash leg (confirms) · Cumberland = liquidity leg (confirms) · T-RIZE = collateral
                (asset) leg — receiver (observer, confirms nothing) · Custodian = BitGo (on-ledger informee —
                custody reference only · no value moved) · Clearing agent = Cumberland DRW.
              </div>
            </div>
          </div>
          )}

          {/* Actual on-chain deployment data */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(59,130,246,0.2)' }}>
            <div className="card-header border-0" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.08), #0f172a)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="bi bi-fingerprint me-2" style={{ color: '#60a5fa' }}></i>Actual on-chain deployment data (production)
              </span>
            </div>
            <div className="card-body">
              <div className="row g-2">
                {CONTRACT_FACTS.map(([k, v], i) => (
                  <div key={i} className="col-12 col-md-6">
                    <div className="p-2 rounded h-100" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ fontSize: '0.6rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</div>
                      <div style={{ fontSize: '0.72rem', color: '#e2e8f0', fontFamily: '"Fira Code",monospace', wordBreak: 'break-all' }}>{v}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CIP-56 token — DTIF instrument reference · K-R-O ARCHIVE */}
          {showKroArchive && (
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(16,185,129,0.25)' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.08), #0f172a)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="bi bi-coin me-2 text-success"></i>CIP-56 Token — Instrument Reference (DTIF)
              </span>
              <span className="badge" style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', fontSize: '0.6rem' }}>CIP-0056 · ISO 24165</span>
            </div>
            <div className="card-body">
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.6, marginBottom: '0.85rem' }}>
                The collateral and cash legs settle as <b style={{ color: '#cbd5e1' }}>CIP-56 tokens</b> (Canton token
                standard). The <b style={{ color: '#cbd5e1' }}>DTIF (ISO 24165 Digital Token Identifier)</b> is not an
                off-chain lookup — it is the <b style={{ color: '#a7f3d0' }}>legal instrument classification</b> that makes
                KRO.CER a recognized <b style={{ color: '#cbd5e1' }}>financial instrument</b>, and it is the
                <b style={{ color: '#a7f3d0' }}> settlement identifier institutions and banks key on</b> to book, clear and
                reconcile the asset. The same identifier is also surfaced in the registry instrument metadata
                (<code style={{ color: '#a7f3d0' }}>identifier.dtif</code>) so an on-ledger holding resolves to its
                globally-registered instrument identity.
              </div>
              <div className="row g-2">
                {[
                  ['DTIF (ISO 24165)', 'ZX18049M0'],
                  ['Role', 'Legal instrument classification · settlement identifier for banks (not an off-chain reference)'],
                  ['Token standard', 'CIP-0056 (Canton Network Token Standard)'],
                  ['Instrument', 'KRO.CER — kro.as.accountability.v2 (UCC Art. 12 CER)'],
                  ['Package', 'Canton Network Token Standard (CIP-0056) · 0.4.0'],
                  ['Also surfaced in registry metadata', 'identifier.dtif · identifiers[] (scheme: ISO-24165)'],
                  ['Registry endpoint', 'https://api.kromarketplace.com/registry/metadata/v1/info'],
                ].map(([k, v], i) => (
                  <div key={i} className="col-12 col-md-6">
                    <div className="p-2 rounded h-100" style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.18)' }}>
                      <div style={{ fontSize: '0.6rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</div>
                      <div style={{ fontSize: '0.72rem', color: '#e2e8f0', fontFamily: '"Fira Code",monospace', wordBreak: 'break-all' }}>{v}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          )}

          {/* Live MainNet joint allocation — acting parties + settlement params (pulled from ledger) */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(52,211,153,0.28)' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'linear-gradient(135deg, rgba(52,211,153,0.10), #0f172a)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="bi bi-people-fill me-2" style={{ color: '#34d399' }}></i>Multi-Party Intraday Settlement — Atomic DvP (Acting Parties &amp; Routing)
              </span>
              <span className="badge" style={{ background: 'rgba(52,211,153,0.15)', color: '#6ee7b7', fontSize: '0.6rem' }}>Cash leg: Circle or Cumberland (1-of-2) — T-RIZE · observer — BitGo · custody — GSF · governance — threshold 1</span>
            </div>
            <div className="card-body">
              {/* Party summary */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                {[
                  { k: 'Eligible cash funders', v: '2', c: '#34d399' },
                  { k: 'Confirming threshold', v: '1', c: '#60a5fa' },
                  { k: 'Informed parties', v: '3', c: '#a78bfa' },
                  { k: 'Arranging hub / issuer', v: '1', c: '#64748b' },
                ].map((s) => (
                  <div key={s.k} className="px-3 py-2 rounded" style={{ background: `${s.c}14`, border: `1px solid ${s.c}33` }}>
                    <div style={{ fontSize: '0.58rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.k}</div>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: s.c }}>{s.v}</div>
                  </div>
                ))}
              </div>

              {/* Party access model */}
              {(() => {
                const ACCESS = {
                  hub:     { label: 'HUB / SIGNS', bg: 'rgba(96,165,250,0.15)', fg: '#60a5fa' },
                  confirm: { label: 'CONFIRMS LEG', bg: 'rgba(52,211,153,0.15)', fg: '#34d399' },
                  inform:  { label: 'INFORMED', bg: 'rgba(167,139,250,0.15)', fg: '#a78bfa' },
                };
                return (
                  <>
                    <div style={{ fontSize: '0.66rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' }}>Party access model</div>
                    <div className="table-responsive mb-3">
                      <table className="table table-sm mb-0" style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>
                        <thead>
                          <tr style={{ color: '#64748b', borderColor: 'rgba(255,255,255,0.08)' }}>
                            <th>Party</th><th>Role / leg</th><th>Access</th><th>On-ledger</th><th>Visibility</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ACTING_PARTIES.map((p, i) => {
                            const a = ACCESS[p.access] || ACCESS.confirm;
                            return (
                              <tr key={i} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                                <td style={{ color: '#e2e8f0', fontFamily: '"Fira Code",monospace', fontSize: '0.68rem' }}>{p.party}</td>
                                <td style={{ color: '#94a3b8' }}>{p.role}</td>
                                <td>
                                  <span className="badge" style={{ background: a.bg, color: a.fg, fontSize: '0.58rem' }}>{a.label}</span>
                                </td>
                                <td>
                                  <span className="badge" style={{ background: p.access === 'inform' ? 'transparent' : 'rgba(56,189,248,0.15)', color: p.access === 'inform' ? '#64748b' : '#38bdf8', fontSize: '0.58rem' }}>
                                    {p.access === 'hub' ? 'signatory' : p.access === 'inform' ? 'observer' : 'controller (own leg)'}
                                  </span>
                                </td>
                                <td style={{ color: '#94a3b8' }}>{p.sees}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
              {/* Settlement status view */}
              <div style={{ fontSize: '0.66rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' }}>Intraday settlement status (on-ledger)</div>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {SETTLEMENT_STATUS_VIEW.map((o) => (
                  <div key={o.s} className="px-2 py-1 rounded" style={{ background: `${o.c}12`, border: `1px solid ${o.c}33`, minWidth: 150 }}>
                    <div style={{ fontSize: '0.62rem', fontWeight: 700, color: o.c, fontFamily: '"Fira Code",monospace' }}>{o.s}</div>
                    <div style={{ fontSize: '0.6rem', color: '#94a3b8', lineHeight: 1.4 }}>{o.d}</div>
                  </div>
                ))}
              </div>

              {/* Settlement parameters */}
              <div style={{ fontSize: '0.66rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' }}>Settlement parameters (from ledger)</div>
              <div className="row g-2 mb-3">
                {JOINT_SETTLEMENT_PARAMS.map(([k, v], i) => (
                  <div key={i} className="col-12 col-md-6">
                    <div className="p-2 rounded h-100" style={{ background: 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.16)' }}>
                      <div style={{ fontSize: '0.6rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</div>
                      <div style={{ fontSize: '0.72rem', color: '#e2e8f0', fontFamily: '"Fira Code",monospace', wordBreak: 'break-word' }}>{v}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Daml source blocks — K-R-O ARCHIVE (hidden from the counterparty view) */}
              {showKroArchive && (
              <>
              <div className="d-flex align-items-center justify-content-between mb-1">
                <div style={{ fontSize: '0.66rem', color: '#34d399', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Daml source — CIP-0056 AllocationRequest (standard multi-party intraday DvP)</div>
                <button
                  onClick={() => copy(INTRADAY_MULTIPARTY_SRC, 'mp-daml-src')}
                  className="btn btn-sm"
                  style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', color: copied === 'mp-daml-src' ? '#10b981' : '#34d399', fontSize: '0.62rem', padding: '0.18rem 0.6rem' }}
                >
                  <i className={`bi ${copied === 'mp-daml-src' ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>
                  {copied === 'mp-daml-src' ? 'Copied' : 'Copy source'}
                </button>
              </div>
              <pre style={{ margin: 0, padding: '1rem', background: '#0b1220', color: '#cbd5e1', fontSize: '0.68rem', lineHeight: 1.55, fontFamily: '"Fira Code",monospace', overflowX: 'auto', maxHeight: 460, borderRadius: 8 }}>
                <code>{INTRADAY_MULTIPARTY_SRC}</code>
              </pre>

              {/* Daml source — current token-standard joint allocation (0.4.0) */}
              <div className="d-flex align-items-center justify-content-between mb-1 mt-3">
                <div style={{ fontSize: '0.66rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Token-standard source — CIP-0056 AllocationRequest (Canton Network Token Standard · v0.4.0)</div>
                <button
                  onClick={() => copy(JOINT_ALLOC_SRC, 'joint-daml-src')}
                  className="btn btn-sm"
                  style={{ background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.3)', color: copied === 'joint-daml-src' ? '#10b981' : '#38bdf8', fontSize: '0.62rem', padding: '0.18rem 0.6rem' }}
                >
                  <i className={`bi ${copied === 'joint-daml-src' ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>
                  {copied === 'joint-daml-src' ? 'Copied' : 'Copy source'}
                </button>
              </div>
              <pre style={{ margin: 0, padding: '1rem', background: '#0b1220', color: '#94a3b8', fontSize: '0.68rem', lineHeight: 1.55, fontFamily: '"Fira Code",monospace', overflowX: 'auto', maxHeight: 360, borderRadius: 8, opacity: 0.9 }}>
                <code>{JOINT_ALLOC_SRC}</code>
              </pre>
              </>
              )}
            </div>
          </div>

          {/* PCP collateral settlement flow · K-R-O ARCHIVE */}
          {showKroArchive && (
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(167,139,250,0.2)' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between" style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.08), #0f172a)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="bi bi-diagram-3 me-2" style={{ color: '#a78bfa' }}></i>PCP Collateral Settlement Flow — Main.daml <span className="badge" style={{ background: 'rgba(148,163,184,0.15)', color: '#94a3b8', fontSize: '0.5rem', fontWeight: 600 }}>K-R-O REF ONLY</span>
              </span>
              <span className="badge" style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontSize: '0.6rem' }}>T+0 atomic DvP</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>
                  <thead>
                    <tr style={{ color: '#64748b' }}>
                      <th>Template</th><th>Signatories</th><th>Choices (commands)</th><th>Actual data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PCP_FLOW.map((r, i) => (
                      <tr key={i} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                        <td style={{ color: '#e2e8f0', fontFamily: '"Fira Code",monospace', fontSize: '0.68rem' }}>{r.t}</td>
                        <td style={{ color: '#94a3b8' }}>{r.sig}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {r.choices.map((c, j) => (
                              <span key={j} className="badge" style={{ background: c === '—' ? 'transparent' : 'rgba(167,139,250,0.12)', color: c === '—' ? '#475569' : '#c4b5fd', fontSize: '0.58rem', fontFamily: '"Fira Code",monospace' }}>{c}</span>
                            ))}
                          </div>
                        </td>
                        <td style={{ color: '#94a3b8' }}>{r.data}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}

          {/* Daml source — read-only review copy · K-R-O ARCHIVE */}
          {showKroArchive && (
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="bi bi-braces me-2 text-info"></i>Daml source — offer/take instrument (CIP-0056 · v0.1.0)
              </span>
              <button
                onClick={() => copy(CONTRACT_LANGUAGE_V014, 'daml-src')}
                className="btn btn-sm"
                style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: copied === 'daml-src' ? '#10b981' : '#60a5fa', fontSize: '0.62rem', padding: '0.18rem 0.6rem' }}
              >
                <i className={`bi ${copied === 'daml-src' ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>
                {copied === 'daml-src' ? 'Copied' : 'Copy source'}
              </button>
            </div>
            <div className="card-body p-0">
              <pre style={{ margin: 0, padding: '1rem', background: '#0b1220', color: '#cbd5e1', fontSize: '0.68rem', lineHeight: 1.55, fontFamily: '"Fira Code",monospace', overflowX: 'auto', maxHeight: 560 }}>
                <code>{CONTRACT_LANGUAGE_V014}</code>
              </pre>
            </div>
          </div>
          )}

          {/* Settlement Instructions & Routing — mirrors the onboarding packet */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(96,165,250,0.28)' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'linear-gradient(135deg, rgba(96,165,250,0.10), #0f172a)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="bi bi-signpost-split me-2" style={{ color: '#60a5fa' }}></i>Settlement Instructions &amp; Routing
              </span>
              <span className="badge" style={{ background: 'rgba(96,165,250,0.15)', color: '#93c5fd', fontSize: '0.6rem' }}>ONBOARDING PACKET · ref {SETTLEMENT_ROUTING.reference}</span>
            </div>
            <div className="card-body">
              <div className="p-2 rounded mb-3" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.22)', fontSize: '0.72rem', color: '#bfdbfe', lineHeight: 1.6 }}>
                <i className="bi bi-envelope-paper me-1"></i>
                These are the <b>exact parameters sent to counterparties</b> in the onboarding packet — the same
                routing, wallet, and instrument details the acting parties receive. The <code style={{ color: '#93c5fd' }}>SettlementSpec</code>
                fields in the contract source above are populated from these values at offer time.
              </div>

              {/* Issuer / CIP-0056 wrapped-denom explainer */}
              <div className="p-2 rounded mb-3" style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)', fontSize: '0.7rem', color: '#a7f3d0', lineHeight: 1.6 }}>
                <i className="bi bi-info-circle me-1"></i>
                <b>What "Issuer" means (CIP-0056):</b> every token holding is a{' '}
                <code style={{ color: '#6ee7b7' }}>{'InstrumentId { admin, id }'}</code>. The <b>admin is the issuer</b>.
                For the <b>collateral leg</b> the issuer is <b>K-R-O</b> (<code style={{ color: '#6ee7b7' }}>KRO-Mainnet-1::1220d605db…</code>)
                and the id is <code style={{ color: '#6ee7b7' }}>KRO.CER</code> — the CIP-0056 wrapping of the UCC Art. 12 CER /
                Provenance accountability-marker denom (DTIF <code style={{ color: '#6ee7b7' }}>ZX18049M0</code>). For the
                <b> cash leg</b> the issuer is <b>Circle</b> and the id is <code style={{ color: '#6ee7b7' }}>USDCx</code>.
              </div>

              {[
                { title: 'Trade terms', rows: SETTLEMENT_ROUTING.tradeTerms, cols: 2 },
                { title: 'Settlement instructions (routing)', rows: SETTLEMENT_ROUTING.settlement, cols: 1 },
                { title: 'Wallet & technical appendix', rows: SETTLEMENT_ROUTING.appendix, cols: 1 },
              ].map((sec) => (
                <div key={sec.title} className="mb-3">
                  <div style={{ fontSize: '0.66rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' }}>{sec.title}</div>
                  <div className="row g-2">
                    {sec.rows.map(([k, v], i) => (
                      <div key={i} className={sec.cols === 2 ? 'col-12 col-md-6' : 'col-12'}>
                        <div className="p-2 rounded h-100" style={{ background: 'rgba(96,165,250,0.05)', border: '1px solid rgba(96,165,250,0.16)' }}>
                          <div style={{ fontSize: '0.6rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</div>
                          <div style={{ fontSize: '0.72rem', color: '#e2e8f0', fontFamily: '"Fira Code",monospace', wordBreak: 'break-all' }}>{v}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Participants → identified counterparties */}
              <div style={{ fontSize: '0.66rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' }}>Participants — identified counterparties</div>
              <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>
                  <thead>
                    <tr style={{ color: '#64748b', borderColor: 'rgba(255,255,255,0.08)' }}>
                      <th>Role</th><th>Counterparty</th><th>Canton party ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SETTLEMENT_ROUTING.participants.map(([role, name, pid], i) => (
                      <tr key={i} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                        <td style={{ color: '#94a3b8' }}>{role}</td>
                        <td style={{ color: '#e2e8f0' }}>{name}</td>
                        <td style={{ color: '#93c5fd', fontFamily: '"Fira Code",monospace', fontSize: '0.66rem', wordBreak: 'break-all' }}>{pid}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Settlement evidence — SHA-256 document key (source of wealth) & KRO's qualified custodian */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(16,185,129,0.28)' }}>
            <div className="card-header border-0 d-flex align-items-center" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.10), #0f172a)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="bi bi-patch-check-fill me-2" style={{ color: '#34d399' }}></i>Settlement Evidence — Source of Wealth &amp; Custody
              </span>
            </div>
            <div className="card-body">
              {/* SHA-256 document key */}
              <div className="p-3 rounded mb-3" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)' }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                  <div style={{ fontSize: '0.62rem', color: '#6ee7b7', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <i className="bi bi-key-fill me-1"></i>SHA-256 document key (collateral reference · on-ledger)
                  </div>
                  <button
                    onClick={() => copy(SETTLEMENT_EVIDENCE.sha256Key, 'evi-sha')}
                    className="btn btn-sm"
                    style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', color: copied === 'evi-sha' ? '#10b981' : '#6ee7b7', fontSize: '0.6rem', padding: '0.14rem 0.55rem' }}
                  >
                    <i className={`bi ${copied === 'evi-sha' ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>{copied === 'evi-sha' ? 'Copied' : 'Copy key'}
                  </button>
                </div>
                <code style={{ color: '#e2e8f0', fontSize: '0.72rem', wordBreak: 'break-all', display: 'block' }}>{SETTLEMENT_EVIDENCE.sha256Key}</code>
              </div>

              {/* KRO's qualified custodian */}
              <div>
                <div style={{ fontSize: '0.62rem', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' }}>
                  <i className="bi bi-safe2-fill me-1"></i>KRO&apos;s Qualified Custodian
                </div>
                {SETTLEMENT_EVIDENCE.custody.map(([k, v], i) => (
                  <div key={i} className="p-2 rounded mb-1" style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)' }}>
                    <div style={{ fontSize: '0.58rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{k}</div>
                    <div style={{ fontSize: '0.7rem', color: '#e2e8f0', fontFamily: '"Fira Code",monospace', wordBreak: 'break-all' }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Daml source — UCC Article 12 control manifest · K-R-O ARCHIVE */}
          {showKroArchive && (
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="bi bi-shield-lock me-2 text-info"></i>Daml source — UCC Art. 12 control manifest (Kro/Cer/ControlManifest.daml)
              </span>
              <div className="d-flex align-items-center gap-2">
                <span className="badge" style={{ background: 'rgba(148,163,184,0.15)', color: '#94a3b8', fontSize: '0.58rem' }}>Reference · deployed module</span>
                <button
                  onClick={() => copy(CONTROL_MANIFEST_V1, 'cm-daml-src')}
                  className="btn btn-sm"
                  style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: copied === 'cm-daml-src' ? '#10b981' : '#60a5fa', fontSize: '0.62rem', padding: '0.18rem 0.6rem' }}
                >
                  <i className={`bi ${copied === 'cm-daml-src' ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>
                  {copied === 'cm-daml-src' ? 'Copied' : 'Copy source'}
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="p-2" style={{ fontSize: '0.66rem', color: '#94a3b8', lineHeight: 1.55, borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(148,163,184,0.04)' }}>
                <i className="bi bi-info-circle me-1" style={{ color: '#94a3b8' }}></i>
                <b style={{ color: '#cbd5e1' }}>For reference.</b> This is the deployed control-proof layer in the vetted
                {' '}<code style={{ color: '#93c5fd' }}>Canton Network Token Standard (CIP-0056)</code> DAR. It is invoked on demand via
                {' '}<code style={{ color: '#93c5fd' }}>Verify_Control</code>, which anchors an on-ledger
                {' '}<code style={{ color: '#93c5fd' }}>CER_ControlAttestation</code> — it is <b>not</b> exercised by the pilot
                self-DvP. The evidence that actually accompanies the settlement is the card above.
              </div>
              <pre style={{ margin: 0, padding: '1rem', background: '#0b1220', color: '#cbd5e1', fontSize: '0.68rem', lineHeight: 1.55, fontFamily: '"Fira Code",monospace', overflowX: 'auto', maxHeight: 560 }}>
                <code>{CONTROL_MANIFEST_V1}</code>
              </pre>
            </div>
          </div>
          )}

          {/* Intraday repo flow */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(99,102,241,0.2)' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.08), #0f172a)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="bi bi-arrow-repeat me-2" style={{ color: '#818cf8' }}></i>Intraday Repo Flow — Repo/Intraday.daml
              </span>
              <span className="badge" style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', fontSize: '0.6rem' }}>MainNet pilot executed</span>
            </div>
            <div className="card-body">
              {/* Actual MainNet lifecycle */}
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ marginBottom: '0.5rem' }}>
                <div style={{ fontSize: '0.66rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Actual MainNet lifecycle — <code style={{ color: '#94a3b8' }}>MainNetRepoScript:mainnetRepoSetup</code>
                </div>
                {/* Status-marker legend shared with GSF / T-RIZE */}
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {Object.entries(STAGE_KIND).map(([k, v]) => (
                    <span key={k} title={v.tip} className="badge" style={{ background: `${v.c}1c`, color: v.c, fontSize: '0.56rem', border: `1px solid ${v.c}44` }}>
                      <i className={`bi ${v.icon} me-1`}></i>{v.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="d-flex flex-column gap-1 mb-3">
                {REPO_MAINNET_LIFECYCLE.map((s) => {
                  const k = STAGE_KIND[s.kind] || STAGE_KIND.step;
                  return (
                    <div key={s.n} className="d-flex align-items-start gap-2 p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${s.kind === 'gate' ? 'rgba(245,158,11,0.28)' : 'rgba(99,102,241,0.12)'}` }}>
                      <span className="badge rounded-circle flex-shrink-0" style={{ background: 'rgba(99,102,241,0.2)', color: '#a5b4fc', fontSize: '0.62rem' }}>{s.n}</span>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <code style={{ color: '#c7d2fe', fontSize: '0.7rem' }}>{s.cmd}</code>
                          <span title={k.tip} className="badge flex-shrink-0" style={{ background: `${k.c}1c`, color: k.c, fontSize: '0.54rem', border: `1px solid ${k.c}44` }}>
                            <i className={`bi ${k.icon} me-1`}></i>{k.label}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.67rem', color: '#94a3b8' }}>{s.data}</div>
                        {/* Observable status event delivered to GSF / T-RIZE */}
                        <div style={{ fontSize: '0.6rem', color: '#64748b', marginTop: '0.15rem' }}>
                          <i className="bi bi-broadcast-pin me-1" style={{ color: '#818cf8' }}></i>
                          observers see: <code style={{ color: '#a5b4fc', fontSize: '0.6rem' }}>{s.emits}</code>
                          {s.kind === 'gate' && <span style={{ color: '#fcd34d' }}> · breakpoint: pass = witnessed new draw · precondition fail = submission rejected (off-ledger alert) · CancelDraw = explicit witnessed exit</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Daml pattern — standard CIP-0056 allocation lifecycle · K-R-O ARCHIVE */}
              {showKroArchive && (
              <>
              <div className="d-flex align-items-center justify-content-between mb-1">
                <div style={{ fontSize: '0.66rem', color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Daml pattern — standard allocation lifecycle (CIP-0056: EXECUTED / REJECTED / CANCELED / EXPIRED)</div>
                <button
                  onClick={() => copy(STAGE_STATUS_SRC, 'stage-status-src')}
                  className="btn btn-sm"
                  style={{ background: 'rgba(129,140,248,0.12)', border: '1px solid rgba(129,140,248,0.3)', color: copied === 'stage-status-src' ? '#10b981' : '#818cf8', fontSize: '0.62rem', padding: '0.18rem 0.6rem' }}
                >
                  <i className={`bi ${copied === 'stage-status-src' ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>
                  {copied === 'stage-status-src' ? 'Copied' : 'Copy source'}
                </button>
              </div>
              <pre style={{ margin: '0 0 1rem', padding: '1rem', background: '#0b1220', color: '#cbd5e1', fontSize: '0.68rem', lineHeight: 1.55, fontFamily: '"Fira Code",monospace', overflowX: 'auto', maxHeight: 420, borderRadius: 8 }}>
                <code>{STAGE_STATUS_SRC}</code>
              </pre>
              </>
              )}

              {/* Repo templates inventory */}
              <div style={{ fontSize: '0.66rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' }}>Templates &amp; choices</div>
              <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>
                  <tbody>
                    {REPO_TEMPLATES.map((r, i) => (
                      <tr key={i} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                        <td style={{ color: '#e2e8f0', fontFamily: '"Fira Code",monospace', fontSize: '0.68rem', width: '28%' }}>{r.t}</td>
                        <td style={{ width: '44%' }}>
                          <div className="d-flex flex-wrap gap-1">
                            {r.choices.map((c, j) => (
                              <span key={j} className="badge" style={{ background: c === '—' ? 'transparent' : 'rgba(99,102,241,0.12)', color: c === '—' ? '#475569' : '#a5b4fc', fontSize: '0.58rem', fontFamily: '"Fira Code",monospace' }}>{c}</span>
                            ))}
                          </div>
                        </td>
                        <td style={{ color: '#94a3b8' }}>{r.data}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: REPO LIFECYCLE (Repo.Intraday — DvP intraday repo settlement)
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'repo' && (
        <>
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <i className="bi bi-arrow-repeat text-info fs-5"></i>
            <div>
              <h5 className="mb-0 fw-bold text-white">Intraday Repo Lifecycle</h5>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                Repo.Intraday contracts on Canton {env.label} — RFQ → Facility → Draw → Rollover → Close-Out · live from canton_event_log
              </div>
            </div>
            <span className="badge ms-auto" style={{ background: '#14b8a620', color: '#5eead4', fontSize: '0.68rem' }}>
              {repoContracts.length} repo record{repoContracts.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Lifecycle stage strip with per-stage counts */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a' }}>
            <div className="card-body py-3">
              <div className="d-flex align-items-stretch gap-2 flex-wrap">
                {REPO_LIFECYCLE.map((s, i) => {
                  const count = repoContracts.filter(r => r.event_type === s.type).length;
                  return (
                    <React.Fragment key={s.type}>
                      <div className="text-center px-2" style={{ minWidth: 92, opacity: count ? 1 : 0.45 }}>
                        <div className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-1"
                          style={{ width: 34, height: 34, background: `${s.color}1c`, border: `1px solid ${s.color}55`, color: s.color }}>
                          <i className={`bi ${s.icon}`}></i>
                        </div>
                        <div style={{ fontSize: '0.62rem', color: '#94a3b8', lineHeight: 1.2 }}>{s.label}</div>
                        <div className="fw-bold" style={{ fontSize: '0.9rem', color: count ? s.color : '#475569' }}>{count}</div>
                      </div>
                      {i < REPO_LIFECYCLE.length - 1 && (
                        <div className="d-flex align-items-center" style={{ color: '#334155' }}>
                          <i className="bi bi-chevron-right"></i>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          {repoContracts.length === 0 && (
            <div className="card border-0 mb-4" style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="card-body text-center py-5" style={{ color: '#475569' }}>
                <i className="bi bi-arrow-repeat fs-2 mb-2 d-block"></i>
                No repo contracts in canton_event_log for {env.label} yet.
                <div className="mt-1"><small>Run <code style={{ color: '#64748b' }}>submit-mainnet-repo-tx.ps1</code> or the feed to populate this view.</small></div>
              </div>
            </div>
          )}

          {/* One section per lifecycle stage that has records */}
          {REPO_LIFECYCLE.map(stage => {
            const rows = repoContracts.filter(r => r.event_type === stage.type);
            if (!rows.length) return null;
            return (
              <div key={stage.type} className="card border-0 shadow-sm mb-3" style={{ background: '#0f172a', borderLeft: `3px solid ${stage.color}` }}>
                <div className="card-body p-3">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <i className={`bi ${stage.icon}`} style={{ color: stage.color }}></i>
                    <span style={{ color: stage.color, fontWeight: 600, fontSize: '0.85rem' }}>{stage.label}</span>
                    <span className="badge ms-auto" style={{ background: `${stage.color}18`, color: stage.color, fontSize: '0.58rem' }}>{rows.length}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.75rem' }}>
                    {rows.map((r, i) => (
                      <div key={i} className="p-3 rounded" style={{ background: `${stage.color}08`, border: `1px solid ${stage.color}20` }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <code style={{ fontSize: '0.6rem', color: '#94a3b8', wordBreak: 'break-all', flex: 1 }}>
                            {r.contract_id?.slice(0, 22)}…
                          </code>
                          <button className="btn btn-sm p-0 px-1" title="Copy contract ID"
                            style={{ background: `${stage.color}10`, border: `1px solid ${stage.color}30`, color: copied === `repo-${stage.type}-${i}` ? '#10b981' : stage.color, fontSize: '0.6rem', flexShrink: 0 }}
                            onClick={() => copy(r.contract_id, `repo-${stage.type}-${i}`)}>
                            <i className={`bi ${copied === `repo-${stage.type}-${i}` ? 'bi-check2' : 'bi-clipboard'}`}></i>
                          </button>
                        </div>
                        {repoFields(stage.type, r, parseMeta(r.metadata)).map(([label, value], j) => (
                          <div key={j} className="d-flex justify-content-between align-items-start mb-1" style={{ fontSize: '0.7rem' }}>
                            <span style={{ color: '#475569', flexShrink: 0 }}>{label}</span>
                            <span style={{ color: '#e2e8f0', textAlign: 'right', wordBreak: 'break-all', maxWidth: '62%' }}>{value ?? '—'}</span>
                          </div>
                        ))}
                        {r.event_timestamp && (
                          <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${stage.color}15`, fontSize: '0.62rem', color: '#475569' }}>
                            <i className="bi bi-clock me-1"></i>{new Date(r.event_timestamp).toLocaleString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: RFP TRANSACTIONS
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'rfp' && (() => {
        // Protocol is "live/deployed" when the active network is promoted to LIVE
        // (mainnet) or the backend reports on-ledger evidence (active package, an
        // open RFQ, or a recorded settlement). Drives the deployment badge below so
        // it reflects real ledger state instead of a hardcoded "NOT DEPLOYED".
        const protocolLive = env?.status === 'LIVE'
          || !!liveStatus?.activePackage
          || !!liveStatus?.openRfp
          || !!liveStatus?.lastSettlement
          || (typeof liveStatus?.activeContracts === 'number' && liveStatus.activeContracts > 0);
        return (
        <>
          <div className="d-flex align-items-center gap-2 mb-3">
            <i className="bi bi-send-fill text-success fs-5"></i>
            <div>
              <h5 className="mb-0 fw-bold text-white">RFQ Transaction Report</h5>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                Request for Quote — IntradayRepoRFP to greenlit on-ledger operators · CIP-56 atomic DvP on Global Synchronizer
              </div>
            </div>
            <span className="badge ms-auto" style={{ background: '#10b98120', color: '#10b981', fontSize: '0.68rem' }}>
              {rfqBroadcastCount} broadcast{rfqBroadcastCount !== 1 ? 's' : ''} on record{broadcasts ? ' · live' : ''}
            </span>
          </div>

          {/* ── LIVE RFQ BROADCAST LOG + CARRIERS + JOINT OFFERS (from backend) ── */}
          {(broadcasts || transferOffers || jointOffers) && (
            <div className="card border-0 shadow-sm mb-4" style={{ background: '#0b1220', color: '#e2e8f0', border: '1px solid rgba(16,185,129,0.25)' }}>
              <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'linear-gradient(135deg,#064e3b22,#0b1220)' }}>
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-broadcast text-success fs-5"></i>
                  <div>
                    <div className="fw-bold" style={{ fontSize: '0.92rem' }}>Live On-Ledger RFQ Activity</div>
                    <div style={{ fontSize: '0.65rem', color: '#64748b' }}>
                      Broadcast attempt log, Canton Coin RFQ carriers, and joint RFQ disclosed offers — from the backend, refreshed with this tab.
                    </div>
                  </div>
                </div>
                <button className="btn btn-sm" onClick={loadBroadcasts}
                  style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7', fontSize: '0.72rem' }}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
              </div>
              <div className="card-body">
                {/* Broadcast attempt summary */}
                {broadcasts && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span style={{ color: '#6ee7b7', fontWeight: 600, fontSize: '0.8rem' }}>RFQ Broadcast Log</span>
                      <span style={{ color: '#475569', fontSize: '0.68rem' }}>{broadcasts.total} attempts on record</span>
                    </div>
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      {Object.entries(broadcasts.summary?.byStatus || {}).map(([s, n]) => (
                        <span key={s} className="badge" style={{ background: s === 'OK' || s === 'SUCCESS' || s === 'BROADCAST' ? '#10b98118' : s === 'FAILED' ? '#ef444418' : '#64748b18', color: s === 'OK' || s === 'SUCCESS' || s === 'BROADCAST' ? '#10b981' : s === 'FAILED' ? '#fca5a5' : '#94a3b8', fontSize: '0.6rem' }}>{s}: {n}</span>
                      ))}
                    </div>
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                      {(broadcasts.broadcasts || []).slice(0, 12).map((b, i) => (
                        <div key={i} className="d-flex align-items-center justify-content-between gap-2 py-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '0.68rem' }}>
                          <span style={{ color: '#e2e8f0', flex: 1 }}>{b.partnerName || b.partnerId}</span>
                          <code style={{ color: '#64748b', fontSize: '0.58rem' }}>{(b.template || '').split(':').pop()}</code>
                          <span className="badge" style={{ background: b.status === 'FAILED' ? '#ef444418' : '#10b98118', color: b.status === 'FAILED' ? '#fca5a5' : '#10b981', fontSize: '0.55rem' }}>{b.status}</span>
                          <span style={{ color: '#475569', fontSize: '0.58rem' }}>{b.ts ? new Date(b.ts).toLocaleDateString() : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Joint RFQ disclosed offers */}
                {jointOffers && jointOffers.count > 0 && (
                  <div className="mb-3 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ color: '#c4b5fd', fontWeight: 600, fontSize: '0.8rem', marginBottom: '0.4rem' }}>Joint RFQ Offers ({jointOffers.count})</div>
                    {jointOffers.jointOffers.map((jo) => (
                      <div key={jo.settlementRef} className="p-2 rounded mb-2" style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.2)' }}>
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                          <code style={{ color: '#ddd6fe', fontSize: '0.68rem' }}>{jo.settlementRef}</code>
                          <span className="badge" style={{ background: '#8b5cf618', color: '#c4b5fd', fontSize: '0.55rem' }}>{(jo.legs || []).length} legs{jo.hasDisclosureBlob ? ' · disclosed' : ''}</span>
                        </div>
                        {(jo.legs || []).map((l) => (
                          <div key={l.legId} style={{ color: '#94a3b8', fontSize: '0.64rem' }}>
                            <span style={{ color: '#a78bfa' }}>{l.legId}</span> · {l.role} <span style={{ color: '#475569' }}>({l.instrumentId})</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {/* Canton Coin RFQ carrier offers */}
                {transferOffers && transferOffers.count > 0 && (
                  <div className="pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span style={{ color: '#6ee7b7', fontWeight: 600, fontSize: '0.8rem' }}>Canton Coin RFQ Carriers ({transferOffers.count})</span>
                      {transferOffers.respondBy && <span style={{ color: '#475569', fontSize: '0.66rem' }}>respond by {transferOffers.respondBy}</span>}
                    </div>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {transferOffers.offers.map((o) => (
                        <div key={o.trackingId} className="d-flex align-items-center justify-content-between gap-2 py-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '0.68rem' }}>
                          <span style={{ color: '#e2e8f0', flex: 1 }}>{o.partnerName || o.partnerId}</span>
                          <code style={{ color: '#a78bfa', fontSize: '0.58rem' }}>{o.rfqRef}</code>
                          <span className="badge" style={{ background: o.expired ? '#ef444418' : '#10b98118', color: o.expired ? '#fca5a5' : '#10b981', fontSize: '0.55rem' }}>{o.expired ? 'EXPIRED' : 'ACTIVE'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── PRE-DEPLOY PREVIEW — the contract that will be submitted ───────── */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid rgba(245,158,11,0.3)' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2"
              style={{ background: 'linear-gradient(135deg,#78350f22,#0f172a)' }}>
              <div className="d-flex align-items-center gap-2">
                <i className={`bi ${protocolLive ? 'bi-broadcast text-success' : 'bi-eye-fill text-warning'} fs-5`}></i>
                <div>
                  <div className="fw-bold" style={{ fontSize: '0.92rem' }}>
                    {protocolLive ? 'Live Contract — exact on-ledger payload' : 'Pre-Deploy Preview — see the contract before deploying'}
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b' }}>
                    Exact <code style={{ color: '#fcd34d' }}>IntradayRepoRFP</code> create payload per partner — identical to what <code>run-intraday-repo.js</code> submits{protocolLive ? ' on the live ledger' : ''}. This view is read-only; nothing is sent from here.
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {protocolLive ? (
                  <span className="badge" style={{ background: '#10b98122', color: '#10b981', fontSize: '0.65rem' }}>DEPLOYED · LIVE ON {(env?.label || activeEnv).toUpperCase()}</span>
                ) : (
                  <span className="badge" style={{ background: '#f59e0b22', color: '#fcd34d', fontSize: '0.65rem' }}>PREVIEW · NOT DEPLOYED</span>
                )}
                <button className="btn btn-sm" onClick={loadRfpPreview} disabled={previewLoading}
                  style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#fcd34d', fontSize: '0.72rem' }}>
                  <i className={`bi ${previewLoading ? 'bi-hourglass-split' : 'bi-arrow-clockwise'} me-1`}></i>
                  {previewLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
            </div>
            <div className="card-body">
              {previewError && (
                <div className="p-2 rounded mb-3" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5', fontSize: '0.72rem' }}>
                  <i className="bi bi-exclamation-triangle-fill me-1"></i>{previewError}
                </div>
              )}
              {rfpPreview && (
                <div className="d-flex flex-wrap gap-3 mb-3" style={{ fontSize: '0.7rem' }}>
                  <div><span style={{ color: '#475569' }}>Template</span><div className="font-monospace" style={{ color: '#fcd34d' }}>{rfpPreview.template}</div></div>
                  <div><span style={{ color: '#475569' }}>Borrower (signatory)</span><div className="font-monospace text-truncate" style={{ maxWidth: 320, color: '#e2e8f0' }}>{rfpPreview.borrower}</div></div>
                  <div><span style={{ color: '#475569' }}>Network</span><div style={{ color: '#e2e8f0' }}>{(rfpPreview.network || activeEnv).toUpperCase()}</div></div>
                  <div><span style={{ color: '#475569' }}>Contracts</span><div style={{ color: '#e2e8f0' }}>{rfpPreview.count}</div></div>
                </div>
              )}
              {!previewLoading && rfpPreview && rfpPreview.count === 0 && (
                <div className="text-center py-4" style={{ color: '#475569', fontSize: '0.8rem' }}>
                  No addressable partners with a Canton party ID for {env.label}.
                </div>
              )}
              {rfpPreview && (rfpPreview.previews || []).map((pv, i) => {
                const a = pv.createArguments || {};
                const m = pv.meta || {};
                const open = !!expandedJson[pv.partner?.id];
                return (
                  <div key={i} className="p-3 rounded mb-3" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${pv.warning ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.18)'}` }}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                      <div className="fw-bold" style={{ fontSize: '0.82rem', color: '#fcd34d' }}>
                        <i className="bi bi-bank me-1"></i>{pv.partner?.name} <span style={{ color: '#475569', fontWeight: 400 }}>({pv.partner?.id})</span>
                      </div>
                      <span className="badge" style={{ background: m.covered ? '#10b98120' : '#ef444420', color: m.covered ? '#10b981' : '#fca5a5', fontSize: '0.62rem' }}>
                        {m.covered ? 'COLLATERAL COVERS DRAW ✓' : 'UNDER-COLLATERALIZED ✗'}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span style={{ fontSize: '0.6rem', color: '#475569' }}>approvedLender (receiver, on their Canton node)</span>
                      <code style={{ display: 'block', fontSize: '0.6rem', color: '#ddd6fe', wordBreak: 'break-all' }}>{(a.approvedLenders || [])[0]}</code>
                    </div>
                    <div className="row g-2" style={{ fontSize: '0.7rem' }}>
                      {[
                        ['Draw notional',    `$${(m.notional || 0).toLocaleString()}`],
                        ['Collateral',       `$${(m.collat || 0).toLocaleString()}`],
                        ['Adjusted (post-haircut)', `$${Math.round(m.adjusted || 0).toLocaleString()}`],
                        ['Haircut',          `${((m.haircut || 0) * 100).toFixed(2)}%`],
                        ['Intraday rate',    `${m.rateBps} bps`],
                        ['DTIF',             a.dtifId],
                        ['RFQ correlation ref', a.rfqCorrelationRef],
                        ['Window',           `${a.startTime} → ${a.expiryTime}`],
                      ].map(([l, v], k) => (
                        <div className="col-md-6 d-flex justify-content-between" key={k}>
                          <span style={{ color: '#475569', flexShrink: 0 }}>{l}</span>
                          <span style={{ color: '#e2e8f0', textAlign: 'right', wordBreak: 'break-all', maxWidth: '70%' }}>{v}</span>
                        </div>
                      ))}
                    </div>
                    {pv.warning && (
                      <div className="mt-2 p-2 rounded" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5', fontSize: '0.66rem' }}>
                        <i className="bi bi-exclamation-triangle-fill me-1"></i>{pv.warning}
                      </div>
                    )}
                    <div className="mt-2 d-flex gap-2">
                      <button className="btn btn-sm" onClick={() => setExpandedJson(s => ({ ...s, [pv.partner?.id]: !open }))}
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', fontSize: '0.68rem' }}>
                        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-code-slash'} me-1`}></i>{open ? 'Hide' : 'View'} raw createArguments
                      </button>
                      <button className="btn btn-sm" onClick={() => copy(JSON.stringify({ templateId: pv.templateId, createArguments: a }, null, 2), `pv-${pv.partner?.id}`)}
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: copied === `pv-${pv.partner?.id}` ? '#10b981' : '#94a3b8', fontSize: '0.68rem' }}>
                        <i className={`bi ${copied === `pv-${pv.partner?.id}` ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>Copy JSON
                      </button>
                    </div>
                    {open && (
                      <pre className="mt-2 p-2 rounded" style={{ background: '#020617', border: '1px solid rgba(255,255,255,0.08)', color: '#a7f3d0', fontSize: '0.62rem', maxHeight: 260, overflow: 'auto' }}>
{JSON.stringify({ templateId: pv.templateId, createArguments: a }, null, 2)}
                      </pre>
                    )}
                  </div>
                );
              })}
              <div className="p-2 rounded" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.18)', fontSize: '0.68rem', color: '#6ee7b7' }}>
                <i className="bi bi-terminal me-1"></i>
                Ready to deploy? Run <code style={{ color: '#a7f3d0' }}>node backend/scripts/run-intraday-repo.js --execute</code> (add <code style={{ color: '#a7f3d0' }}>--partner &lt;id&gt;</code> for one at a time).
              </div>
            </div>
          </div>

          {RFP_TRANSACTIONS.filter(r => r.env === activeEnv || activeEnv === 'mainnet').length === 0 && (
            <div className="card border-0 mb-4" style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="card-body text-center py-5" style={{ color: '#475569' }}>
                <i className="bi bi-send fs-2 mb-2 d-block"></i>
                No RFQ transactions recorded for {env.label} yet.
              </div>
            </div>
          )}

          {RFP_TRANSACTIONS.map((rfp, idx) => {
            // Dates follow the live on-ledger RFP (network-status openRfp) when
            // present, so the card reflects the actual latest submission/expiry
            // instead of a hardcoded timestamp. Falls back to the static values
            // only when no live RFP is currently observable on the ledger.
            const liveRfp     = liveStatus?.openRfp;
            const submittedAt = liveRfp?.startTime  || rfp.submittedAt;
            const expiresAt   = liveRfp?.expiryTime || rfp.expiresAt;
            const datesLive   = Boolean(liveRfp?.startTime || liveRfp?.expiryTime);
            return (
            <div key={idx} className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid rgba(16,185,129,0.2)' }}>

              {/* Header */}
              <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2"
                style={{ background: 'linear-gradient(135deg,#064e3b18,#0f172a)' }}>
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-broadcast text-success fs-5"></i>
                  <div>
                    <div className="fw-bold" style={{ fontSize: '0.92rem' }}>{rfp.colr001Ref || rfp.rfpContractId}</div>
                    <div style={{ fontSize: '0.65rem', color: '#64748b' }}>
                      {new Date(submittedAt).toLocaleString()}{datesLive ? ' · on-ledger' : ''} · DTIF: {rfp.dtifId} · DvP via Canton
                    </div>
                  </div>
                </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="badge" style={{ background: rfp.status === 'ACTIVE' ? '#10b98120' : '#64748b20', color: rfp.status === 'ACTIVE' ? '#10b981' : '#94a3b8', fontSize: '0.65rem' }}>
                    {rfp.status}
                  </span>
                  {expiresAt && (
                    <span className="badge" style={{ background: '#7c3aed20', color: '#c4b5fd', fontSize: '0.65rem' }}>
                      Open until {new Date(expiresAt).toLocaleString()}
                    </span>
                  )}
                  <span className="badge" style={{ background: rfp.lenderStatus.startsWith('BROADCAST') ? '#10b98120' : '#f59e0b20', color: rfp.lenderStatus.startsWith('BROADCAST') ? '#10b981' : '#f59e0b', fontSize: '0.65rem' }}>
                    {rfp.lenderStatus}
                  </span>
                </div>
              </div>

              <div className="card-body">
                <div className="row g-3">

                  {/* Canton Contract */}
                  <div className="col-12">
                    <div className="p-3 rounded" style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)' }}>
                      <div style={{ fontSize: '0.65rem', color: '#6ee7b7', marginBottom: '0.3rem' }}>
                        <i className="bi bi-file-earmark-lock2-fill me-1"></i>Canton Contract — IntradayRepoRFP
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <code style={{ fontSize: '0.62rem', color: '#a7f3d0', wordBreak: 'break-all' }}>{rfp.rfpContractId}</code>
                        <button onClick={() => copy(rfp.rfpContractId, 'cid')}
                          style={{ background: 'none', border: 'none', color: copied === 'cid' ? '#10b981' : '#475569', cursor: 'pointer', flexShrink: 0 }}>
                          <i className={`bi ${copied === 'cid' ? 'bi-check2' : 'bi-clipboard'} fs-6`}></i>
                        </button>
                      </div>
                      <div className="mt-1" style={{ fontSize: '0.62rem', color: '#475569' }}>
                        Package: {rfp.package} · Hash: {rfp.packageHash.slice(0, 16)}…
                      </div>
                    </div>
                  </div>

                  {/* Settlement Architecture */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 rounded h-100" style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.18)' }}>
                      <div style={{ fontSize: '0.65rem', color: '#6ee7b7', marginBottom: '0.5rem' }}>
                        <i className="bi bi-diagram-3-fill me-1"></i>Settlement Architecture
                      </div>
                      {/* DvP flow */}
                      {rfp.dvpFlow && (
                        <div className="mb-2">
                          <div style={{ fontSize: '0.6rem', color: '#475569', marginBottom: '0.3rem' }}>Canton DvP Flow (primary)</div>
                          <ol className="mb-0 ps-3" style={{ fontSize: '0.68rem', color: '#a7f3d0' }}>
                            {rfp.dvpFlow.map((step, i) => <li key={i}>{step}</li>)}
                          </ol>
                        </div>
                      )}
                      <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(16,185,129,0.12)' }}>
                        {[
                          ['Method',       rfp.transmissionMethod],
                          ['Cash leg',     'CIP-56 token — atomic on-ledger DvP'],
                          ['RFQ correlation ref', rfp.rfqCorrelationRef],
                        ].map(([l, v], i) => (
                          <div key={i} className="d-flex justify-content-between align-items-start mb-1" style={{ fontSize: '0.68rem' }}>
                            <span style={{ color: '#475569', flexShrink: 0 }}>{l}</span>
                            <span style={{ color: '#e2e8f0', textAlign: 'right', wordBreak: 'break-all', maxWidth: '65%' }}>{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Draw terms */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 rounded h-100" style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)' }}>
                      <div style={{ fontSize: '0.65rem', color: '#fcd34d', marginBottom: '0.5rem' }}>
                        <i className="bi bi-bank me-1"></i>Draw Terms
                      </div>
                      {[
                        ['KRO Role',          rfp.kroRole],
                        ['LEI',               rfp.lei],
                        ['Open Until',        expiresAt ? new Date(expiresAt).toLocaleString() : '—'],
                        ['Draw Notional',     `$${rfp.drawNotional.toLocaleString()}`],
                        ['Collateral Value',  `$${rfp.collateralValue.toLocaleString()}`],
                        ['Haircut',           rfp.haircutPct],
                        ['Pricing Inquiry',   `Intraday T+0 · ${rfp.intradayRateBps} bps indicative (quotes solicited)`],
                        ['DTIF',              rfp.dtifId],
                        ['Collateral',        rfp.collateral],
                      ].map(([l, v], i) => (
                        <div key={i} className="d-flex justify-content-between align-items-start mb-1" style={{ fontSize: '0.72rem' }}>
                          <span style={{ color: '#475569', flexShrink: 0 }}>{l}</span>
                          <span style={{ color: l === 'KRO Role' ? '#fcd34d' : '#e2e8f0', textAlign: 'right', wordBreak: 'break-all', maxWidth: '60%' }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Receivers / approvedLenders */}
                  <div className="col-12">
                    <div className="p-3 rounded" style={{ background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.15)' }}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div style={{ fontSize: '0.65rem', color: '#c4b5fd' }}>
                          <i className="bi bi-people-fill me-1"></i>RFQ Respondents (approvedLenders on Canton)
                        </div>
                        <span className="badge" style={{ background: 'rgba(139,92,246,0.2)', color: '#c4b5fd', fontSize: '0.65rem' }}>
                          {rfp.approvedLenders.length} respondent{rfp.approvedLenders.length !== 1 ? 's' : ''} in broadcast
                        </span>
                      </div>
                      {rfp.approvedLenders.map((l, i) => (
                        <div key={i} className="p-2 rounded mb-2" style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.1)' }}>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            {l.name && <div style={{ fontSize: '0.72rem', color: '#c4b5fd', fontWeight: 600 }}><i className="bi bi-bank me-1"></i>{l.name}</div>}
                            {l.entityType && (
                              <span className="badge ms-auto" style={{ background: 'rgba(100,116,139,0.18)', color: '#94a3b8', fontSize: '0.58rem', fontWeight: 400 }}>
                                {l.entityType}
                              </span>
                            )}
                          </div>
                          <code style={{ fontSize: '0.6rem', color: '#ddd6fe', wordBreak: 'break-all' }}>{l.party}</code>
                        </div>
                      ))}
                      <div className="mt-2 p-2 rounded" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', fontSize: '0.68rem', color: '#6ee7b7' }}>
                        <i className="bi bi-lightning-charge-fill me-1"></i>
                        <strong>On-chain DvP:</strong> first greenlit SV to call <code style={{ color: '#a7f3d0' }}>AcceptRFP</code> creates the <code style={{ color: '#a7f3d0' }}>IntradayRepoFacility</code> atomically on Global Synchronizer.
                      </div>
                    </div>
                  </div>

                  {/* Lighthouse link */}
                  <div className="col-12">
                    <a href={rfp.lighthouseUrl} target="_blank" rel="noopener noreferrer"
                      className="btn btn-sm w-100"
                      style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', color: '#10b981', fontSize: '0.75rem' }}>
                      <i className="bi bi-box-arrow-up-right me-2"></i>
                      View on Canton Lighthouse — MainNet Production Ledger
                    </a>
                  </div>

                </div>
              </div>
            </div>
            );
          })}
        </>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: SV REGISTRY
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'svregistry' && (
        <>
          {/* Header */}
          <div className="card border-0 shadow-sm mb-3" style={{ background: '#0f172a', color: '#e2e8f0' }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center gap-3 mb-2">
                <i className="bi bi-bank2" style={{ fontSize: '1.4rem', color: '#10b981' }}></i>
                <div>
                  <h5 className="fw-bold mb-0" style={{ color: '#f1f5f9' }}>Canton MainNet Super-Validators</h5>
                  <p className="mb-0" style={{ color: '#64748b', fontSize: '0.78rem' }}>
                    Source: {svLive ? <span style={{ color: '#10b981' }}>live backend registry (/canton/sv-registry)</span> : <>ANS registry (<code style={{ color: '#a7f3d0' }}>.sv.cns</code>)</>} · {svRegistryList.length} active SVs
                  </p>
                </div>
                <div className="ms-auto d-flex gap-2">
                  <span className="badge" style={{ background: '#10b98118', color: '#10b981', fontSize: '0.68rem', padding: '4px 10px' }}>
                    {greenlitCount} greenlit{liveScope ? ' · live gate' : ''}
                  </span>
                  <span className="badge" style={{ background: '#64748b18', color: '#94a3b8', fontSize: '0.68rem', padding: '4px 10px' }}>
                    bridge pending Foundation
                  </span>
                </div>
              </div>
              {/* Target institution gap notice */}
              <div className="p-3 rounded mt-2" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
                <div className="mb-1" style={{ color: '#fcd34d', fontWeight: 600, fontSize: '0.78rem' }}>
                  <i className="bi bi-exclamation-triangle me-2"></i>TARGET INSTITUTIONS NOT YET ON CANTON GLOBAL SYNCHRONIZER <span className="badge" style={{ background: 'rgba(148,163,184,0.2)', color: '#cbd5e1', fontSize: '0.5rem', fontWeight: 600, verticalAlign: 'middle' }}>K-R-O REF ONLY</span>
                </div>
                <div style={{ color: '#94a3b8', fontSize: '0.73rem', lineHeight: 1.6 }}>
                  BNY Mellon, Broadridge, DTCC, Euroclear, Clearstream, JPMorgan (Kinexys), Goldman Sachs (GS DAP), and Morgan Stanley
                  are <strong style={{ color: '#fcd34d' }}>not yet registered as Canton Global Synchronizer MainNet parties</strong>.
                  They operate on private Canton deployments or are on integration roadmap.
                  <br />
                  <span style={{ color: '#a7f3d0' }}>RFQ scope:</span> {greenlitCount} greenlit on-ledger settlement operators on Global Synchronizer (live from backend <code style={{ color: '#a7f3d0' }}>CER_RFQ_PILOT_GREENLIT</code> gate). Private-sync primes listed above are network watchlist only — not in Canton pilot RFQ broadcasts.
                </div>
              </div>
              <div className="p-3 rounded mt-2" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.25)' }}>
                <div className="mb-2" style={{ color: '#c4b5fd', fontWeight: 600, fontSize: '0.78rem' }}>
                  <i className="bi bi-diagram-3 me-2"></i>COLLATERAL MOBILITY — USE CASE LANDSCAPE
                </div>
                {collateralUseCases.map((uc) => (
                  <div key={uc.id} className="mb-2" style={{ color: '#94a3b8', fontSize: '0.73rem', lineHeight: 1.55 }}>
                    <strong style={{ color: '#e2e8f0' }}>{uc.name}</strong> — {uc.interest}
                    <span style={{ color: '#a7f3d0' }}> · Cash leg: {uc.cashLeg}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Repo-eligible SVs + bridge SVs */}
          <div className="card border-0 shadow-sm mb-3" style={{ background: '#0d2818', borderLeft: '3px solid #10b981' }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-currency-dollar" style={{ color: '#10b981' }}></i>
                <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>GREENLIT — ON-CHAIN RFQ RESPONDENTS</span>
                <span className="badge ms-2" style={{ background: '#10b98122', color: '#10b981', fontSize: '0.6rem' }}>approvedLenders · can settle on-chain today</span>
              </div>
              {liveGreenlit ? (
                liveGreenlit.map((sv) => {
                  const pid = sv.cantonPartyId || sv.partyId;
                  return (
                    <div key={sv.id || pid} className="mb-3 pb-3" style={{ borderBottom: '1px solid rgba(16,185,129,0.1)' }}>
                      <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                        <div>
                          <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.88rem' }}>{sv.name}</span>
                          <span className="badge ms-2" style={{ background: '#10b98122', color: '#10b981', fontSize: '0.58rem' }}>GREENLIT · LIVE GATE</span>
                        </div>
                        {sv.settlementProfile && <code style={{ color: '#94a3b8', fontSize: '0.6rem' }}>{sv.settlementProfile}</code>}
                      </div>
                      <p style={{ color: '#64748b', fontSize: '0.75rem', margin: '2px 0 4px' }}>{sv.role}</p>
                      <div className="d-flex align-items-center gap-2">
                        <code style={{ color: '#a7f3d0', fontSize: '0.62rem', wordBreak: 'break-all', flex: 1 }}>{pid}</code>
                        <button className="btn btn-sm p-0 px-1" style={{ background: 'rgba(167,243,208,0.06)', border: '1px solid rgba(167,243,208,0.2)', color: '#a7f3d0', fontSize: '0.65rem' }}
                          onClick={() => navigator.clipboard.writeText(pid)} title="Copy party ID">
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <>
                  {CANTON_SV_REGISTRY.filter(isGreenlitSv).map((sv) => (
                    <div key={sv.id} className="mb-3 pb-3" style={{ borderBottom: '1px solid rgba(16,185,129,0.1)' }}>
                      <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                        <div>
                          <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.88rem' }}>{sv.name}</span>
                          <span className="badge ms-2" style={{ background: '#0ea5e918', color: '#0ea5e9', fontSize: '0.58rem' }}>SV · LIVE</span>
                        </div>
                        <code style={{ color: '#94a3b8', fontSize: '0.6rem' }}>{sv.ansName}</code>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '0.75rem', margin: '2px 0 4px' }}>{sv.role}</p>
                      <div className="d-flex align-items-center gap-2">
                        <code style={{ color: '#a7f3d0', fontSize: '0.62rem', wordBreak: 'break-all', flex: 1 }}>{sv.partyId}</code>
                        <button className="btn btn-sm p-0 px-1" style={{ background: 'rgba(167,243,208,0.06)', border: '1px solid rgba(167,243,208,0.2)', color: '#a7f3d0', fontSize: '0.65rem' }}
                          onClick={() => navigator.clipboard.writeText(sv.partyId)} title="Copy party ID">
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                  {CANTON_VALIDATOR_NODES.filter((n) => isGreenlitSv(n) && !CANTON_SV_REGISTRY.some((sv) => sv.id === n.id)).map((n) => (
                    <div key={n.id} className="mb-3 pb-3" style={{ borderBottom: '1px solid rgba(16,185,129,0.1)' }}>
                      <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                        <div>
                          <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.88rem' }}>{n.name}</span>
                          <span className="badge ms-2" style={{ background: '#10b98122', color: '#10b981', fontSize: '0.58rem' }}>VALIDATOR · GREENLIT</span>
                        </div>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '0.75rem', margin: '2px 0 4px' }}>{n.role}</p>
                      <div className="d-flex align-items-center gap-2">
                        <code style={{ color: '#a7f3d0', fontSize: '0.62rem', wordBreak: 'break-all', flex: 1 }}>{n.partyId}</code>
                        <button className="btn btn-sm p-0 px-1" style={{ background: 'rgba(167,243,208,0.06)', border: '1px solid rgba(167,243,208,0.2)', color: '#a7f3d0', fontSize: '0.65rem' }}
                          onClick={() => navigator.clipboard.writeText(n.partyId)} title="Copy party ID">
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Observe / non-broadcast settlement operators (live) */}
          {liveObserve && liveObserve.length > 0 && (
            <div className="card border-0 shadow-sm mb-3" style={{ background: '#0b1220', borderLeft: '3px solid #0ea5e9' }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-eye" style={{ color: '#0ea5e9' }}></i>
                  <span style={{ color: '#0ea5e9', fontWeight: 600, fontSize: '0.85rem' }}>OBSERVE — SETTLEMENT OPERATORS (NOT BROADCAST)</span>
                  <span className="badge ms-2" style={{ background: '#0ea5e918', color: '#0ea5e9', fontSize: '0.6rem' }}>live · resolved party · outside current gate</span>
                </div>
                {liveObserve.map((o) => {
                  const pid = o.cantonPartyId || o.partyId;
                  return (
                    <div key={o.id || pid} className="mb-2 pb-2" style={{ borderBottom: '1px solid rgba(14,165,233,0.1)' }}>
                      <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                        <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.82rem' }}>{o.name}</span>
                        {o.settlementProfile && <code style={{ color: '#64748b', fontSize: '0.58rem' }}>{o.settlementProfile}</code>}
                      </div>
                      {o.role && <p style={{ color: '#64748b', fontSize: '0.72rem', margin: '2px 0 4px' }}>{o.role}</p>}
                      <code style={{ color: '#7dd3fc', fontSize: '0.6rem', wordBreak: 'break-all' }}>{pid}</code>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bridge SVs (DA) */}
          <div className="card border-0 shadow-sm mb-3" style={{ background: '#1a1200', borderLeft: '3px solid #f59e0b' }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-signpost-split" style={{ color: '#f59e0b' }}></i>
                <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: '0.85rem' }}>BRIDGE — PENDING FOUNDATION (NOT IN RFQ)</span>
                <span className="badge ms-2" style={{ background: '#64748b18', color: '#94a3b8', fontSize: '0.6rem' }}>documented only · not in approvedLenders</span>
              </div>
              {svRegistryList.filter(sv => !sv.repoEligible && sv.isBridge).map((sv) => (
                <div key={sv.id} className="mb-3 pb-3" style={{ borderBottom: '1px solid rgba(245,158,11,0.1)' }}>
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                    <div>
                      <span style={{ color: '#fcd34d', fontWeight: 600, fontSize: '0.85rem' }}>{sv.name}</span>
                      <span className="badge ms-2" style={{ background: '#0ea5e918', color: '#0ea5e9', fontSize: '0.58rem' }}>SV · LIVE</span>
                    </div>
                    <code style={{ color: '#78716c', fontSize: '0.6rem' }}>{sv.ansName}</code>
                  </div>
                  <p style={{ color: '#78716c', fontSize: '0.75rem', margin: '2px 0 4px' }}>{sv.role}</p>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <code style={{ color: '#d97706', fontSize: '0.62rem', wordBreak: 'break-all', flex: 1 }}>{sv.partyId}</code>
                    <button className="btn btn-sm p-0 px-1" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', color: '#f59e0b', fontSize: '0.65rem' }}
                      onClick={() => navigator.clipboard.writeText(sv.partyId)} title="Copy party ID">
                      <i className="bi bi-clipboard"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Off-Global-Synchronizer watchlist — not in Canton RFQ broadcast */}
          <div className="card border-0 shadow-sm mb-3" style={{ background: '#0f172a', borderLeft: '3px solid #64748b' }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-signpost-2" style={{ color: '#94a3b8' }}></i>
                <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.85rem' }}>NETWORK WATCHLIST</span>
                <span className="badge ms-2" style={{ background: '#64748b18', color: '#94a3b8', fontSize: '0.6rem' }}>not in Canton RFQ broadcast</span>
              </div>
              <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ color: '#64748b', fontSize: '0.68rem', marginBottom: '0.5rem' }}>Off-chain / private-sync prime targets</div>
                <div className="d-flex flex-wrap gap-1">
                  {offChainPrime.map(t => (
                    <span key={t.id} className="badge" style={{ background: 'rgba(245,158,11,0.1)', color: '#fcd34d', fontSize: '0.58rem' }} title={`${t.reachVia} · ${t.status}`}>
                      {t.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Governance SVs */}
          <div className="card border-0 shadow-sm mb-3" style={{ background: '#0f172a' }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-shield-check" style={{ color: '#64748b' }}></i>
                <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.85rem' }}>GOVERNANCE / INFRASTRUCTURE SVs</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.75rem' }}>
                {svRegistryList.filter(sv => !sv.repoEligible && !sv.isBridge).map((sv) => (
                  <div key={sv.id} className="p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <span style={{ color: '#cbd5e1', fontWeight: 500, fontSize: '0.82rem' }}>{sv.name}</span>
                      <code style={{ color: '#475569', fontSize: '0.55rem' }}>{sv.ansName}</code>
                    </div>
                    <p style={{ color: '#475569', fontSize: '0.72rem', margin: '2px 0 4px' }}>{sv.role}</p>
                    <div className="d-flex align-items-center gap-1">
                      <code style={{ color: '#64748b', fontSize: '0.58rem', wordBreak: 'break-all', flex: 1 }}>{sv.partyId.substring(0, 52)}…</code>
                      <button className="btn btn-sm p-0 px-1" style={{ background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.2)', color: '#64748b', fontSize: '0.6rem' }}
                        onClick={() => navigator.clipboard.writeText(sv.partyId)} title="Copy party ID">
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* DSO + discovery note */}
          <div className="card border-0 shadow-sm" style={{ background: '#0f172a', borderLeft: '3px solid #3b82f6' }}>
            <div className="card-body p-3">
              <p style={{ color: '#64748b', fontSize: '0.75rem', margin: 0 }}>
                <i className="bi bi-info-circle me-2" style={{ color: '#3b82f6' }}></i>
                <strong style={{ color: '#94a3b8' }}>DSO:</strong>{' '}
                <code style={{ color: '#93c5fd' }}>DSO::1220b1431ef217342db44d516bb9befde802be7d8899637d290895fa58880f19accc</code>
                &nbsp;·&nbsp;Discovery: <code style={{ color: '#a7f3d0' }}>GET /api/validator/v0/scan-proxy/ans-entries?page_size=200</code> (144 total entries, 13 SVs)
              </p>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: NODE OPERATORS (CASH SETTLEMENT PARTICIPANTS)
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'onboard' && (
        <>
          {/* Header */}
          <div className="card border-0 shadow-sm mb-3" style={{ background: '#0f172a', color: '#e2e8f0' }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center gap-3 mb-2">
                <i className="bi bi-file-earmark-arrow-down" style={{ fontSize: '1.4rem', color: '#10b981' }}></i>
                <div>
                  <h5 className="fw-bold mb-0" style={{ color: '#f1f5f9' }}>{onboarding?.packetTitle || 'RFQ / Pilot Routing Note — Collateralized On-Chain DvP Settlement Flow'}</h5>
                  <p className="mb-0" style={{ color: '#64748b', fontSize: '0.78rem' }}>
                    Collateralized on-chain DvP pilot routing · atomic CIP-0056 settlement · standard participant processing · network: <code style={{ color: '#a7f3d0' }}>{activeEnv}</code>
                  </p>
                </div>
                <button className="btn btn-sm ms-auto" style={{ background: '#10b98118', color: '#10b981', border: '1px solid #10b98140', fontSize: '0.72rem' }}
                  onClick={loadOnboarding}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
              </div>
              <p className="mb-3" style={{ color: '#94a3b8', fontSize: '0.74rem', lineHeight: 1.6 }}>
                K-R-O submits this <b style={{ color: '#cbd5e1' }}>RFQ / pilot routing note</b> for a fully collateralized, deterministic on-chain DvP flow under the Canton Network pilot framework.
                Each packet is the standard handoff a provider&apos;s registry operator needs to vet <code style={{ color: '#a7f3d0' }}>KRO.CER</code>, confirm routing at{' '}
                <code style={{ color: '#a7f3d0' }}>https://api.kromarketplace.com/registry</code>, and proceed with participant processing.
                Pilot envelope: <b style={{ color: '#cbd5e1' }}>10 days · 2 flows · $5M per flow · $10M daily cap</b> · T+0 atomic finality via CIP-0056.
              </p>
            </div>
          </div>

          {onboardingErr && (
            <div className="alert" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5', fontSize: '0.8rem' }}>
              <i className="bi bi-exclamation-octagon me-2"></i>Could not load onboarding info: {onboardingErr}
            </div>
          )}

          {!onboarding && !onboardingErr && (
            <div className="text-center p-4" style={{ color: '#64748b' }}>
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>Loading onboarding packets…
            </div>
          )}

          {onboarding && (
            <>
              {/* Instrument + package summary */}
              <div className="card border-0 shadow-sm mb-3" style={{ background: '#0d2818', borderLeft: '3px solid #10b981' }}>
                <div className="card-body p-3">
                  <div className="row g-3" style={{ fontSize: '0.76rem' }}>
                    <div className="col-md-6">
                      <div style={{ color: '#64748b' }}>Instrument</div>
                      <code style={{ color: '#a7f3d0' }}>{onboarding.instrument}</code>
                      <div className="mt-2" style={{ color: '#64748b' }}>Issuer / registry admin</div>
                      <code style={{ color: '#94a3b8', fontSize: '0.62rem', wordBreak: 'break-all' }}>{onboarding.issuer}</code>
                    </div>
                    <div className="col-md-6">
                      <div style={{ color: '#64748b' }}>Instrument package <span className="badge ms-1" style={{ background: '#f59e0b18', color: '#f59e0b', fontSize: '0.55rem' }}>{onboarding.instrumentPackage?.action}</span></div>
                      <code style={{ color: '#a7f3d0' }}>{onboarding.instrumentPackage?.name}</code>
                      <div style={{ color: '#94a3b8', fontSize: '0.6rem', wordBreak: 'break-all' }}>{onboarding.instrumentPackage?.packageId}</div>
                      <div className="mt-2" style={{ color: '#64748b' }}>Standard interfaces <span className="badge ms-1" style={{ background: '#10b98122', color: '#10b981', fontSize: '0.55rem' }}>vetted network-wide</span></div>
                      <div style={{ color: '#94a3b8' }}>{(onboarding.standardInterfaces || []).length} canonical token-standard packages</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Discovery — registry URL + CNS (live) */}
              {onboarding.discovery && (
                <div className="card border-0 shadow-sm mb-3" style={{ background: '#0f172a', borderLeft: '3px solid #0ea5e9' }}>
                  <div className="card-body p-3" style={{ fontSize: '0.74rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="bi bi-compass" style={{ color: '#0ea5e9' }}></i>
                      <span style={{ color: '#0ea5e9', fontWeight: 600 }}>Discovery — direct registry URL + CNS auto-discovery (live)</span>
                      {onboarding.discovery.cnsLive && (
                        <span className="badge" style={{ background: '#10b98122', color: '#10b981', fontSize: '0.55rem' }}>CNS LIVE</span>
                      )}
                    </div>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div style={{ color: '#64748b' }}>Registry base URL</div>
                        <code style={{ color: '#a7f3d0', fontSize: '0.65rem', wordBreak: 'break-all' }}>{onboarding.discovery.registryBaseUrl || onboarding.registry?.url || '—'}</code>
                      </div>
                      <div className="col-md-6">
                        <div style={{ color: '#64748b' }}>CNS web UI</div>
                        <a href={onboarding.discovery.cnsWebUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontSize: '0.65rem', wordBreak: 'break-all' }}>
                          {onboarding.discovery.cnsWebUrl || '—'}
                        </a>
                      </div>
                      {onboarding.discovery.cnsEntryName && (
                        <div className="col-12">
                          <div style={{ color: '#64748b' }}>CNS entry</div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <code style={{ color: '#a7f3d0', fontSize: '0.65rem' }}>{onboarding.discovery.cnsEntryName}</code>
                            <span className="badge" style={{ background: '#f59e0b22', color: '#fbbf24', fontSize: '0.55rem' }}>
                              {onboarding.discovery.cnsEntryStatus || 'published'}
                            </span>
                            {onboarding.discovery.cnsWebUrl && (
                              <a href={onboarding.discovery.cnsWebUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontSize: '0.65rem' }}>
                                Open in CNS
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Per-provider packet links */}
              <div className="card border-0 shadow-sm mb-3" style={{ background: '#0f172a' }}>
                <div className="card-body p-3">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="bi bi-people-fill" style={{ color: '#10b981' }}></i>
                    <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>
                      {(onboarding.providers || []).some((p) => p.kind === 'joint-rfq') ? 'UNIFIED RFQ PACKETS' : 'GREENLIT SETTLEMENT PROVIDERS'}
                    </span>
                    <span className="badge ms-1" style={{ background: '#10b98122', color: '#10b981', fontSize: '0.6rem' }}>{(onboarding.providers || []).length}</span>
                  </div>
                  {(onboarding.providers || []).map((p) => {
                    const mdUrl = `${API_BASE}${p.packetMarkdown}`;
                    const jsonUrl = `${API_BASE}${p.packetJson}`;
                    return (
                      <div key={p.id} className="mb-3 pb-3" style={{ borderBottom: '1px solid rgba(16,185,129,0.1)' }}>
                        <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                          <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.88rem' }}>{p.name}</span>
                          <span className="badge" style={{ background: p.kind === 'production-readiness' ? '#f59e0b18' : '#0ea5e918', color: p.kind === 'production-readiness' ? '#fbbf24' : '#0ea5e9', fontSize: '0.58rem' }}>
                            {p.kind === 'joint-rfq' ? `threshold ${p.threshold} · atomic DvP` : p.kind === 'production-readiness' ? 'in preparation' : 'greenlit'}
                          </span>
                        </div>
                        {p.settlementRef && (
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span style={{ color: '#64748b', fontSize: '0.62rem' }}>settlementRef</span>
                            <code style={{ color: '#a7f3d0', fontSize: '0.62rem', wordBreak: 'break-all', flex: 1 }}>{p.settlementRef}</code>
                            <button className="btn btn-sm p-0 px-1" style={{ background: 'rgba(167,243,208,0.06)', border: '1px solid rgba(167,243,208,0.2)', color: '#a7f3d0', fontSize: '0.65rem' }}
                              onClick={() => navigator.clipboard.writeText(p.settlementRef)} title="Copy settlementRef">
                              <i className="bi bi-clipboard"></i>
                            </button>
                          </div>
                        )}
                        {Array.isArray(p.members) && p.members.length > 0 ? (
                          <div className="mb-2" style={{ fontSize: '0.62rem' }}>
                            {p.members.map((m) => (
                              <div key={m.id} className="d-flex align-items-center gap-2 mb-1">
                                <span className="badge" style={{ background: '#10b98118', color: '#6ee7b7', fontSize: '0.52rem', minWidth: 60, textAlign: 'center' }}>
                                  {(m.leg || '').split('—')[0].trim() || m.id}
                                </span>
                                <span style={{ color: '#cbd5e1', minWidth: 128 }}>{m.name}</span>
                                <code style={{ color: '#94a3b8', wordBreak: 'break-all', flex: 1 }}>{m.cantonPartyId}</code>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <code style={{ color: '#a7f3d0', fontSize: '0.62rem', wordBreak: 'break-all', flex: 1 }}>{p.cantonPartyId}</code>
                            <button className="btn btn-sm p-0 px-1" style={{ background: 'rgba(167,243,208,0.06)', border: '1px solid rgba(167,243,208,0.2)', color: '#a7f3d0', fontSize: '0.65rem' }}
                              onClick={() => navigator.clipboard.writeText(p.cantonPartyId)} title="Copy party ID">
                              <i className="bi bi-clipboard"></i>
                            </button>
                          </div>
                        )}
                        <div className="d-flex flex-wrap gap-2">
                          <a href={mdUrl} target="_blank" rel="noopener noreferrer"
                            className="btn btn-sm" style={{ background: '#10b98118', color: '#10b981', border: '1px solid #10b98140', fontSize: '0.7rem' }}>
                            <i className="bi bi-file-earmark-text me-1"></i>Open packet (Markdown)
                          </a>
                          <a href={jsonUrl} target="_blank" rel="noopener noreferrer"
                            className="btn btn-sm" style={{ background: 'rgba(148,163,184,0.08)', color: '#cbd5e1', border: '1px solid rgba(148,163,184,0.25)', fontSize: '0.7rem' }}>
                            <i className="bi bi-braces me-1"></i>JSON
                          </a>
                          <button className="btn btn-sm" style={{ background: 'rgba(148,163,184,0.08)', color: '#cbd5e1', border: '1px solid rgba(148,163,184,0.25)', fontSize: '0.7rem' }}
                            onClick={() => navigator.clipboard.writeText(mdUrl)} title="Copy packet URL">
                            <i className="bi bi-link-45deg me-1"></i>Copy link
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ color: '#64748b', fontSize: '0.68rem' }}>
                    Live index: <code style={{ color: '#94a3b8' }}>{`${API_BASE}/registry/onboarding/v1/info?network=${activeEnv}`}</code>
                    {' · '}Registry URL: <code style={{ color: '#94a3b8' }}>{onboarding.registryUrl || onboarding.discovery?.registryBaseUrl || 'https://api.kromarketplace.com/registry'}</code>
                  </div>
                </div>
              </div>

              {(onboarding.audits || []).length > 0 && (
                <div className="card border-0 shadow-sm mb-3" style={{ background: '#0f172a', borderLeft: '3px solid #a78bfa' }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="bi bi-shield-check" style={{ color: '#a78bfa' }}></i>
                      <span style={{ color: '#c4b5fd', fontWeight: 600, fontSize: '0.85rem' }}>Security &amp; Privacy Audits</span>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm mb-0" style={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                        <thead>
                          <tr style={{ color: '#64748b' }}>
                            <th>Audit / control</th>
                            <th>Status</th>
                            <th>Summary</th>
                          </tr>
                        </thead>
                        <tbody>
                          {onboarding.audits.map((a) => (
                            <tr key={a.id} style={{ borderColor: '#1e293b' }}>
                              <td style={{ color: '#e2e8f0', fontWeight: 600 }}>{a.title}</td>
                              <td><span className="badge" style={{ background: '#10b98118', color: '#10b981', fontSize: '0.58rem' }}>{a.status}</span></td>
                              <td>{a.detail}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {activeTab === 'validators' && (
        <>
          <div className="card border-0 shadow-sm mb-3" style={{ background: '#0f172a' }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center gap-3 mb-2">
                <i className="bi bi-building-fill" style={{ fontSize: '1.4rem', color: '#6366f1' }}></i>
                <div>
                  <h5 className="fw-bold mb-0" style={{ color: '#f1f5f9' }}>Canton MainNet Node Operators</h5>
                  <p className="mb-0" style={{ color: '#64748b', fontSize: '0.78rem' }}>
                    Source: <code style={{ color: '#a7f3d0' }}>lighthouse.cantonloop.com/api/validators</code> · 2,312 total nodes · verified 2026-06-18
                  </p>
                </div>
                <div className="ms-auto d-flex gap-2">
                  <span className="badge" style={{ background: '#10b98118', color: '#10b981', fontSize: '0.68rem', padding: '4px 10px' }}>
                    {greenlitCount} greenlit RFQ scope{liveScope ? ' · live gate' : ''}
                  </span>
                  <span className="badge" style={{ background: '#10b98118', color: '#10b981', fontSize: '0.68rem', padding: '4px 10px' }}>
                    2,312 total
                  </span>
                </div>
              </div>
              <div className="p-3 rounded mt-2" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)' }}>
                <div style={{ color: '#a5b4fc', fontSize: '0.75rem', lineHeight: 1.7 }}>
                  Network watchlist from Lighthouse — not all nodes are in greenlit RFQ scope. Production <code style={{ color: '#c4b5fd' }}>approvedLenders</code> are the four SVs on the <strong>SV Registry</strong> tab. Broadridge and other validator nodes appear here for bridge / future-access interest only.
                </div>
                <div className="d-flex gap-2 mt-2 flex-wrap">
                  {[
                    { label: 'Greenlit — in approvedLenders (SV Registry tab)', color: '#10b981', bg: '#10b98118' },
                    { label: 'Bridge-watch — off-chain prime via bridge SV',       color: '#f59e0b', bg: '#f59e0b18' },
                    { label: 'Watch — future greenlight candidates',             color: '#818cf8', bg: '#6366f118' },
                    { label: 'Infrastructure — not a counterparty',            color: '#64748b', bg: '#64748b18' },
                  ].map(k => (
                    <span key={k.label} className="badge" style={{ background: k.bg, color: k.color, fontSize: '0.65rem', padding: '4px 8px' }}>{k.label}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Live greenlit settlement operators (backend gate) */}
          {liveGreenlit && liveGreenlit.length > 0 && (
            <div className="card border-0 shadow-sm mb-3" style={{ background: '#0d2818', borderLeft: '3px solid #10b981' }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-currency-dollar" style={{ color: '#10b981' }}></i>
                  <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>GREENLIT — ON-LEDGER SETTLEMENT OPERATORS</span>
                  <span className="badge ms-2" style={{ background: '#10b98122', color: '#10b981', fontSize: '0.6rem' }}>live · CER_RFQ_PILOT_GREENLIT gate</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.75rem' }}>
                  {liveGreenlit.map((o) => {
                    const pid = o.cantonPartyId || o.partyId;
                    return (
                      <div key={o.id || pid} className="p-2 rounded" style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.18)' }}>
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.82rem' }}>{o.name}</span>
                          {o.settlementProfile && <code style={{ color: '#64748b', fontSize: '0.55rem' }}>{o.settlementProfile}</code>}
                        </div>
                        {o.role && <p style={{ color: '#64748b', fontSize: '0.72rem', margin: '2px 0 4px' }}>{o.role}</p>}
                        <div className="d-flex align-items-center gap-1">
                          <code style={{ color: '#a7f3d0', fontSize: '0.58rem', wordBreak: 'break-all', flex: 1 }}>{pid}</code>
                          <button className="btn btn-sm p-0 px-1" style={{ background: 'rgba(167,243,208,0.06)', border: '1px solid rgba(167,243,208,0.2)', color: '#a7f3d0', fontSize: '0.6rem' }}
                            onClick={() => navigator.clipboard.writeText(pid)} title="Copy party ID">
                            <i className="bi bi-clipboard"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Bridge-watch + watch nodes */}
          {['bridge-watch', 'watch'].map(tier => {
            const tierNodes = CANTON_VALIDATOR_NODES.filter(v => v.eligTier === tier);
            if (!tierNodes.length) return null;
            const tierColor = tier === 'bridge-watch' ? '#f59e0b' : '#818cf8';
            const tierBg    = tier === 'bridge-watch' ? '#1a1200' : '#0f172a';
            const tierLabel = tier === 'bridge-watch'
              ? 'BRIDGE-WATCH — Off-chain prime interest (e.g. Broadridge DLR via Digital Asset SV)'
              : 'WATCH — Network cash interest, not in greenlit RFQ scope';
            return (
              <div key={tier} className="card border-0 shadow-sm mb-3" style={{ background: tierBg, borderLeft: `3px solid ${tierColor}` }}>
                <div className="card-body p-3">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <i className={`bi ${tier === 'bridge-watch' ? 'bi-signpost-split' : 'bi-eye'}`} style={{ color: tierColor }}></i>
                    <span style={{ color: tierColor, fontWeight: 600, fontSize: '0.8rem' }}>{tierLabel}</span>
                    <span className="badge ms-auto" style={{ background: `${tierColor}18`, color: tierColor, fontSize: '0.58rem' }}>{tierNodes.length} nodes</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '0.75rem' }}>
                    {tierNodes.map((v) => (
                      <div key={v.id} className="p-3 rounded" style={{ background: `${tierColor}08`, border: `1px solid ${tierColor}20` }}>
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                          <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.84rem' }}>{v.name}</span>
                          <div className="d-flex gap-1 flex-shrink-0">
                            <span className="badge" style={{ background: 'rgba(100,116,139,0.18)', color: '#94a3b8', fontSize: '0.52rem', fontWeight: 400 }}>{v.entityType}</span>
                            <span className="badge" style={{ background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', fontSize: '0.52rem' }}>via {v.sponsor}</span>
                          </div>
                        </div>
                        <p style={{ color: '#475569', fontSize: '0.7rem', margin: '2px 0 3px' }}>{v.role}</p>
                        <div className="d-flex align-items-center gap-1">
                          <code style={{ color: '#64748b', fontSize: '0.58rem', wordBreak: 'break-all', flex: 1 }}>{v.partyId}</code>
                          <button className="btn btn-sm p-0 px-1" style={{ background: `${tierColor}10`, border: `1px solid ${tierColor}30`, color: tierColor, fontSize: '0.6rem', flexShrink: 0 }}
                            onClick={() => navigator.clipboard.writeText(v.partyId)} title="Copy party ID">
                            <i className="bi bi-clipboard"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Market infrastructure nodes */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a' }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-bar-chart-fill" style={{ color: '#64748b' }}></i>
                <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.85rem' }}>MARKET INFRASTRUCTURE NODES</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.65rem' }}>
                {CANTON_VALIDATOR_NODES.filter(v => v.eligTier === 'infra').map((v) => (
                  <div key={v.id} className="p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <span style={{ color: '#cbd5e1', fontWeight: 500, fontSize: '0.8rem' }}>{v.name}</span>
                      <span style={{ color: '#475569', fontSize: '0.58rem', flexShrink: 0 }}>via {v.sponsor}</span>
                    </div>
                    <p style={{ color: '#475569', fontSize: '0.7rem', margin: '2px 0 4px' }}>{v.role}</p>
                    <div className="d-flex align-items-center gap-1">
                      <code style={{ color: '#64748b', fontSize: '0.58rem', flex: 1, wordBreak: 'break-all' }}>{v.partyId.substring(0, 52)}…</code>
                      <button className="btn btn-sm p-0 px-1" style={{ background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.2)', color: '#64748b', fontSize: '0.6rem' }}
                        onClick={() => navigator.clipboard.writeText(v.partyId)} title="Copy party ID">
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: LIGHTHOUSE GUIDE
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'lighthouse' && (
        <>
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0' }}>
            <div className="card-body">
              <div className="d-flex align-items-start gap-3 mb-3">
                <i className="bi bi-binoculars-fill text-primary fs-3 mt-1"></i>
                <div>
                  <h5 className="fw-bold mb-1">What is Lighthouse?</h5>
                  <p style={{ color: '#94a3b8', fontSize: '0.84rem', marginBottom: 0 }}>
                    <strong className="text-white">Lighthouse</strong> is the Canton Network's official on-chain explorer — similar to Etherscan for Ethereum.
                    It shows every validator (participant node), every Daml contract, every transaction, and every party on the network.
                    It is a <strong>read-only</strong> public tool: anyone with the Lighthouse URL can look up your party, see your active contracts, and verify your validator's status.
                    KRO has no admin control over Lighthouse — it is operated by the Canton Network Foundation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What you can track */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="card-header border-0" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <h5 className="mb-0 fw-bold text-white"><i className="bi bi-eye-fill me-2 text-primary"></i>What You Can Track in Lighthouse</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
              <table className="table table-dark table-hover align-middle mb-0" style={{ background: 'transparent' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <th style={{ width: '25%', color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>What</th>
                    <th style={{ width: '35%', color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Where in Lighthouse</th>
                    <th style={{ width: '40%', color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>What You'll See</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      what: 'Your validator profile',
                      where: `${env.lighthouseUrl}/participant/{party_id}`,
                      see: 'Validator name, contact card, connected synchronizers, active since, package registrations',
                    },
                    {
                      what: 'Active contracts',
                      where: `${env.lighthouseUrl}/contracts?party={party_id}`,
                      see: 'All live MarkerCollateral, IntradayRepoFacility, DrawWorkflow, CloseOutReceipt contracts you are party to',
                    },
                    {
                      what: 'Who accepted an RFQ',
                      where: `${env.lighthouseUrl}/events/by-contract/{rfp_contract_id}`,
                      see: 'The Accept choice event — shows the accepting party ID, timestamp, and resulting contracts created',
                    },
                    {
                      what: 'Transaction history',
                      where: `${env.lighthouseUrl}/transactions?party={party_id}`,
                      see: 'Every atomic transaction: which templates were created/archived, which choices were exercised, by whom',
                    },
                    {
                      what: 'Package registration',
                      where: `${env.lighthouseUrl}/packages`,
                      see: `Confirms ${env.darVersion} is vetted and active on the network — proof of deployment`,
                    },
                    {
                      what: 'Counterparty party ID',
                      where: `${env.lighthouseUrl}/participant/{counterparty_party_id}`,
                      see: 'Counterparty validator info, their active contracts — confirms they are a credentialed network member',
                    },
                  ].map((r, i) => (
                    <tr key={i} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                      <td><strong className="text-white">{r.what}</strong></td>
                      <td><code style={{ fontSize: '0.7rem', color: '#60a5fa', wordBreak: 'break-all' }}>{r.where}</code></td>
                      <td><small style={{ color: '#94a3b8' }}>{r.see}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>

          {/* Environment Lighthouse links */}
          <div className="row g-3 mb-4">
            {ENVS.map(e => (
              <div key={e.id} className="col-12 col-md-4">
                <div className="card border-0 shadow-sm h-100" style={{ background: '#0f172a', border: `1px solid ${e.statusColor}22` }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span className="rounded-circle" style={{ width: 8, height: 8, background: e.statusColor }}></span>
                      <strong style={{ color: '#e2e8f0' }}>{e.label} Lighthouse</strong>
                    </div>
                    <div className="d-grid gap-2">
                      <a href={e.lighthouseUrl} target="_blank" rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ background: `${e.statusColor}15`, border: `1px solid ${e.statusColor}33`, color: e.statusColor, fontSize: '0.72rem' }}>
                        <i className="bi bi-binoculars me-1"></i>{e.lighthouseUrl.replace('https://', '')}
                      </a>
                      <a href={e.partyUrl} target="_blank" rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: '0.72rem' }}>
                        <i className="bi bi-person-badge me-1"></i>KRO Party Profile
                      </a>
                      <a href={`https://${e.validatorHost}/wallet`} target="_blank" rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: '0.72rem' }}>
                        <i className="bi bi-wallet2 me-1"></i>Validator Wallet UI
                      </a>
                    </div>
                    <small className="d-block mt-2" style={{ color: '#64748b', fontSize: '0.65rem' }}>
                      Validator IP: <code style={{ color: '#94a3b8' }}>{e.validatorIP}</code>
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: VALIDATOR CONTACT CARD
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'validator' && (
        <>
          {/* Contact card display */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)', color: '#e2e8f0', border: '1px solid rgba(16,185,129,0.25)' }}>
            <div className="card-header border-0" style={{ background: 'linear-gradient(135deg,#064e3b,#0f172a)' }}>
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-person-badge-fill text-success fs-5"></i>
                <h5 className="mb-0 fw-bold text-white">Validator Contact Card — KRO MainNet</h5>
                <span className="badge bg-success ms-2" style={{ fontSize: '0.62rem' }}>UPDATED</span>
              </div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {[
                  { label: 'Organization Name', value: VALIDATOR_CONTACT.name,     icon: 'bi-building' },
                  { label: 'Contact Email',      value: VALIDATOR_CONTACT.email,    icon: 'bi-envelope-fill', highlight: true },
                  { label: 'LEI',                value: VALIDATOR_CONTACT.lei,      icon: 'bi-fingerprint' },
                  { label: 'Website',            value: VALIDATOR_CONTACT.url,      icon: 'bi-globe', link: VALIDATOR_CONTACT.url },
                  { label: 'Location',           value: VALIDATOR_CONTACT.location, icon: 'bi-geo-alt-fill' },
                  { label: 'Party Hint',         value: VALIDATOR_CONTACT.partyHint,icon: 'bi-tag-fill' },
                ].map((f, i) => (
                  <div key={i} className="col-12 col-md-6">
                    <div className="p-3 rounded d-flex align-items-start gap-2"
                      style={{ background: f.highlight ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${f.highlight ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
                      <i className={`bi ${f.icon} mt-1 flex-shrink-0`} style={{ color: f.highlight ? '#10b981' : '#475569' }}></i>
                      <div>
                        <div style={{ fontSize: '0.63rem', color: f.highlight ? '#6ee7b7' : '#475569' }}>{f.label}</div>
                        {f.link
                          ? <a href={f.link} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontSize: '0.85rem' }}>{f.value}</a>
                          : <div className="fw-bold" style={{ fontSize: '0.88rem', color: f.highlight ? '#10b981' : '#e2e8f0' }}>{f.value}</div>
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* IS action required */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0' }}>
            <div className="card-header border-0" style={{ background: 'linear-gradient(135deg,#1e1b4b,#0f172a)' }}>
              <h5 className="mb-0 fw-bold"><i className="bi bi-send-fill text-info me-2"></i>IS Action Required — Update Lighthouse Contact Card</h5>
              <small style={{ color: '#94a3b8' }}>Send this request to IS (InfraSingularity) to update the contact card on the Canton Network Lighthouse.</small>
            </div>
            <div className="card-body">
              <p style={{ color: '#94a3b8', fontSize: '0.84rem', marginBottom: '1rem' }}>
                The Lighthouse validator contact card is managed by the validator node operator (IS). The contact email is stored in the Canton participant's metadata and is visible in Lighthouse to all network participants. Send IS the following update request:
              </p>

              <div className="p-3 rounded mb-3" style={{ background: '#0b1120', border: '1px solid rgba(99,102,241,0.25)' }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <small style={{ color: '#6366f1', fontSize: '0.68rem' }}>
                    <i className="bi bi-terminal me-1"></i>
                    Participant Admin API — Canton Validator Node (all environments)
                  </small>
                  <button
                    onClick={() => copy(`POST /v1/validator/admin/contact\nAuthorization: Bearer {participant_admin_token}\nContent-Type: application/json\n\n{\n  "name": "K-R-O BROKERS SPC",\n  "email": "lexy@krotrust.org",\n  "url": "https://kromarketplace.com"\n}`, 'isreq')}
                    style={{ background: 'none', border: 'none', color: copied === 'isreq' ? '#10b981' : '#475569', cursor: 'pointer', fontSize: '0.72rem' }}
                  >
                    <i className={`bi ${copied === 'isreq' ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>Copy
                  </button>
                </div>
                <pre style={{ margin: 0, color: '#94a3b8', fontSize: '0.72rem', lineHeight: 1.7 }}>{`POST /v1/validator/admin/contact
Authorization: Bearer {participant_admin_token}   ← participant_admin scope token
Content-Type: application/json

{
  "name": "K-R-O BROKERS SPC",
  "email": "lexy@krotrust.org",
  "url":   "https://kromarketplace.com"
}`}</pre>
              </div>

              <div className="alert border-0 py-2 mb-0" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2) !important', fontSize: '0.8rem' }}>
                <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                Run this on <strong>Canton MainNet</strong> using the MainNet <code>participant_admin</code> token.
                The token audience is <code>https://{'{validator-host}'}/api/validator</code> with scope <code>participant_admin</code>.
              </div>
            </div>
          </div>

          {/* Where the contact card appears */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-dark text-white">
              <h5 className="mb-0"><i className="bi bi-eye me-2"></i>Where the Contact Card Appears <span className="badge" style={{ background: 'rgba(148,163,184,0.2)', color: '#cbd5e1', fontSize: '0.5rem', fontWeight: 600, verticalAlign: 'middle' }}>K-R-O REF ONLY</span></h5>
            </div>
            <div className="card-body p-0">
              <table className="table table-hover mb-0">
                <thead className="table-dark">
                  <tr><th>Surface</th><th>Who Sees It</th><th>What It Shows</th></tr>
                </thead>
                <tbody>
                  {[
                    { surface: 'Lighthouse Validator Profile', who: 'All Canton network participants + public', what: 'Name, email, website — visible to any institution checking if KRO is a credentialed participant' },
                    { surface: 'RFQ Broadcast Response',       who: 'Counterparty receiving the RFQ',          what: 'Contact info is embedded in the Daml contract metadata for counterparty reference' },
                    { surface: 'Canton Network Directory',     who: 'IS / Canton Foundation operators',        what: 'Used for incident communication and network governance outreach' },
                    { surface: 'Participant Admin API',        who: 'IS & KRO admin operators',               what: 'Machine-readable JSON returned by GET /v1/validator/admin/contact' },
                  ].map((r, i) => (
                    <tr key={i}>
                      <td><strong>{r.surface}</strong></td>
                      <td><small>{r.who}</small></td>
                      <td><small style={{ color: '#6b7280' }}>{r.what}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: CONTRACT ENGAGEMENT TRACKING
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'tracking' && (
        <>
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0' }}>
            <div className="card-body">
              <div className="d-flex align-items-start gap-3 mb-3">
                <i className="bi bi-activity text-success fs-3 mt-1"></i>
                <div>
                  <h5 className="fw-bold mb-1">Tracking Who Accepts Your Contracts</h5>
                  <p style={{ color: '#94a3b8', fontSize: '0.84rem', marginBottom: 0 }}>
                    Every time a counterparty exercises a Daml choice (such as accepting an RFQ, approving a draw, or confirming settlement),
                    it creates a ledger event recorded on the Canton ledger. These events are visible in three places:
                    <strong className="text-white ms-1">Lighthouse</strong>,
                    <strong className="text-white ms-1">Canton Ledger API</strong>, and
                    <strong className="text-white ms-1">KRO's own canton_event_log</strong> (Azure SQL).
                  </p>
                </div>
              </div>

              <div className="row g-3">
                {[
                  {
                    source: 'Lighthouse Explorer',
                    icon: 'bi-binoculars-fill',
                    color: '#3b82f6',
                    how: `Go to ${env.lighthouseUrl}/events/by-party/{counterparty_party_id} or search the contract CID. You'll see the Accept choice, the exercising party, and the timestamp.`,
                    good: 'Visual, no auth required',
                    limit: 'Read-only, no filtering by template',
                  },
                  {
                    source: 'Canton Ledger API',
                    icon: 'bi-cpu-fill',
                    color: '#10b981',
                    how: 'POST /v2/query with templateId=IntradayRepoRFP and filter for contracts where signatories includes your party. Exercises appear as archived events on accepted RFQs.',
                    good: 'Programmable, filterable by template and party',
                    limit: 'Requires Auth0 bearer token (daml_ledger_api scope)',
                  },
                  {
                    source: 'Azure SQL canton_event_log',
                    icon: 'bi-database-fill',
                    color: '#f59e0b',
                    how: 'KRO\'s cantonService already streams all events into canton_event_log. Query: SELECT * FROM canton_event_log WHERE event_type = \'INTRADAY_CLOSEOUT\' or \'CLOSEOUT_ACTIVE\'.',
                    good: 'Already integrated, used by Production Analytics',
                    limit: 'Only captures events the KRO party is signatory/observer to',
                  },
                ].map((s, i) => (
                  <div key={i} className="col-12 col-md-4">
                    <div className="p-3 rounded h-100" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${s.color}22` }}>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className={`bi ${s.icon}`} style={{ color: s.color, fontSize: '1.1rem' }}></i>
                        <strong style={{ fontSize: '0.85rem' }}>{s.source}</strong>
                      </div>
                      <p style={{ color: '#94a3b8', fontSize: '0.77rem', marginBottom: '0.5rem' }}>{s.how}</p>
                      <div className="d-flex flex-column gap-1">
                        <small style={{ color: '#10b981', fontSize: '0.68rem' }}><i className="bi bi-check2 me-1"></i>{s.good}</small>
                        <small style={{ color: '#f59e0b', fontSize: '0.68rem' }}><i className="bi bi-exclamation me-1"></i>{s.limit}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Ledger API endpoint reference */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-dark text-white">
              <h5 className="mb-0"><i className="bi bi-code-slash me-2"></i>Ledger API — Contract Engagement Endpoints</h5>
              <small className="opacity-75">Base URL: <code>https://{env.validatorHost}/json-ledger-api</code></small>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: '10%' }}>Method</th>
                      <th style={{ width: '35%' }}>Endpoint</th>
                      <th style={{ width: '30%' }}>Description</th>
                      <th style={{ width: '25%' }}>Use Case</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TRACKING_APIS.map((a, i) => (
                      <tr key={i}>
                        <td><span className={`badge bg-${a.method === 'GET' ? 'success' : 'primary'}`}>{a.method}</span></td>
                        <td><code style={{ fontSize: '0.68rem', wordBreak: 'break-all' }}>{a.endpoint}</code></td>
                        <td><small style={{ fontSize: '0.75rem' }}>{a.desc}</small></td>
                        <td><small style={{ fontSize: '0.73rem', color: '#6b7280' }}>{a.useCase}</small></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* What to look for when an RFP is accepted */}
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0' }}>
            <div className="card-header border-0" style={{ background: 'linear-gradient(135deg,#1e1b4b,#0f172a)' }}>
              <h6 className="mb-0 fw-bold"><i className="bi bi-broadcast text-info me-2"></i>What to Look For: RFQ Accepted</h6>
            </div>
            <div className="card-body">
              <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '1rem' }}>
                When a counterparty accepts your intraday repo RFQ, the Canton ledger produces this sequence of events:
              </p>
              <div className="d-flex flex-column gap-2">
                {[
                  { icon: '1', color: '#3b82f6',  label: 'IntradayRepoRFP archived', detail: 'The RFQ contract is consumed by the Accept choice. event_type: INTRADAY_CLOSEOUT or similar. Signatories now include the counterparty party ID.' },
                  { icon: '2', color: '#8b5cf6',  label: 'IntradayRepoDraw created', detail: 'A new DrawWorkflow contract is created with both KRO and the counterparty as signatories. This is visible in Lighthouse under both party views.' },
                  { icon: '3', color: '#f59e0b',  label: 'Cash leg — CIP-56 atomic DvP', detail: 'Cash token allocated on-ledger in the same submit as collateral — Allocation_ExecuteTransfer on Global Synchronizer.' },
                  { icon: '4', color: '#10b981',  label: 'CloseOutReceipt created',   detail: 'Upon DvP completion, both parties receive a CloseOutReceipt CID. This is the on-chain proof of settlement finality — visible in Lighthouse.' },
                ].map((s, i) => (
                  <div key={i} className="d-flex align-items-start gap-3 p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <span className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                      style={{ width: 26, height: 26, background: `${s.color}22`, color: s.color, border: `1px solid ${s.color}44`, fontSize: '0.72rem' }}>
                      {s.icon}
                    </span>
                    <div>
                      <strong style={{ fontSize: '0.82rem', color: s.color }}>{s.label}</strong>
                      <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.76rem' }}>{s.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: LIVE EVENT FEED
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'events' && (
        <>
          <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', color: '#e2e8f0' }}>
            <div className="card-header border-0 d-flex align-items-center justify-content-between" style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)' }}>
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-list-check text-info"></i>
                <strong>Canton Event Feed — {env.label}</strong>
              </div>
              <div className="d-flex align-items-center gap-2">
                <small style={{ color: '#334155', fontSize: '0.65rem' }}>{recentEvents.length} records (latest {eventLimit}) · live canton_event_log · auto-refresh 30s</small>
                <button
                  onClick={fetchData}
                  disabled={loading}
                  className="btn btn-sm"
                  style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', color: '#818cf8', fontSize: '0.65rem', padding: '0.15rem 0.5rem' }}
                >
                  <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-arrow-clockwise'} me-1`}></i>
                  {loading ? '…' : 'Refresh'}
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              {recentEvents.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                    <thead>
                      <tr style={{ borderColor: '#1e293b', color: '#475569' }}>
                        <th>Event Type</th>
                        <th>Contract ID</th>
                        <th>Template</th>
                        <th>Amount</th>
                        <th>UETR</th>
                        <th>LEI / Party</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentEvents.map((e, i) => {
                        const col = { COLLATERAL_ACTIVE: '#f59e0b', CLOSEOUT_ACTIVE: '#10b981', INTRADAY_RFP: '#0ea5e9', INTRADAY_FACILITY: '#3b82f6', INTRADAY_DRAW: '#6366f1', REPO_ROLLOVER: '#14b8a6', INTRADAY_CLOSEOUT: '#8b5cf6', HERITAGE_RECORD: '#ec4899' }[e.event_type] || '#64748b';
                        return (
                          <tr key={i} style={{ borderColor: '#1e293b' }}>
                            <td>
                              <span className="badge" style={{ background: `${col}18`, color: col, border: `1px solid ${col}33`, fontSize: '0.6rem' }}>
                                {e.event_type?.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td>
                              <code style={{ fontSize: '0.6rem', color: '#475569' }}>
                                {e.contract_id?.slice(0, 14)}…
                              </code>
                              <button
                                onClick={() => copy(e.contract_id, `cid-${i}`)}
                                style={{ background: 'none', border: 'none', color: copied === `cid-${i}` ? '#10b981' : '#334155', cursor: 'pointer', fontSize: '0.65rem', padding: '0 3px' }}
                              >
                                <i className={`bi ${copied === `cid-${i}` ? 'bi-check2' : 'bi-clipboard'}`}></i>
                              </button>
                            </td>
                            <td><small style={{ color: '#334155', fontSize: '0.62rem' }}>{e.template_id?.split(':').pop() || '—'}</small></td>
                            <td><small>{e.amount_usd ? `$${Number(e.amount_usd).toLocaleString()}` : '—'}</small></td>
                            <td><code style={{ fontSize: '0.6rem', color: '#334155' }}>{e.uetr?.slice(0, 14) || '—'}</code></td>
                            <td><code style={{ fontSize: '0.6rem', color: '#475569' }}>{e.party_lei || '—'}</code></td>
                            <td><small style={{ color: '#334155' }}>{e.event_timestamp ? new Date(e.event_timestamp).toLocaleString() : '—'}</small></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {recentEvents.length >= eventLimit && (
                    <div className="text-center p-2" style={{ borderTop: '1px solid #1e293b' }}>
                      <button
                        onClick={() => setEventLimit(l => l + 100)}
                        disabled={loading}
                        className="btn btn-sm"
                        style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', color: '#818cf8', fontSize: '0.65rem', padding: '0.2rem 0.8rem' }}
                      >
                        <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-arrow-down-circle'} me-1`}></i>
                        {loading ? 'Loading…' : `Load more (next 100 — full history)`}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-5 text-center" style={{ color: '#334155' }}>
                  <i className="bi bi-database fs-1 mb-3 d-block"></i>
                  <div className="mb-1">No events in canton_event_log for <strong>{env.label}</strong> yet.</div>
                  <small>Run a settlement test or deploy on {env.label} to see live events here.</small>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
